import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStaffList, useDepartments, useUpdateStaff, useDeleteStaff, useEmployeeStatistics, useFilteredEmployeeStatistics, useHRProfilesForStaff } from '../hooks';
import { useOffboardExternalPersonnel } from '../hooks/useExternalPersonnel';
import AddEmployeeModal from '../components/employees/AddEmployeeModal';
import ProfileChangeLogSection from '../components/employees/ProfileChangeLogSection';
import CareerTimelineHistorySection from '../components/employees/CareerTimelineHistorySection';
import {
  useEmergencyContactsForStaff,
  useCreateEmergencyContact,
  useUpdateEmergencyContact,
  useDeleteEmergencyContact,
} from '../hooks/useEmergencyContactsData';
import EmergencyContactModal from '../components/UI/EmergencyContactModal';
import { isValidPhoneNumber as validatePhone } from 'libphonenumber-js';
import { useCurrentSalary, useCalculatedRates } from '../hooks/usePayrollData';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Pagination } from '../components/UI/Pagination';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { useTabPersistence, usePersistence, useNestedTabPersistence } from '../hooks/usePersistence';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { 
  Users, 
  Search, 
  Plus, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building,
  Building2,
  Lightbulb,
  Download,
  Upload,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  User,
  Briefcase,
  Shield,
  FileText,
  CreditCard,
  Contact,
  GraduationCap,
  Award,
  File,
  Globe,
  Heart,
  EyeOff,
  Clock,
  FileCheck,
  AlertTriangle,
  UserPlus,
  Timer,
  UserCheck,
  RefreshCw,
  Car,
  DollarSign,
  LogOut,
  Baby,
  IdCard,
  Fingerprint,
  HeartPulse,
  Landmark,
  Receipt,
  Save,
  Edit3,
  Stethoscope,
  Thermometer,
  Hospital,
  MinusCircle,
  Smartphone,
  Pause,
  Play,
  ArrowRightLeft,
  Plane,
  ArrowLeft,
  Lock,
  CalendarClock,
} from 'lucide-react';
import { addMonths, isAfter, startOfDay } from 'date-fns';

import api, { confidentialAPI, configAPI, staffAPI } from '../services/api';
import {
  COMPANY_EMAIL_DOMAIN,
  isValidWorkEmail,
  workEmailDomainError,
} from '../utils/emailUtils';
import { RELIGION_OPTIONS, RELIGION_DISPLAY, RELIGION_DEFAULT } from '../constants/religionOptions';
import { RACE_OPTIONS, RACE_DISPLAY } from '../constants/raceOptions';
import {
  getMaritalStatusLabel,
  isIncompleteMaritalStatus,
  MARITAL_STATUS_OPTIONS,
} from '../constants/maritalStatusOptions';
import {
  formatEmergencyContactRelationship,
  EMERGENCY_CONTACT_RELATIONSHIP_VALUES,
} from '../constants/emergencyContactRelationships';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateTime, formatDaySpan, formatEducationYearRange, formatServicePeriod, formatExperienceFromDays, formatWorkExperiencePeriod, getCalendarDaysUntil, toLocalISODate, parseLocalDate } from '../utils/dateUtils';
import { formatWorkScheduleTemplateLabel } from '../utils/scheduleUtils';
import {
  getDocumentSubtitle,
  keepLatestIdCardPerSide,
  hasLaterReplacementUpload,
  mapHrDocumentListStatus,
  isDocumentSubmittedToHr,
} from '../config/documentTypes';
import PostalCodeLookup from '../components/UI/PostalCodeLookup';
import ResidentialAddressEditor from '../components/profile/ResidentialAddressEditor';
import CountryOfBirthField from '../components/profile/CountryOfBirthField';
import {
  OVERSEAS_RESIDENTIAL_COUNTRY,
  ensureCountryInAddress,
  inferResidentialAddressLocation,
} from '../utils/residentialAddressUtils';
import SkillsUpgradingTab from '../components/SkillsUpgrading/SkillsUpgradingTab';
import DrivingLicenseManager from '../components/DrivingLicense/DrivingLicenseManager';
import DocumentVerificationModal from '../components/DocumentVerificationModal';
import NSDocumentVerificationModal from '../components/NSDocumentVerificationModal';
import MCDocumentVerificationModal from '../components/MCDocumentVerificationModal';
import SimpleDocumentViewerModal from '../components/UI/SimpleDocumentViewerModal';
import Portal from '../components/UI/Portal';
import ConfirmModal from '../components/UI/ConfirmModal';
import Modal from '../components/UI/Modal';
import EmployeeExportModal from '../components/EmployeeExportModal';
import RoleBadge from '../components/RoleBadge';
import { TradeBadge } from '../components/UI/TradeBadge';
import { isTradeRedundantWithDepartment } from '../config/tradeConfig';
import { StatusBadge } from '../components/UI/StatusBadge';
import { BcaSchemeBadge } from '../components/UI/BcaSchemeBadge';
import { InfoBadge } from '../components/UI/InfoBadge';
import RejectionFeedback from '../components/UI/RejectionFeedback';
import FieldLabelRow from '../components/UI/FieldLabelRow';
import PassDateFieldDisplay from '../components/UI/PassDateFieldDisplay';
import AccordionSection from '../components/UI/AccordionSection';
import { ClassificationBadge } from '../components/UI/ClassificationBadge';
import { CountBadge } from '../components/UI/CountBadge';
import { FilterButton } from '../components/UI/FilterButton';
import FormSelect from '../components/UI/FormSelect';
import SearchableCombobox from '../components/UI/SearchableCombobox';
import DatePickerField from '../components/UI/DatePickerField';
import PillSelect from '../components/UI/PillSelect';
import PillToggle from '../components/UI/PillToggle';
import { SensitiveFieldDisplay as SharedSensitiveField, SensitiveInlineToggle, SensitiveRevealButton, maskValue, formatVisible } from '../components/UI/SensitiveFieldDisplay';
import { getClassificationAvatarHex } from '../utils/classificationConfig';
import { pluralizeDays } from '../utils/numberUtils';
import { formatDirectLine, formatPhoneForDisplay, isSgOfficeDid, splitWorkContact } from '../utils/phoneUtils';
import toast from '../utils/toast';
import { firstMeaningfulQualificationN8nPage } from '../utils/documentOcrUtils';
import {
  useEmployeeCertificates,
  useEmployeeEducation,
  useEmployeeWorkExperience,
  useEmployeeMovements,
  useCreateWorkExperience,
  useUpdateWorkExperience,
  useDeleteWorkExperience,
} from '../hooks/useTimelineData';
import WorkExperienceFormModal from '../components/employees/WorkExperienceFormModal';
import {
  getWorkExpEmploymentTypeLabel,
  getWorkExpIndustryLabel,
  getWorkExpVerificationLabel,
  splitWorkExperienceDescription,
} from '../utils/workExperienceUtils';
import { useTradeCategories, useDistinctDesignations, useMOMDesignations } from '../hooks/useConfigData';

// Helper functions for work schedule display
const getDefaultScheduleName = (classification) => {
  const scheduleMap = {
    'worker': 'Worker 6-Day Schedule',
    'staff': 'Staff 5-Day Schedule (90%) or Staff 5.5-Day Schedule (10%)',
    'executive_director': 'Executive Director 5-Day Schedule'
  };
  return scheduleMap[classification] || 'Not assigned';
};

/**
 * Derive the employment-status badge state from raw employee fields.
 * Considers all three runtime signals:
 *   - employment_status === 'inactive'                       → 'deactivated' (terminated)
 *   - is_active === false AND start_date in the future       → 'pre_onboarding'
 *   - is_active === false AND no future start_date           → 'deactivated' (manually disabled)
 *   - otherwise                                              → 'active'
 *
 * Outsourced holders always have is_active=False (no portal login). Their
 * lifecycle is employment_status only — do not treat them as pre_onboarding
 * or deactivated solely because is_active is false.
 *
 * Without this, a chip that only checks employment_status will incorrectly
 * show "Active" for employees who are imported but have not yet started.
 */
const getEmploymentBadgeStatus = (employee) => {
  if (!employee) return 'active';
  if (employee.employment_classification === 'outsourced') {
    return employee.employment_status === 'inactive' ? 'deactivated' : 'active';
  }
  if (employee.employment_status === 'inactive') return 'deactivated';
  if (employee.is_active === false) {
    const start = employee.employment_start_date || employee.date_joined;
    if (start) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(start);
      startDate.setHours(0, 0, 0, 0);
      if (startDate > today) return 'pre_onboarding';
    }
    return 'deactivated';
  }
  return 'active';
};


const EmployeeManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Ref to read current searchParams inside write-effects without triggering re-runs
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  
  // Tracks the last URL search string we processed/wrote, preventing the
  // URL→State and State→URL effects from ping-ponging on the same change.
  const prevSearchStr = useRef(searchParams.toString());

  // Promise-based confirm modal
  const confirmCallbackRef = useRef(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const showConfirm = (config) => {
    return new Promise((resolve) => {
      confirmCallbackRef.current = resolve;
      setConfirmDialog(config);
    });
  };
  
  // Filter state - synced to URL
  // Workforce scope: '' = operational employees (default), 'outsourced' = JB holders
  const [searchTerm, setSearchTerm] = usePersistence('admin-employees-search', '');
  const [selectedDepartment, setSelectedDepartment] = usePersistence('admin-employees-department', '');
  const [selectedStatus, setSelectedStatus] = usePersistence('admin-employees-status', 'active');
  const [selectedSubStatus, setSelectedSubStatus] = usePersistence('admin-employees-substatus', '');
  const [selectedWorkforce, setSelectedWorkforce] = usePersistence('admin-employees-workforce', '');
  const [currentPage, setCurrentPage] = useState(1);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const isOutsourcedWorkforce = selectedWorkforce === 'outsourced';
  
  // Initialize filters from URL on mount (only once)
  useEffect(() => {
    if (!filtersInitialized) {
      const urlSearch = searchParams.get('search');
      const urlDepartment = searchParams.get('department');
      const urlStatus = searchParams.get('status');
      const urlSubStatus = searchParams.get('substatus');
      const urlWorkforce = searchParams.get('workforce');
      
      if (urlSearch !== null) setSearchTerm(urlSearch);
      if (urlDepartment !== null) setSelectedDepartment(urlDepartment);
      if (urlStatus !== null) setSelectedStatus(urlStatus);
      if (urlSubStatus !== null) setSelectedSubStatus(urlSubStatus);
      if (urlWorkforce !== null) setSelectedWorkforce(urlWorkforce === 'outsourced' ? 'outsourced' : '');
      setFiltersInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersInitialized]); // Only run when filtersInitialized changes
  
  // Listen to URL changes after initialization to keep filters in sync
  // This handles browser back/forward navigation and external URL changes
  // IMPORTANT: We compare against prevSearchStr to only react to genuine URL
  // changes (e.g. browser back/forward), NOT to our own setSearchParams writes
  // or the filtersInitialized toggle — which previously caused an infinite loop.
  useEffect(() => {
    if (!filtersInitialized) return;
    
    const currentStr = searchParams.toString();
    if (currentStr === prevSearchStr.current) return; // URL didn't actually change
    prevSearchStr.current = currentStr;
    
    const urlSearch = searchParams.get('search') || '';
    const urlDepartment = searchParams.get('department') || '';
    const urlStatus = searchParams.get('status') || 'active';
    const urlSubStatus = searchParams.get('substatus') || '';
    const urlWorkforce = searchParams.get('workforce') === 'outsourced' ? 'outsourced' : '';
    
    if (urlSearch !== searchTerm) setSearchTerm(urlSearch);
    if (urlDepartment !== selectedDepartment) setSelectedDepartment(urlDepartment);
    if (urlStatus !== selectedStatus) setSelectedStatus(urlStatus);
    if (urlSubStatus !== selectedSubStatus) setSelectedSubStatus(urlSubStatus);
    if (urlWorkforce !== selectedWorkforce) setSelectedWorkforce(urlWorkforce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, filtersInitialized]); // Intentionally exclude filter states to prevent loops
  
  // Sync filters to URL on change (but not during initialization)
  // Note: useTabPersistence handles tab param, so we don't touch it here
  // IMPORTANT: searchParams is read via ref to avoid feedback loop (this effect writes to URL)
  useEffect(() => {
    if (!filtersInitialized) return; // Skip during initialization
    
    const currentParams = searchParamsRef.current;
    
    // Check if URL already matches current filter state to avoid unnecessary updates
    const currentUrlSearch = currentParams.get('search') || '';
    const currentUrlDepartment = currentParams.get('department') || '';
    const currentUrlStatus = currentParams.get('status') || '';
    const currentUrlSubStatus = currentParams.get('substatus') || '';
    const currentUrlWorkforce = currentParams.get('workforce') || '';
    
    const searchMatches = (searchTerm || '') === currentUrlSearch;
    const departmentMatches = (selectedDepartment || '') === currentUrlDepartment;
    const statusMatches = (selectedStatus === 'active' ? '' : selectedStatus || '') === currentUrlStatus;
    const substatusMatches = (selectedSubStatus || '') === currentUrlSubStatus;
    const workforceMatches = (selectedWorkforce || '') === currentUrlWorkforce;
    
    // Only update URL if something actually changed
    if (searchMatches && departmentMatches && statusMatches && substatusMatches && workforceMatches) {
      return; // URL already matches state, no update needed
    }
    
    const params = new URLSearchParams(currentParams);
    // Update filter params
    if (searchTerm) {
      params.set('search', searchTerm);
    } else {
      params.delete('search');
    }
    if (selectedDepartment) {
      params.set('department', selectedDepartment);
    } else {
      params.delete('department');
    }
    if (selectedStatus && selectedStatus !== 'active') {
      params.set('status', selectedStatus);
    } else {
      params.delete('status');
    }
    if (selectedSubStatus) {
      params.set('substatus', selectedSubStatus);
    } else {
      params.delete('substatus');
    }
    if (selectedWorkforce === 'outsourced') {
      params.set('workforce', 'outsourced');
    } else {
      params.delete('workforce');
    }
    // Preserve employee param (tab is handled by useTabPersistence)
    const employeeParam = currentParams.get('employee');
    if (employeeParam) params.set('employee', employeeParam);
    
    // Stamp the ref so Effect A (URL→State) knows this URL change came from
    // us and skips re-syncing state, preventing the ping-pong loop.
    prevSearchStr.current = params.toString();
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedDepartment, selectedStatus, selectedSubStatus, selectedWorkforce, filtersInitialized, setSearchParams]);
  
  // React Query hooks for data management
  const staffFilters = useMemo(() => ({
    page: currentPage,
    search: searchTerm,
    department: selectedDepartment !== '' ? selectedDepartment : undefined,
    employment_classification: isOutsourcedWorkforce ? 'outsourced' : undefined,
    employment_status: (() => {
      // Outsourced lifecycle is active | inactive only (no probation/notice).
      if (isOutsourcedWorkforce) {
        return selectedStatus !== '' ? selectedStatus : undefined;
      }
      // Handle nested status logic for operational employees
      if (selectedStatus === 'active' && selectedSubStatus) {
        return selectedSubStatus; // probation or notice
      }
      return selectedStatus !== '' ? selectedStatus : undefined;
    })(),
    include_inactive: true, // Include inactive employees for frontend filtering
    page_size: 100
  }), [currentPage, searchTerm, selectedDepartment, selectedStatus, selectedSubStatus, isOutsourcedWorkforce]);
  
  const { 
    data: staffData = { results: [], count: 0, totalPages: 1 }, 
    isLoading: employeesLoading, 
    error: employeesError 
  } = useStaffList(staffFilters);
  
  const employees = staffData.results;
  const totalEmployeeCount = staffData.count;
  const totalPages = staffData.totalPages;
  
  const { 
    data: departments = [], 
    isLoading: departmentsLoading 
  } = useDepartments();
  
  // Employee statistics hook - use filtered statistics that match current filters
  const statisticsFilters = useMemo(() => ({
    search: searchTerm,
    department: selectedDepartment !== '' ? selectedDepartment : undefined,
    employment_classification: isOutsourcedWorkforce ? 'outsourced' : undefined,
    employment_status: (() => {
      if (isOutsourcedWorkforce) {
        return selectedStatus !== '' ? selectedStatus : undefined;
      }
      // Handle nested status logic
      if (selectedStatus === 'active' && selectedSubStatus) {
        return selectedSubStatus; // probation or notice
      }
      return selectedStatus !== '' ? selectedStatus : undefined;
    })(),
    include_inactive: true, // Include inactive employees for frontend filtering
  }), [searchTerm, selectedDepartment, selectedStatus, selectedSubStatus, isOutsourcedWorkforce]);

  const { 
    data: employeeStats = {
      total: 0,
      newHires: 0,
      currentYear: new Date().getFullYear(),  // Fallback to current year if not provided
      probation: 0,
      avgTenure: 0,
      departments: 0,
      systemAccounts: 0,
      totalIncludingInactive: 0
    }, 
    isLoading: statsLoading 
  } = useFilteredEmployeeStatistics(statisticsFilters);
  
  // Derived loading state
  const loading = employeesLoading || departmentsLoading || statsLoading;
  
  // Compute employees needing HR profiles (without work email OR without work phone)
  // Memoized for performance - fetch HR profiles for employees missing work contact info
  const employeesNeedingHRProfiles = useMemo(() => {
    return employees
      .filter(emp => {
        const hasWorkEmail = emp.work_email && emp.work_email.trim() !== '';
        const hasWorkPhone = emp.work_phone_number && emp.work_phone_number.trim() !== '';
        // Include employees missing either work email or work phone
        return !hasWorkEmail || !hasWorkPhone;
      })
      .map(emp => emp.staff_id);
  }, [employees]);
  
  // Fetch HR profiles for employees needing contact info (missing work email or phone) using React Query
  const { 
    data: hrProfileMap = new Map(), 
    isLoading: hrProfilesLoading 
  } = useHRProfilesForStaff(employeesNeedingHRProfiles);
  
  const [showFilters, setShowFilters] = useState(false);
  // Default cohort is Active employees — don't treat that as an "applied" filter
  // (otherwise Filter badge + Clear always look dirty on first paint).
  const hasActiveFilters = Boolean(
    searchTerm ||
    selectedDepartment ||
    selectedSubStatus ||
    selectedWorkforce ||
    (selectedStatus && selectedStatus !== 'active')
  );
  const clearAllFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedDepartment('');
    setSelectedStatus('active');
    setSelectedSubStatus('');
    setSelectedWorkforce('');
  }, [setSearchTerm, setSelectedDepartment, setSelectedStatus, setSelectedSubStatus, setSelectedWorkforce]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [showEmployeeView, setShowEmployeeView] = useState(false);
  const [viewEmployeeHRProfile, setViewEmployeeHRProfile] = useState(null);
  const [viewEmployeeLoading, setViewEmployeeLoading] = useState(false);
  const [workScheduleTemplates, setWorkScheduleTemplates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    staffAPI.getWorkScheduleTemplates()
      .then((response) => {
        if (!cancelled) {
          setWorkScheduleTemplates(response.data?.results || response.data || []);
        }
      })
      .catch((error) => {
        console.error('Error loading work schedule templates:', error);
        toast.error('Failed to load work schedule templates');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Inline editing state for view modal (Profile.js pattern)
  const [isViewEditing, setIsViewEditing] = useState(false);
  const [viewFormData, setViewFormData] = useState({});
  const [originalViewFormData, setOriginalViewFormData] = useState({});
  const [viewSaving, setViewSaving] = useState(false);

  // Round-trip support: when the detail modal is deep-linked from another page
  // (e.g. Levy Optimization), `returnTo` carries that page's URL so closing the
  // modal returns the user to exactly where they were instead of stranding them
  // on the employee list. `returnLabel` provides the human-readable origin name.
  const returnTo = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel');
  const safeReturnTo = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')
    ? returnTo
    : null;

  const closeEmployeeView = useCallback(() => {
    if (isViewEditing) setIsViewEditing(false);
    setShowEmployeeView(false);
    setViewingEmployee(null);
    setViewEmployeeHRProfile(null);
    if (safeReturnTo) {
      navigate(safeReturnTo);
      return;
    }
    const params = new URLSearchParams(searchParamsRef.current);
    params.delete('employee');
    setSearchParams(params, { replace: true });
  }, [isViewEditing, safeReturnTo, navigate, setSearchParams]);

  // View-mode: backdrop / Escape / X dismiss immediately (DS Dismissal Patterns).
  // Edit-mode: block accidental dismiss while saving, and confirm if the form is dirty.
  const requestCloseEmployeeView = useCallback(async () => {
    if (viewSaving) return;
    if (isViewEditing) {
      const isDirty = Object.keys(viewFormData).some(
        (key) => viewFormData[key] !== originalViewFormData[key],
      );
      if (isDirty) {
        const confirmed = await showConfirm({
          title: 'Discard unsaved changes?',
          message: 'You have unsaved profile edits. Closing now will discard them.',
          confirmLabel: 'Discard',
          cancelLabel: 'Keep editing',
          variant: 'danger',
        });
        if (!confirmed) return;
      }
    }
    closeEmployeeView();
  }, [
    viewSaving,
    isViewEditing,
    viewFormData,
    originalViewFormData,
    closeEmployeeView,
  ]);

  // Get valid tabs for an employee (includes conditional tabs) - memoized
  const getValidTabsForEmployee = useCallback((employee) => {
    // Outsourced holders are asset holders only — no payroll, documents, or skills.
    if (employee?.employment_classification === 'outsourced') {
      return ['personal', 'employment'];
    }
    const baseTabs = ['personal', 'employment', 'compensation', 'professional', 'documents'];
    const conditionalTabs = employee?.employment_classification === 'worker'
      ? ['skills-upgrading']
      : [];
    return [...baseTabs, ...conditionalTabs];
  }, []);
  
  // Tab persistence with URL sync
  const validTabs = ['personal', 'employment', 'compensation', 'professional', 'skills-upgrading', 'documents'];
  const [activeViewTab, setActiveViewTab] = useTabPersistence('admin-employees-view', 'personal', validTabs);
  // Compensation → Employment deep-link: open Record Employment Event preset to salary_change.
  const [pendingEmploymentEventPreset, setPendingEmploymentEventPreset] = useState(null);

  // Personal → Family empty/primary CTA: jump to Documents → Upload (birth/marriage/interim).
  // Same deep-link pattern as Skills Upgrading → Documents.
  const handleUploadFamilyDocument = useCallback(() => {
    try {
      localStorage.setItem(
        'admin-employees-documents-subTab',
        JSON.stringify('upload'),
      );
    } catch {
      /* ignore quota / private mode */
    }
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', 'documents');
      params.set('subTab', 'upload');
      return params;
    }, { replace: true });
    setActiveViewTab('documents');
  }, [setSearchParams, setActiveViewTab]);
  
  // Validate tab when employee changes (ensure tab is valid for current employee).
  // Do not infer deletion from the current list contents: the list is filter-,
  // search-, and page-scoped, so a legitimate employee can disappear from it
  // while the detail modal should remain open (e.g. pre-onboarding hires).
  // IMPORTANT: searchParams read via ref to avoid feedback loop
  useEffect(() => {
    if (viewingEmployee && showEmployeeView) {
      // Validate tab for this employee
      const validTabsForEmployee = getValidTabsForEmployee(viewingEmployee);
      if (!validTabsForEmployee.includes(activeViewTab)) {
        setActiveViewTab('personal');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingEmployee, showEmployeeView, activeViewTab, getValidTabsForEmployee, setActiveViewTab]);

  // Lock body scroll while the employee detail modal is open. Plain
  // `overflow: hidden` is ignored by iOS Safari for touch panning, so the page
  // rubber-bands and visibly drags the fixed `.modal-overlay` around when the
  // user touches a non-scrollable region (header, photo, tabs). useBodyScrollLock
  // pins <body> with position:fixed, which stops the bounce on every device.
  useBodyScrollLock(showEmployeeView);

  useEffect(() => {
    if (!showEmployeeView) return undefined;

    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      // Nested ConfirmModal owns Escape while a discard prompt is open.
      if (confirmDialog) return;
      requestCloseEmployeeView();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showEmployeeView, confirmDialog, requestCloseEmployeeView]);
  // Page size is now handled by the React Query hook
  const actualPageSize = 100;

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'green' },
    { value: 'inactive', label: 'Inactive', color: 'red' },
    { value: 'probation', label: 'On Probation', color: 'yellow' },
    { value: 'notice', label: 'Serving Notice', color: 'orange' }
  ];

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, selectedDepartment, selectedStatus, selectedSubStatus, selectedWorkforce]);
  
  // Auto-open employee modal from URL on mount or when employees load
  // Fixed: Proper cleanup, security validation, and race condition handling
  // IMPORTANT: searchParams read via ref to avoid feedback loop
  const employeeIdFromUrl = searchParams.get('employee');
  const cancelledRef = useRef(false);
  
  useEffect(() => {
    // Reset cancellation flag on mount
    cancelledRef.current = false;
    
    if (!employeeIdFromUrl || employeesLoading || showEmployeeView) {
      return;
    }
    
    // Security: Validate employee ID format (basic check)
    if (typeof employeeIdFromUrl !== 'string' || employeeIdFromUrl.trim() === '') {
      // Invalid employee ID - clean URL
      const params = new URLSearchParams(searchParamsRef.current);
      params.delete('employee');
      setSearchParams(params, { replace: true });
      return;
    }
    
    const employeeFromCurrentPage = employees.find(
      emp => emp.staff_id === employeeIdFromUrl,
    );
    
    // Use a small delay to avoid race conditions with other effects
    const timer = setTimeout(async () => {
      if (cancelledRef.current) return;

      let employee = employeeFromCurrentPage;
      if (!employee) {
        try {
          const employeeResponse = await staffAPI.getStaff(employeeIdFromUrl);
          employee = employeeResponse.data;
        } catch (error) {
          if (cancelledRef.current) return;

          const params = new URLSearchParams(searchParamsRef.current);
          params.delete('employee');
          setSearchParams(params, { replace: true });

          if (error.response?.status === 403) {
            toast.error('You do not have access to this employee');
          } else if (error.response?.status === 404) {
            toast.error('Employee not found');
          } else {
            toast.error('Failed to load employee details');
          }
          return;
        }
      }

      if (user?.has_worker_admin_access && employee.employment_classification !== 'worker') {
        const params = new URLSearchParams(searchParamsRef.current);
        params.delete('employee');
        setSearchParams(params, { replace: true });
        toast.error('Access restricted: You can only view details of worker employees');
        return;
      }
      
      setViewingEmployee(employee);
      setViewEmployeeLoading(true);
      setShowEmployeeView(true);
      
      // Update URL with employee ID (tab is handled by useTabPersistence)
      const params = new URLSearchParams(searchParamsRef.current);
      params.set('employee', employee.staff_id);
      setSearchParams(params, { replace: true });
      
      // Validate tab for this employee
      const validTabsForEmployee = getValidTabsForEmployee(employee);
      if (!validTabsForEmployee.includes(activeViewTab)) {
        setActiveViewTab('personal');
      }
      
      try {
        // Fetch HR profile with error handling
        const hrResponse = await confidentialAPI.getHRProfiles({ hrprofile_staff_id: employee.staff_id });
        if (cancelledRef.current) return; // Check again after async operation
        
        const hrProfileData = hrResponse.data.results?.[0] || hrResponse.data || null;
        setViewEmployeeHRProfile(hrProfileData);
      } catch (error) {
        if (cancelledRef.current) return;
        
        console.error('Error fetching employee HR profile:', error);
        setViewEmployeeHRProfile(null);
        toast.error('Failed to load employee profile data');
      } finally {
        if (!cancelledRef.current) {
          setViewEmployeeLoading(false);
        }
      }
    }, 100);
    
    return () => {
      cancelledRef.current = true;
      clearTimeout(timer);
    };
  }, [
    employees,
    employeesLoading,
    employeeIdFromUrl,
    showEmployeeView,
    activeViewTab,
    setSearchParams,
    getValidTabsForEmployee,
    setActiveViewTab,
    user,
  ]);




  // Note: Metrics are now calculated server-side using the filtered statistics API
  // This provides accurate counts across all pages, not just the current page







  // React Query mutations
  const deleteStaffMutation = useDeleteStaff();
  const updateStaffMutation = useUpdateStaff();
  const offboardExternalMutation = useOffboardExternalPersonnel();
  
  const handleDeleteEmployee = async (staffId) => {
    const confirmed = await showConfirm({
      title: 'Delete Employee',
      message: 'Are you sure you want to delete this employee?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      deleteStaffMutation.mutate(staffId);
    }
  };

  // Outsourced holders skip EmploymentMovement — same HR outcome as employees
  // (status → Inactive), wired through the external-personnel offboard API.
  const handleMarkOutsourcedInactive = async () => {
    if (!viewingEmployee?.staff_id || viewingEmployee.employment_classification !== 'outsourced') {
      return;
    }
    if (viewingEmployee.employment_status === 'inactive') {
      toast.info('This holder is already Inactive.');
      return;
    }
    const displayName = getDisplayNameAliasFirst(viewingEmployee);
    const confirmed = await showConfirm({
      title: 'Set Status to Inactive',
      message: `Set ${displayName} (${viewingEmployee.staff_id}) to Inactive? They will no longer receive new asset assignments. Existing asset history is preserved.`,
      confirmLabel: 'Set to Inactive',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      // Stamp end date if HR hasn't set one yet — last day of engagement.
      if (!viewingEmployee.employment_end_date) {
        await staffAPI.updateStaff(viewingEmployee.staff_id, {
          employment_end_date: toLocalISODate(new Date()),
        });
      }
      await offboardExternalMutation.mutateAsync(viewingEmployee.staff_id);
      await refreshViewingEmployee();
    } catch {
      /* toasts handled by mutation / update */
    }
  };

  const handleViewEmployee = async (employee) => {
    // Security: Validate employee exists and is accessible
    if (!employee || !employee.staff_id) {
      toast.error('Invalid employee data');
      return;
    }
    
    // Check if employee is in accessible list
    const isAccessible = employees.some(emp => emp.staff_id === employee.staff_id);
    if (!isAccessible) {
      toast.error('Employee not found or access denied');
      // Clean URL if employee param exists
      const params = new URLSearchParams(searchParams);
      if (params.get('employee') === employee.staff_id) {
        params.delete('employee');
        setSearchParams(params, { replace: true });
      }
      return;
    }
    
    // Worker-admin restriction: block viewing non-worker employee details
    if (user?.has_worker_admin_access && employee.employment_classification !== 'worker') {
      toast.error('Access restricted: You can only view details of worker employees');
      return;
    }
    
    setViewingEmployee(employee);
    setViewEmployeeLoading(true);
    setShowEmployeeView(true);
    
    // Update URL with employee ID (tab is handled by useTabPersistence automatically)
    const params = new URLSearchParams(searchParams);
    params.set('employee', employee.staff_id);
    setSearchParams(params, { replace: true });
    
    // Validate tab for this employee
    const validTabsForEmployee = getValidTabsForEmployee(employee);
    if (!validTabsForEmployee.includes(activeViewTab)) {
      setActiveViewTab('personal');
    }
    
    try {
      // Fetch the employee's HR profile data using the confidential API
      const hrResponse = await confidentialAPI.getHRProfiles({ hrprofile_staff_id: employee.staff_id });
      const hrProfileData = hrResponse.data.results?.[0] || hrResponse.data || null;
      setViewEmployeeHRProfile(hrProfileData);
    } catch (error) {
      console.error('Error fetching employee HR profile:', error);
      setViewEmployeeHRProfile(null);
      toast.error('Failed to load employee profile data');
    } finally {
      setViewEmployeeLoading(false);
    }
  };

  // ── Inline editing helpers for view modal ──────────────────────────────
  const STAFF_FIELDS = [
    'first_name', 'last_name', 'alias', 'work_email', 'work_phone_number',
    'did_number', 'extension_number', 'department', 'designation', 'mom_designation', 'reporting_officer_id',
    'employment_classification', 'employment_status', 'employment_start_date',
    'employment_end_date', 'employment_type', 'attendance_tracking_mode',
    'work_schedule_template_id',
    'security_bond_expiry_date'
  ];

  const initializeViewFormData = (employee, hrProfile) => {
    let departmentId = '';
    if (employee?.department) {
      if (typeof employee.department === 'object' && employee.department.id) {
        departmentId = employee.department.id;
      } else if (typeof employee.department === 'string') {
        const match = departments.find(d => d.department_name === employee.department);
        departmentId = match ? match.id : '';
      } else {
        departmentId = employee.department;
      }
    }

    let reportingOfficerId = '';
    if (employee?.reporting_officer) {
      if (typeof employee.reporting_officer === 'object' && employee.reporting_officer.staff_id) {
        reportingOfficerId = employee.reporting_officer.staff_id;
      } else if (typeof employee.reporting_officer === 'string') {
        reportingOfficerId = employee.reporting_officer;
      }
    }

    const data = {
      // Staff model fields
      first_name: employee?.first_name || '',
      last_name: employee?.last_name || '',
      alias: employee?.alias || '',
      work_email: employee?.work_email || '',
      work_phone_number: employee?.work_phone_number || '',
      did_number: employee?.did_number || '',
      extension_number: employee?.extension_number || '',
      department: departmentId,
      designation: employee?.designation || '',
      mom_designation: employee?.mom_designation || '',
      reporting_officer_id: reportingOfficerId,
      employment_classification: employee?.employment_classification || 'worker',
      employment_status: employee?.employment_status || 'active',
      work_schedule_template_id: employee?.work_schedule_template?.id || '',
      employment_start_date: employee?.employment_start_date || '',
      employment_end_date: employee?.employment_end_date || '',
      employment_type: employee?.employment_type || 'full_time',
      attendance_tracking_mode: employee?.attendance_tracking_mode || 'standard',
      security_bond_expiry_date: employee?.security_bond_expiry_date || '',
      // HR Profile fields
      hrprofile_gender: hrProfile?.hrprofile_gender || '',
      hrprofile_date_of_birth: hrProfile?.hrprofile_date_of_birth || '',
      hrprofile_nationality: hrProfile?.hrprofile_nationality || '',
      hrprofile_country_of_birth: hrProfile?.hrprofile_country_of_birth || '',
      hrprofile_marital_status: hrProfile?.hrprofile_marital_status || '',
      hrprofile_race: hrProfile?.hrprofile_race || '',
      hrprofile_religion: hrProfile?.hrprofile_religion || RELIGION_DEFAULT,
      hrprofile_nric_fin_number: hrProfile?.hrprofile_nric_fin_number || '',
      hrprofile_wp_number: hrProfile?.hrprofile_wp_number || '',
      hrprofile_passport_number: hrProfile?.hrprofile_passport_number || '',
      hrprofile_passport_expiry_date: hrProfile?.hrprofile_passport_expiry_date || '',
      hrprofile_employment_pass_type: hrProfile?.hrprofile_employment_pass_type || '',
      hrprofile_pass_issue_date: hrProfile?.hrprofile_pass_issue_date || '',
      hrprofile_pass_expiry_date: hrProfile?.hrprofile_pass_expiry_date || '',
      hrprofile_wp_application_date: hrProfile?.hrprofile_wp_application_date || '',
      hrprofile_pr_obtained_date: hrProfile?.hrprofile_pr_obtained_date || '',
      hrprofile_renewal_reminder_days: hrProfile?.hrprofile_renewal_reminder_days || '',
      hrprofile_personal_email: hrProfile?.hrprofile_personal_email || '',
      hrprofile_personal_phone_number: hrProfile?.hrprofile_personal_phone_number || '',
      hrprofile_residential_address: hrProfile?.hrprofile_residential_address || '',
      hrprofile_residential_postal_code: hrProfile?.hrprofile_residential_postal_code || '',
      hrprofile_residential_unit_number: hrProfile?.hrprofile_residential_unit_number || '',
      hrprofile_residential_address_location:
        hrProfile?.hrprofile_residential_address_location ||
        inferResidentialAddressLocation({
          address: hrProfile?.hrprofile_residential_address,
          postalCode: hrProfile?.hrprofile_residential_postal_code,
          location: hrProfile?.hrprofile_residential_address_location,
          country: hrProfile?.hrprofile_residential_address_country,
        }),
      hrprofile_residential_address_country: hrProfile?.hrprofile_residential_address_country || '',
      hrprofile_dormitory_address: hrProfile?.hrprofile_dormitory_address || '',
      hrprofile_dormitory_postal_code: hrProfile?.hrprofile_dormitory_postal_code || '',
      hrprofile_dorm_unit: hrProfile?.hrprofile_dorm_unit || '',
      // Probation end date is amended via the dedicated Amend Probation
      // action — never through the generic Edit Profile save path.
      hrprofile_mom_employment_classification: hrProfile?.hrprofile_mom_employment_classification || '',
    };
    setViewFormData(data);
    setOriginalViewFormData(data);
  };

  const handleViewFormChange = (field, value) => {
    setViewFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleViewEditStart = () => {
    initializeViewFormData(viewingEmployee, viewEmployeeHRProfile);
    setIsViewEditing(true);
  };

  const handleViewEditCancel = () => {
    setIsViewEditing(false);
    setViewFormData({});
    setOriginalViewFormData({});
  };

  const handleViewEditSave = async () => {
    const staffChanges = {};
    const hrChanges = {};

    // Normalise residential Singapore vs overseas before diffing (same as employee Profile save)
    const addressLocation = inferResidentialAddressLocation({
      address: viewFormData.hrprofile_residential_address,
      postalCode: viewFormData.hrprofile_residential_postal_code,
      location: viewFormData.hrprofile_residential_address_location,
      country: viewFormData.hrprofile_residential_address_country,
    });
    const normalisedFormData = {
      ...viewFormData,
      hrprofile_residential_address_location: addressLocation,
      hrprofile_residential_address_country:
        addressLocation === 'overseas' ? OVERSEAS_RESIDENTIAL_COUNTRY : '',
      hrprofile_residential_address:
        addressLocation === 'overseas'
          ? ensureCountryInAddress(
              viewFormData.hrprofile_residential_address,
              OVERSEAS_RESIDENTIAL_COUNTRY
            )
          : viewFormData.hrprofile_residential_address,
    };

    const workEmailErr = workEmailDomainError(
      normalisedFormData.work_email,
      viewingEmployee?.employment_classification
        || normalisedFormData.employment_classification,
    );
    if (workEmailErr) {
      toast.error(workEmailErr);
      return;
    }

    if (
      viewEmployeeHRProfile?.id
      && isIncompleteMaritalStatus(normalisedFormData.hrprofile_marital_status)
    ) {
      toast.error('Marital status is required.');
      return;
    }

    Object.keys(normalisedFormData).forEach(key => {
      if (normalisedFormData[key] !== originalViewFormData[key]) {
        if (STAFF_FIELDS.includes(key)) {
          // Persist blank work_email as empty string → backend normalises to NULL.
          // DateField rejects ''; coerce bond blank → null so clear never 400s the patch.
          const value = normalisedFormData[key];
          staffChanges[key] =
            key === 'security_bond_expiry_date' && value === '' ? null : value;
        } else if (key.startsWith('hrprofile_')) {
          hrChanges[key] = normalisedFormData[key];
        }
      }
    });

    const hasStaffChanges = Object.keys(staffChanges).length > 0;
    const hasHRChanges = Object.keys(hrChanges).length > 0;

    if (!hasStaffChanges && !hasHRChanges) {
      toast('No changes to save');
      setIsViewEditing(false);
      return;
    }

    setViewSaving(true);
    const errors = [];

    try {
      if (hasStaffChanges) {
        await api.patch(`/staff/${viewingEmployee.staff_id}/`, staffChanges);
      }
    } catch (err) {
      console.error('Staff update failed:', err);
      const apiWorkEmail = err?.response?.data?.work_email;
      const detail = Array.isArray(apiWorkEmail) ? apiWorkEmail[0] : apiWorkEmail;
      errors.push(detail || 'basic info');
    }

    try {
      if (hasHRChanges && viewEmployeeHRProfile?.id) {
        await confidentialAPI.updateHRProfile(viewEmployeeHRProfile.id, hrChanges);
      }
    } catch (err) {
      console.error('HR profile update failed:', err);
      errors.push('HR profile');
    }

    setViewSaving(false);

    if (errors.length > 0) {
      toast.error(`Failed to update ${errors.join(' and ')}. Other changes saved.`);
    } else {
      toast.success('Employee updated successfully');
    }

    // Re-fetch to get fresh data
    try {
      const [staffRes, hrRes] = await Promise.all([
        api.get(`/staff/${viewingEmployee.staff_id}/`),
        confidentialAPI.getHRProfiles({ hrprofile_staff_id: viewingEmployee.staff_id })
      ]);
      const freshEmployee = staffRes.data;
      const freshHR = hrRes.data.results?.[0] || hrRes.data || null;
      setViewingEmployee(freshEmployee);
      setViewEmployeeHRProfile(freshHR);
    } catch (err) {
      console.error('Failed to refresh employee data:', err);
    }

    setIsViewEditing(false);
    setViewFormData({});
    setOriginalViewFormData({});
  };
  // ── End inline editing helpers ─────────────────────────────────────────

  const refreshViewingEmployee = useCallback(async () => {
    if (!viewingEmployee?.staff_id) return;
    try {
      const [staffRes, hrRes] = await Promise.all([
        api.get(`/staff/${viewingEmployee.staff_id}/`),
        confidentialAPI.getHRProfiles({ hrprofile_staff_id: viewingEmployee.staff_id })
      ]);
      setViewingEmployee(staffRes.data);
      setViewEmployeeHRProfile(hrRes.data.results?.[0] || hrRes.data || null);
    } catch (err) {
      console.error('Failed to refresh employee data:', err);
    }
  }, [viewingEmployee?.staff_id]);

  // Helper function to detect if a name is Chinese (simple detection)
  const isChineseName = (firstName, lastName) => {
    // Check if first name has multiple parts (common in Chinese names)
    if (firstName && firstName.includes(' ')) {
      return true;
    }
    
    // Check for common Chinese surnames
    const commonChineseSurnames = [
      'Chan', 'Chen', 'Cheng', 'Cheung', 'Chin', 'Chong', 'Chow', 'Chu', 'Chua', 'Chung',
      'Fong', 'Ho', 'Huang', 'Hui', 'Kang', 'Koh', 'Kong', 'Koo', 'Kwan', 'Kwok',
      'Lai', 'Lam', 'Lee', 'Leong', 'Leung', 'Li', 'Lim', 'Lin', 'Ling', 'Liu',
      'Lo', 'Loi', 'Lok', 'Loo', 'Low', 'Lui', 'Lum', 'Lung', 'Ma', 'Mah',
      'Mak', 'Man', 'Mok', 'Mui', 'Mung', 'Ng', 'Ngan', 'Ong', 'Pang', 'Poon',
      'Pun', 'Shum', 'Sin', 'So', 'Soh', 'Sung', 'Tam', 'Tan', 'Tang', 'Tay',
      'Teng', 'Ting', 'Tong', 'Tsang', 'Tse', 'Tsoi', 'Tsui', 'Tung', 'Wan', 'Wee',
      'Wong', 'Woo', 'Wu', 'Yam', 'Yap', 'Yau', 'Yeung', 'Yip', 'Yong', 'Yu',
      'Yuen', 'Yung', 'Zhang', 'Zhao', 'Zhou', 'Zhu'
    ];
    
    if (lastName && commonChineseSurnames.includes(lastName)) {
      return true;
    }
    
    return false;
  };

  // Helper function to format display name (Standardized Western format: First, Last)
  const getDisplayName = (firstName, lastName) => {
    if (!firstName && !lastName) return '';
    
    // Standardized Western format: First Name, Last Name
    // This ensures consistency across all employees regardless of ethnicity
    return `${firstName || ''} ${lastName || ''}`.trim();
  };

  // Simplified avatar logic - just first letter of first name
  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '';
    
    // Just return the first letter of the first name (or last name if no first name)
    const nameToUse = firstName || lastName;
    return nameToUse ? nameToUse.charAt(0).toUpperCase() : '';
  };

  // Generate employee photo with color-coded background
  const getEmployeePhoto = (employee) => {
    const bgHex = getClassificationAvatarHex(employee.employment_classification).replace('#', '');
    const initial = getInitialsAliasFirst(employee);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initial)}&background=${bgHex}&color=ffffff&size=128&shape=circle`;
  };

  // Function to get employment pass type display name
  const getEmploymentPassTypeDisplay = (passType) => {
    if (!passType) return '';
    
    const passTypeMap = {
      'citizen': 'Singapore Citizen',
      'pr': 'Permanent Resident',
      'ep': 'Employment Pass',
      's_pass': 'S Pass',
      'wp': 'Work Permit',
      'ltvp': 'Long Term Visit Pass',
      'dp': 'Dependant Pass',
      'student_pass': 'Student Pass',
      'visit_pass': 'Visit Pass',
      'other': 'Other'
    };
    
    return passTypeMap[passType] || passType;
  };

  // Helper: alias-first display and initials
  const getDisplayNameAliasFirst = (emp) => (emp?.alias && emp.alias.trim()) ? emp.alias.trim() : getDisplayName(emp.first_name, emp.last_name);
  const getInitialsAliasFirst = (emp) => {
    const alias = emp?.alias && emp.alias.trim();
    if (alias) {
      // Just return first letter of alias
      return alias.charAt(0).toUpperCase();
    }
    return getInitials(emp.first_name, emp.last_name);
  };
  const getLegalName = (emp) => `${emp?.first_name || ''} ${emp?.last_name || ''}`.trim();
  
  // Helper: check if using personal email (for workers, this is expected)
  const isUsingPersonalEmail = (employee, hrProfile) => {
    if (employee?.employment_classification === 'worker') {
      return hrProfile?.hrprofile_personal_email && !employee?.work_email;
    }
    return !employee?.work_email && hrProfile?.hrprofile_personal_email;
  };
  
  // Helper function to get display email (work_email or personal_email fallback)
  const getDisplayEmail = (employee, hrProfileMap) => {
    // Normalize work_email: treat empty string as null/undefined
    const workEmail = employee?.work_email?.trim() || null;
    
    // If work_email exists and is not empty, return it (priority)
    if (workEmail) {
      return workEmail;
    }
    
    // Fallback to personal email from HR profile
    if (hrProfileMap && employee?.staff_id) {
      const hrProfile = hrProfileMap.get(employee.staff_id);
      const personalEmail = hrProfile?.hrprofile_personal_email?.trim() || null;
      if (personalEmail) {
        return personalEmail;
      }
    }
    
    // No email available
    return 'Not provided';
  };
  
  // Helper function to get display phone number (work_phone_number or personal_phone_number fallback)
  const getDisplayPhoneNumber = (employee, hrProfileMap) => {
    const { mobile, did, extension } = splitWorkContact(employee);
    const parts = [];
    if (mobile) parts.push(formatPhoneForDisplay(mobile));
    if (did) parts.push(formatDirectLine(did, extension));
    if (parts.length) return parts.join(' · ');

    if (hrProfileMap && employee?.staff_id) {
      const hrProfile = hrProfileMap.get(employee.staff_id);
      const personalPhone = hrProfile?.hrprofile_personal_phone_number?.trim() || null;
      if (personalPhone) {
        return formatPhoneForDisplay(personalPhone) || personalPhone;
      }
    }

    return 'N/A';
  };

  // Function to calculate tenure (years and months)
  const calculateTenure = (startDate) => {
    if (!startDate) return null;
    
    const start = new Date(startDate);
    const today = new Date();
    
    // Calculate difference
    const diffTime = today - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return null; // Future date
    
    // Calculate years and months
    const years = Math.floor(diffDays / 365.25);
    const remainingDays = diffDays - (years * 365.25);
    const months = Math.floor(remainingDays / 30.44);
    
    return { years, months };
  };

  // Function to format tenure display
  const formatTenure = (tenure) => {
    if (!tenure) return null;
    
    const { years, months } = tenure;
    
    if (years === 0 && months === 0) {
      return 'Less than 1 month';
    } else if (years === 0) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else if (months === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years}y ${months}m`;
    }
  };

  // Helper to format average tenure (in decimal years) into "Xy Ym"
  const formatAverageTenure = (avgYears) => {
    if (avgYears === null || avgYears === undefined || isNaN(avgYears)) return '0y 0m';
    const wholeYears = Math.floor(avgYears);
    let months = Math.round((avgYears - wholeYears) * 12);
    // Handle rounding up to 12 months
    let years = wholeYears;
    if (months === 12) {
      years += 1;
      months = 0;
    }
    return `${years}y ${months}m`;
  };

  // Enhanced Loading Component
  const EnhancedLoading = () => (
    <div className="space-y-4">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="animate-pulse">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              <div className="flex-1 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded-xl w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded-xl w-1/2"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded-xl w-full"></div>
                    <div className="h-3 bg-gray-200 rounded-xl w-3/4"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded-xl w-2/3"></div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded-xl w-1/2"></div>
                  </div>
                </div>
              </div>
              <div className="flex space-x-2">
                <div className="w-8 h-8 bg-gray-200 rounded-xl"></div>
                <div className="w-8 h-8 bg-gray-200 rounded-xl"></div>
                <div className="w-8 h-8 bg-gray-200 rounded-xl"></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Enhanced Empty State Component
  const EnhancedEmptyState = ({ searchTerm, onClearSearch, onAddNew }) => {
    const entityLabel = isOutsourcedWorkforce ? 'outsourced holders' : 'employees';
    if (searchTerm) {
      return (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No {entityLabel} found
          </h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">
            No {entityLabel} matching "<span className="font-medium text-gray-900">{searchTerm}</span>".
            Try searching by name, staff ID, or email address.
          </p>
        </div>
      );
    }

    return (
      <div className="text-center py-12">
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Users className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {isOutsourcedWorkforce ? 'No outsourced holders yet' : 'No employees yet'}
        </h3>
        <p className="text-sm text-gray-500 max-w-sm mx-auto">
          {isOutsourcedWorkforce
            ? 'Register an outsourced holder via Add Employee → Outsourced (JB Office), then manage offboarding from here.'
            : 'Get started by adding your first employee to the system. You can import employees in bulk or add them individually.'}
        </p>
      </div>
    );
  };

  // Since filtering is done on the backend, we use employees directly
  const filteredEmployees = employees;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-row items-start sm:items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Employee Management</h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">Manage staff profiles, information, and organizational structure</p>
        </div>
        <div className="flex-shrink-0">
          <button
            onClick={() => setShowAddEmployeeModal(true)}
            className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
            aria-label="Add Employee"
            title="Add Employee"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Employee</span>
          </button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
        {/* Always show dynamic metrics based on current view */}
        {(() => {
          const metrics = employeeStats; // Now using server-side filtered statistics
          const isFiltered = hasActiveFilters;
          
          return (
            <>
              <div className="metrics-card">
                <div className="metrics-card-content">
                  <div className="metrics-card-icon metrics-card-icon-blue">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="metrics-card-text">
                    <p className="metrics-card-value">{metrics.total}</p>
                    <p className="metrics-card-label">
                      {isOutsourcedWorkforce ? (
                        selectedStatus === 'active' ? 'Active Outsourced' :
                        selectedStatus === 'inactive' ? 'Inactive Outsourced' :
                        'Outsourced Holders'
                      ) :
                      selectedStatus === 'active' ? 'Active Employees' :
                       selectedStatus === 'pre_onboarding' ? 'Pre-Onboarding' :
                       selectedStatus === 'inactive' ? 'Inactive Employees' :
                       selectedStatus === 'probation' ? 'Probation Employees' :
                       selectedStatus === 'notice' ? 'Notice Employees' :
                       isFiltered ? 'Filtered Employees' : 'Total Employees'}
                      {isFiltered && (
                        <span className="ml-1 py-0.5 px-2 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Live</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="metrics-card">
                <div className="metrics-card-content">
                  <div className="metrics-card-icon metrics-card-icon-green">
                    <UserPlus className="w-6 h-6" />
                  </div>
                  <div className="metrics-card-text">
                    <p className="metrics-card-value">{metrics.newHires}</p>
                    <p className="metrics-card-label">New Hires ({metrics.currentYear || new Date().getFullYear()})</p>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="metrics-card w-full text-left hover:shadow-md transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                onClick={() => {
                  setSelectedStatus('active');
                  setSelectedSubStatus('probation');
                }}
                aria-label={`On Probation: ${metrics.probation}. Filter list to probation employees.`}
              >
                <div className="metrics-card-content">
                  <div className="metrics-card-icon metrics-card-icon-orange">
                    <AlertCircle className="w-6 h-6" />
                  </div>
                  <div className="metrics-card-text">
                    <p className="metrics-card-value">{metrics.probation}</p>
                    <p className="metrics-card-label">On Probation</p>
                  </div>
                </div>
              </button>
              <div className="metrics-card">
                <div className="metrics-card-content">
                  <div className="metrics-card-icon metrics-card-icon-purple">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div className="metrics-card-text">
                    <p className="metrics-card-value">{formatAverageTenure(metrics.avgTenure)}</p>
                    <p className="metrics-card-label">Avg Tenure</p>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      {/* Enhanced Search and Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200">
        <div className="density-compact flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          {/* Enhanced Search */}
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Global search: name, staff ID, email..."
            className="max-w-xl"
          />

          {/* Toolbar Actions: Scope → Filter → Export → Import → Clear
              Mobile (stacked under search): start-align — justify-end leaves orphaned
              dead space on the left. Desktop: parent sm:justify-between already parks
              this group on the right; inner start/end is a no-op at natural width. */}
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-start w-full sm:w-auto">
            <PillToggle
              value={isOutsourcedWorkforce ? 'outsourced' : 'employees'}
              onChange={(val) => {
                const next = val === 'outsourced' ? 'outsourced' : '';
                setSelectedWorkforce(next);
                setSelectedSubStatus('');
                // Default each universe to its "current" cohort
                setSelectedStatus('active');
              }}
              options={[
                { id: 'employees', label: 'Employees', icon: Users, title: 'Employees' },
                { id: 'outsourced', label: 'Outsourced', icon: Building2, title: 'Outsourced' },
              ]}
              variant="icon"
              theme="admin"
              ariaLabel="Workforce scope"
            />

            <FilterButton
              theme="admin"
              isOpen={showFilters}
              activeCount={
                [searchTerm, selectedDepartment, selectedSubStatus, selectedWorkforce]
                  .filter(Boolean).length
                + (selectedStatus && selectedStatus !== 'active' ? 1 : 0)
              }
              onClick={() => setShowFilters(!showFilters)}
            />

            <button
              onClick={() => setShowExportModal(true)}
              className="icon-button-admin"
              title="Export"
              aria-label="Export"
            >
              <Download className="w-4 h-4" />
            </button>

            <button className="icon-button-admin" title="Import" aria-label="Import">
              <Upload className="w-4 h-4" />
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="icon-button-admin"
                title="Clear all filters"
                aria-label="Clear all filters"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Collapsible filters — Department + Status only; Clear lives in toolbar (DS) */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl">
              <div>
                <label className="form-label-admin">Department</label>
                <PillSelect
                  options={[
                    { value: '', label: 'All Departments' },
                    ...departments.map(dept => ({ value: dept.department_name, label: dept.department_name }))
                  ]}
                  value={selectedDepartment}
                  onChange={setSelectedDepartment}
                  theme="admin"
                  icon={Building2}
                  iconColor="text-red-600"
                  className="w-full"
                />
              </div>
              <div>
                <label className="form-label-admin">Status</label>
                <div className="space-y-2">
                  {/* Outsourced: active | inactive only — no probation/notice/pre-onboarding */}
                  {isOutsourcedWorkforce ? (
                    <PillToggle
                      value={selectedStatus}
                      onChange={(val) => { setSelectedStatus(val); setSelectedSubStatus(''); }}
                      options={[
                        { id: '', label: 'All' },
                        { id: 'active', label: 'Active' },
                        { id: 'inactive', label: 'Inactive' },
                      ]}
                      variant="text"
                      theme="admin"
                      className="w-fit"
                      ariaLabel="Outsourced status"
                    />
                  ) : (
                    <>
                      <PillToggle
                        value={selectedStatus}
                        onChange={(val) => { setSelectedStatus(val); setSelectedSubStatus(''); }}
                        options={[
                          { id: '', label: 'All' },
                          { id: 'active', label: 'Active' },
                          { id: 'pre_onboarding', label: 'Pre-Onboarding' },
                          { id: 'inactive', label: 'Inactive' },
                        ]}
                        variant="text"
                        theme="admin"
                        className="w-fit"
                        ariaLabel="Employment status"
                      />
                      {selectedStatus === 'active' && (
                        <PillToggle
                          value={selectedSubStatus}
                          onChange={setSelectedSubStatus}
                          options={[
                            { id: '', label: 'All Active' },
                            { id: 'probation', label: 'Probation' },
                            { id: 'notice', label: 'Notice' },
                          ]}
                          variant="text"
                          theme="admin"
                          className="w-fit"
                          ariaLabel="Active cohort"
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Employee Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {isOutsourcedWorkforce ? 'Outsourced Staff' : 'Employees'}
              <CountBadge count={totalEmployeeCount} theme="employee" size="md" max={9999} className="ml-3" />
            </h2>
          </div>
        </div>
        
        {loading ? (
          <div className="p-6">
            <EnhancedLoading />
          </div>
        ) : filteredEmployees.length > 0 ? (
          <>
            {/* Mobile grid view */}
            <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
              {filteredEmployees.map((employee) => (
                <button
                  key={employee.id}
                  onClick={() => handleViewEmployee(employee)}
                  className="bg-gray-50 rounded-xl p-3.5 text-left hover:bg-gray-100 transition-colors border border-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden">
                      <img
                        src={getEmployeePhoto(employee)}
                        alt={`${employee.first_name} ${employee.last_name}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-gray-900 truncate">{getDisplayNameAliasFirst(employee)}</p>
                        <ClassificationBadge classification={employee.employment_classification} />
                      </div>
                      <p className="text-xs text-gray-500 truncate">{employee.department?.department_name || employee.department || 'N/A'}</p>
                    </div>
                    <StatusBadge status={getEmploymentBadgeStatus(employee)} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500 pl-[52px]">
                    <span>{employee.designation || 'No designation'}</span>
                    <span className="font-mono">{employee.staff_id}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="table-header border-b border-gray-200">
                  <tr>
                    <th className="table-header-cell">
                      Employee
                    </th>
                    <th className="table-header-cell">
                      Contact
                    </th>
                    <th className="table-header-cell">
                      Department
                    </th>
                    <th className="table-header-cell">
                      Status
                    </th>
                    <th className="table-header-cell">
                      Joined
                    </th>
                    <th className="table-header-cell text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredEmployees.map((employee) => (
                    <tr key={employee.id} className="table-row group">
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden">
                            <img
                              src={getEmployeePhoto(employee)}
                              alt={`${employee.first_name} ${employee.last_name}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="ml-4 flex-1">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900">
                                {getDisplayNameAliasFirst(employee)}
                              </div>
                              {(employee.alias && employee.alias.trim()) && (
                                <span className="text-xs text-gray-500">{getLegalName(employee)}</span>
                              )}
                              <ClassificationBadge classification={employee.employment_classification} />
                              {employee.employment_type === 'part_time' && (
                                <InfoBadge color="orange" label="Part-Time" />
                              )}
                            </div>
                            <div className="text-sm text-gray-500 font-mono">{employee.staff_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center mb-1">
                          <Mail className="w-4 h-4 mr-2 text-gray-400" />
                          {getDisplayEmail(employee, hrProfileMap)}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          {getDisplayPhoneNumber(employee, hrProfileMap)}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-gray-900">
                            <UserCheck className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                            <span className="font-medium">{employee.designation || 'Not Set'}</span>
                            <span className="ml-1.5"><RoleBadge employee={employee} size="sm" showPortalGroups={false} /></span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Building2 className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                            <span>{employee.department?.department_name || employee.department || 'N/A'}</span>
                          </div>
                          {employee.employment_classification === 'worker' &&
                            employee.trade_category &&
                            !isTradeRedundantWithDepartment(
                              employee.department?.department_name || employee.department,
                              null,
                              null,
                              employee.trade_category,
                            ) && (
                            <div className="mt-0.5">
                              <TradeBadge trade={employee.trade_category} size="xs" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="space-y-1">
                          <StatusBadge status={getEmploymentBadgeStatus(employee)} />
                          {employee.employment_status === 'inactive' && employee.employment_end_date && (
                            <div className="text-xs text-red-600 flex items-center">
                              <LogOut className="w-3 h-3 mr-1" />
                              {formatDate(employee.employment_end_date)}
                            </div>
                          )}
                          {getEmploymentBadgeStatus(employee) === 'pre_onboarding' && employee.employment_start_date && (
                            <div className="text-xs text-blue-600 flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              Starts {formatDate(employee.employment_start_date)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500">
                        <div className="space-y-1">
                          {employee.employment_status === 'inactive' ? (
                            <>
                              <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                {employee.employment_start_date ? formatDate(employee.employment_start_date) : 'N/A'}
                              </div>
                              {employee.employment_start_date && employee.employment_end_date && (
                                <div className="flex items-center text-xs text-gray-400">
                                  <Timer className="w-3 h-3 mr-1 text-gray-400" />
                                  {(() => {
                                    const start = new Date(employee.employment_start_date);
                                    const end = new Date(employee.employment_end_date);
                                    const diffTime = end - start;
                                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                    if (diffDays < 0) return 'Invalid dates';
                                    const years = Math.floor(diffDays / 365.25);
                                    const remainingDays = diffDays - (years * 365.25);
                                    const months = Math.floor(remainingDays / 30.44);
                                    if (years === 0 && months === 0) return 'Less than 1 month';
                                    if (years === 0) return `${months} month${months !== 1 ? 's' : ''}`;
                                    if (months === 0) return `${years} year${years !== 1 ? 's' : ''}`;
                                    return `${years}y ${months}m`;
                                  })()}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                {employee.employment_start_date ? formatDate(employee.employment_start_date) : 'N/A'}
                              </div>
                              {employee.employment_start_date && (
                                <div className="flex items-center text-xs text-gray-400">
                                  <Timer className="w-3 h-3 mr-1 text-gray-400" />
                                  {formatTenure(calculateTenure(employee.employment_start_date))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1">
                          {!(user?.has_worker_admin_access && employee.employment_classification !== 'worker') && (
                            <button 
                              onClick={() => handleViewEmployee(employee)}
                              className="icon-button-admin"
                              title="View Employee"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="p-8">
            <EnhancedEmptyState
              searchTerm={searchTerm}
              onClearSearch={() => {
                setSearchTerm('');
                setSelectedDepartment('');
                setSelectedStatus('');
                setSelectedSubStatus('');
              }}
              onAddNew={() => setShowAddEmployeeModal(true)}
            />
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalCount={totalEmployeeCount}
          pageSize={100}
          theme="admin"
        />
      </div>

      {/* Add Employee wizard (6-tab onboarding) */}
      <AddEmployeeModal
        isOpen={showAddEmployeeModal}
        onClose={() => setShowAddEmployeeModal(false)}
        onSuccess={(created) => {
          // After registering an outsourced holder, switch to that workforce
          // scope so HR can immediately see and manage the new record.
          if (created?.employment_classification === 'outsourced' || created?.staff_id?.startsWith?.('JB')) {
            setSelectedWorkforce('outsourced');
            setSelectedStatus('active');
            setSelectedSubStatus('');
            setShowFilters(true);
          }
        }}
      />

      {/* Comprehensive Employee View Modal - Profile.js Style */}
      {showEmployeeView && viewingEmployee && (
        <Portal>
          <div
            className="modal-overlay"
            onClick={requestCloseEmployeeView}
            role="dialog"
            aria-modal="true"
            aria-label="Employee profile"
          >
          <div
            className="modal-container modal-container-xl flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >

            {/* Return-path bar — only when deep-linked from another page (e.g. Levy Optimization) */}
            {safeReturnTo && (
              <div className="px-4 sm:px-6 pt-3 sm:pt-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={closeEmployeeView}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-red-600 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to {returnLabel || 'previous page'}
                </button>
              </div>
            )}

            {/* Header - Profile.js Style */}
            <div className="modal-header flex-shrink-0">
              <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
                <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img
                    src={getEmployeePhoto(viewingEmployee)}
                    alt={`${viewingEmployee.first_name} ${viewingEmployee.last_name}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate pr-2">
                    {getDisplayNameAliasFirst(viewingEmployee)}
                  </h1>
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-1 flex-wrap">
                    <ClassificationBadge classification={viewingEmployee.employment_classification} label={viewingEmployee.staff_id} variant="solid" size="md" />
                    <RoleBadge employee={viewingEmployee} showPortalGroups={false} />
                  </div>
                  {(viewingEmployee.alias && viewingEmployee.alias.trim()) && (
                    <div className="text-xs sm:text-sm text-gray-500 mt-1 truncate">{getLegalName(viewingEmployee)}</div>
                  )}
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">{viewingEmployee.designation}</p>
                  <div className="flex items-center gap-3 sm:gap-4 mt-1.5 sm:mt-2 flex-wrap">
                    <StatusBadge
                      status={getEmploymentBadgeStatus(viewingEmployee)}
                      showIcon
                    />
                    <span className="text-xs sm:text-sm text-gray-500">
                      <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1 text-blue-500" />
                      Joined {viewingEmployee.employment_start_date ? formatDate(viewingEmployee.employment_start_date) : 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 self-start">
                {!(user?.has_worker_admin_access && viewingEmployee?.employment_classification !== 'worker') && (
                  isViewEditing ? (
                    <>
                      <button
                        onClick={handleViewEditCancel}
                        disabled={viewSaving}
                        className="btn btn-outline w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
                        aria-label="Cancel"
                      >
                        <X className="w-4 h-4" />
                        <span className="hidden sm:inline">Cancel</span>
                      </button>
                      <button
                        onClick={handleViewEditSave}
                        disabled={viewSaving}
                        className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
                        aria-label="Save Changes"
                      >
                        {viewSaving ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">{viewSaving ? 'Saving...' : 'Save Changes'}</span>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleViewEditStart}
                      className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
                      aria-label="Edit Profile"
                      title="Edit profile details & corrections (name, contact, pass info). Employment start/end dates can be edited directly on the Employment tab. Promotions, transfers and other employment events use Record Employment Event."
                    >
                      <Edit3 className="w-4 h-4" />
                      <span className="hidden sm:inline">Edit Profile</span>
                    </button>
                  )
                )}
                <button
                  onClick={requestCloseEmployeeView}
                  className="modal-close-admin"
                  aria-label={safeReturnTo ? `Back to ${returnLabel || 'previous page'}` : 'Close'}
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            {/* Navigation Tabs - Modern horizontal scrolling */}
            <div className="relative border-b border-gray-200 flex-shrink-0">
              {/* Scroll container with horizontal scroll */}
              <div className="flex items-center gap-2 px-4 md:px-6 py-3 md:flex-wrap overflow-x-auto md:overflow-x-visible scrollbar-hide scroll-smooth">
                {[
                  { id: 'personal', name: 'Personal', icon: User },
                  { id: 'employment', name: 'Employment', icon: Briefcase },
                  // Outsourced holders are not on payroll — hide compensation / professional / documents.
                  ...(viewingEmployee?.employment_classification === 'outsourced' ? [] : [
                    { id: 'compensation', name: 'Compensation', icon: DollarSign },
                    { id: 'professional', name: 'Professional', icon: Award },
                    ...(viewingEmployee?.employment_classification === 'worker' ? [
                      { id: 'skills-upgrading', name: 'Skills Upgrading', icon: Award }
                    ] : []),
                    { id: 'documents', name: 'Documents', icon: File },
                  ]),
                ].map((tab) => {
                  const IconComponent = tab.icon;
                  const isActive = activeViewTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveViewTab(tab.id)}
                      className={`tab-btn-admin ${isActive ? 'active' : ''}`}
                    >
                      <IconComponent className="w-4 h-4" />
                      <span className="hidden sm:inline">{tab.name}</span>
                      <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </div>
              
              {/* Scroll indicators */}
              <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none"></div>
              <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent pointer-events-none"></div>
            </div>

            {/* Content - Scrollable (vertical only; overflow-x-hidden stops any wide
                child from making the body horizontally pannable and dragging the modal) */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4 sm:p-5 lg:p-6" style={{ touchAction: 'pan-y' }}>
              {viewEmployeeLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                  <span className="ml-3 text-gray-600">Loading employee details...</span>
                </div>
              ) : (
                <>
                  {activeViewTab === 'personal' && (
                    <EmployeePersonalIdentityTab 
                      employee={viewingEmployee} 
                      hrProfile={viewEmployeeHRProfile}
                      getEmploymentPassTypeDisplay={getEmploymentPassTypeDisplay}
                      setViewEmployeeHRProfile={setViewEmployeeHRProfile}
                      isEditing={isViewEditing}
                      formData={viewFormData}
                      onChange={handleViewFormChange}
                      setFormData={setViewFormData}
                      onUploadFamilyDocument={handleUploadFamilyDocument}
                      onEmployeeRefresh={refreshViewingEmployee}
                    />
                  )}
                  {activeViewTab === 'employment' && (
                    <EmployeeEmploymentTab
                      employee={viewingEmployee}
                      hrProfile={viewEmployeeHRProfile}
                      isEditing={isViewEditing}
                      formData={viewFormData}
                      onChange={handleViewFormChange}
                      departments={departments}
                      employees={employees}
                      workScheduleTemplates={workScheduleTemplates}
                      onEmployeeRefresh={refreshViewingEmployee}
                      onMarkInactive={
                        viewingEmployee?.employment_classification === 'outsourced'
                          ? handleMarkOutsourcedInactive
                          : undefined
                      }
                      markInactivePending={offboardExternalMutation.isPending}
                      pendingEventPreset={pendingEmploymentEventPreset}
                      onPendingEventPresetConsumed={() => setPendingEmploymentEventPreset(null)}
                    />
                  )}
                  {activeViewTab === 'compensation' && (
                    <EmployeeCompensationBenefitsTab
                      employee={viewingEmployee}
                      hrProfile={viewEmployeeHRProfile}
                      onRecordSalaryChange={() => {
                        setPendingEmploymentEventPreset('salary_change');
                        setActiveViewTab('employment');
                      }}
                    />
                  )}
                  {activeViewTab === 'professional' && (
                    <EmployeeProfessionalTab employee={viewingEmployee} activeTab={activeViewTab} />
                  )}
                  {/* Legal tab merged into Personal tab - Compliance Information section */}
                  {activeViewTab === 'skills-upgrading' && viewingEmployee?.employment_classification === 'worker' && (
                    <SkillsUpgradingTab 
                      profile={viewingEmployee}
                      hrProfile={viewEmployeeHRProfile}
                      onReviewCertificates={() => {
                        // Prefill nested Documents state before remount (URL + localStorage).
                        try {
                          localStorage.setItem(
                            'admin-employees-documents-subTab',
                            JSON.stringify('verification'),
                          );
                          localStorage.setItem(
                            'admin-employees-documents-category',
                            JSON.stringify('professional'),
                          );
                        } catch {
                          /* ignore quota / private mode */
                        }
                        setSearchParams((prev) => {
                          const params = new URLSearchParams(prev);
                          params.set('tab', 'documents');
                          params.set('subTab', 'verification');
                          params.set('category', 'professional');
                          return params;
                        }, { replace: true });
                        setActiveViewTab('documents');
                      }}
                    />
                  )}
                  {activeViewTab === 'documents' && (
                    <EmployeeDocumentVerificationTab employee={viewingEmployee} hrProfile={viewEmployeeHRProfile} activeTab={activeViewTab} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Portal>
      )}
      <ConfirmModal
        isOpen={!!confirmDialog}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || 'Confirm'}
        cancelLabel={confirmDialog?.cancelLabel || 'Cancel'}
        variant={confirmDialog?.variant || 'danger'}
        onConfirm={() => {
          confirmCallbackRef.current?.(true);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        onCancel={() => {
          confirmCallbackRef.current?.(false);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        theme="admin"
      />

      <EmployeeExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        activeFilters={{
          search: searchTerm || '',
          department: selectedDepartment || '',
          employment_classification: isOutsourcedWorkforce ? 'outsourced' : '',
          employment_status: (() => {
            if (isOutsourcedWorkforce) return selectedStatus || '';
            if (selectedStatus === 'active' && selectedSubStatus) return selectedSubStatus;
            return selectedStatus || '';
          })(),
          include_inactive: selectedStatus === '' || selectedStatus === 'inactive',
        }}
      />
    </div>
  );
};

// Employee View Tab Components - Reorganized (Combined)
const EmployeePersonalIdentityTab = ({
  employee,
  hrProfile,
  getEmploymentPassTypeDisplay,
  setViewEmployeeHRProfile,
  isEditing,
  formData,
  onChange,
  setFormData,
  onUploadFamilyDocument,
  onEmployeeRefresh,
}) => {
  const [sensitiveFieldsVisible, setSensitiveFieldsVisible] = React.useState({});
  const [showPassDates, setShowPassDates] = React.useState(false);
  const [drivingLicenseCount, setDrivingLicenseCount] = React.useState(0);
  // Progressive disclosure for low-frequency Personal sections (matches Profile.js).
  const [openPersonalSections, setOpenPersonalSections] = React.useState({
    emergency: false,
    family: false,
    driving: false,
  });
  const [emergencyContactCount, setEmergencyContactCount] = React.useState(0);
  const [familyDependentCount, setFamilyDependentCount] = React.useState(0);
  const emergencyAddHandlerRef = React.useRef(null);
  const togglePersonalSection = (key) => {
    setOpenPersonalSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  // Focused bond edit (same job-to-be-done as Employment-tab date pencil —
  // renew/correct without unlocking the full profile form).
  const [bondEditing, setBondEditing] = React.useState(false);
  const [bondDate, setBondDate] = React.useState('');
  const [bondSaving, setBondSaving] = React.useState(false);
  const isOutsourcedPersonal = employee?.employment_classification === 'outsourced';
  const workEmailErr = workEmailDomainError(
    formData?.work_email,
    employee?.employment_classification,
  );
  const storedWorkEmailInvalid = Boolean(
    employee?.work_email
      && !isValidWorkEmail(employee.work_email)
      && !['system', 'outsourced'].includes(employee?.employment_classification),
  );

  // An employee has exactly ONE address: residential XOR dormitory (enforced by
  // HRProfile.clean()). The edit form needs a single source of truth for which
  // type is being edited so we never write both sets of fields at once.
  const [addressTypeEdit, setAddressTypeEdit] = React.useState(
    hrProfile?.primary_address?.address_type === 'dormitory' ? 'dormitory' : 'residential'
  );

  // Keep the edit selector in sync with the saved profile until the user starts editing.
  React.useEffect(() => {
    if (!isEditing) {
      setAddressTypeEdit(
        hrProfile?.primary_address?.address_type === 'dormitory' ? 'dormitory' : 'residential'
      );
    }
  }, [isEditing, hrProfile?.primary_address?.address_type]);

  React.useEffect(() => {
    if (isEditing) setBondEditing(false);
  }, [isEditing]);

  React.useEffect(() => {
    setBondEditing(false);
    setBondDate('');
  }, [employee?.staff_id]);

  const openBondEdit = () => {
    setBondDate(employee?.security_bond_expiry_date || '');
    setBondEditing(true);
  };

  const handleBondEditCancel = () => {
    setBondEditing(false);
    setBondDate('');
  };

  const handleBondEditSave = async () => {
    const nextVal = bondDate || '';
    const origVal = employee?.security_bond_expiry_date || '';
    if (nextVal === origVal) {
      toast('No changes to save');
      setBondEditing(false);
      return;
    }
    if (!nextVal) {
      toast.error('Security bond expiry date is required for Work Permit holders');
      return;
    }
    setBondSaving(true);
    try {
      await staffAPI.updateStaff(employee.staff_id, {
        security_bond_expiry_date: nextVal,
      });
      toast.success('Security bond expiry updated');
      setBondEditing(false);
      if (onEmployeeRefresh) await onEmployeeRefresh();
    } catch (err) {
      const data = err?.response?.data;
      const pickMsg = (val) => (Array.isArray(val) ? val[0] : val);
      const detail = pickMsg(data?.security_bond_expiry_date)
        || pickMsg(data?.detail)
        || (data && typeof data === 'object'
          ? pickMsg(Object.values(data).find((v) => typeof v === 'string' || Array.isArray(v)))
          : null);
      toast.error(detail || 'Failed to update security bond expiry');
    } finally {
      setBondSaving(false);
    }
  };

  // Switching address type clears the opposite set so we never persist both
  // (HRProfile.clean() rejects an employee with residential AND dormitory data).
  const handleAddressTypeChange = (newType) => {
    setAddressTypeEdit(newType);
    if (newType === 'dormitory') {
      onChange('hrprofile_residential_address', '');
      onChange('hrprofile_residential_postal_code', '');
      onChange('hrprofile_residential_unit_number', '');
      onChange('hrprofile_residential_address_location', '');
      onChange('hrprofile_residential_address_country', '');
    } else {
      onChange('hrprofile_dormitory_address', '');
      onChange('hrprofile_dormitory_postal_code', '');
      onChange('hrprofile_dorm_unit', '');
    }
  };
  
  const formatFieldValue = (value) => {
    if (!value) return '';
    // Handle cases where encrypted fields might return memory addresses or objects
    if (typeof value === 'object' && value !== null) {
      // If it's an object, try to get a meaningful string representation
      if (value.toString && value.toString() !== '[object Object]') {
        return value.toString();
      }
      return 'Data processing error';
    }
    // Handle memory address strings
    if (typeof value === 'string' && value.includes('<memory at 0x')) {
      return 'Data processing error';
    }
    
    // Format specific field values for better display
    if (typeof value === 'string') {
      const maritalLabel = getMaritalStatusLabel(value);
      if (maritalLabel) {
        return maritalLabel;
      }
      
      // Format nationality - Standardize display format
      const nationalityMap = {
        'singapore': 'Singaporean',     // Demonym
        'malaysia': 'Malaysian',       // Demonym
        'malaysian': 'Malaysian',      // Demonym
        'china': 'Chinese',            // Demonym
        'chinese': 'Chinese',          // Demonym
        'india': 'Indian',             // Demonym
        'indian': 'Indian',            // Demonym
        'philippines': 'Filipino',     // Demonym
        'indonesia': 'Indonesian',     // Demonym
        'indonesian': 'Indonesian',    // Demonym
        'thailand': 'Thai',            // Demonym
        'thai': 'Thai',                // Demonym
        'vietnam': 'Vietnamese',       // Demonym
        'vietnamese': 'Vietnamese',    // Demonym
        'myanmar': 'Burmese',          // Ethnic descriptor (as requested)
        'burmese': 'Burmese',          // Ethnic descriptor (as requested)
        'bangladesh': 'Bangladeshi',   // Demonym
        'bangladeshi': 'Bangladeshi'   // Demonym
      };
      
      if (nationalityMap[value.toLowerCase()]) {
        return nationalityMap[value.toLowerCase()];
      }

      if (RACE_DISPLAY[value.toLowerCase()]) {
        return RACE_DISPLAY[value.toLowerCase()];
      }

      // Format religion (workplace accommodations)
      if (RELIGION_DISPLAY[value.toLowerCase()]) {
        return RELIGION_DISPLAY[value.toLowerCase()];
      }
    }
    
    return value;
  };

  const getGenderDisplay = (genderCode) => {
    if (!genderCode) return '';
    switch (genderCode.toUpperCase()) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
      case 'O':
        return 'Other';
      default:
        return genderCode;
    }
  };
  
  const toggleFieldVisibility = (fieldName) => {
    setSensitiveFieldsVisible(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // Employment Pass grid uses size="comfortable" on EVERY label row so a
  // micro edit on one cell (Security Bond) cannot change the shared baseline.
  const PASS_LABEL_SIZE = 'comfortable';
  const passReminderDays = hrProfile?.hrprofile_pass_renewal_reminder_days || 90;
  const renderPassDateField = (props) => (
    <PassDateFieldDisplay
      theme="admin"
      size={PASS_LABEL_SIZE}
      reminderDays={passReminderDays}
      {...props}
      isVisible={!!sensitiveFieldsVisible[props.fieldName]}
      onToggleVisibility={toggleFieldVisibility}
    />
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <User className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-gray-900 truncate min-w-0">
            Personal & Identity Information
          </h2>
        </div>
        <div className="flex-shrink-0">
          {!hrProfile ? (
            <StatusBadge status="pending" label="HR Profile Incomplete" showIcon />
          ) : (
            <StatusBadge
              status={hrProfile.hrprofile_consent_obtained ? 'approved' : 'pending'}
              label={
                hrProfile.hrprofile_consent_obtained
                  ? (hrProfile.hrprofile_consent_date
                    ? `PDPA · ${formatDate(hrProfile.hrprofile_consent_date)}`
                    : 'PDPA Obtained')
                  : 'PDPA Consent Pending'
              }
              showIcon={!hrProfile.hrprofile_consent_obtained}
            />
          )}
        </div>
      </div>

      {/* Identity & Personal Details — demographics + NRIC identity + MOM race; religion is accommodation-only */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <User className="w-5 h-5 text-red-600" />
          Identity & Personal Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className="field-label">First Name</label>
            {isEditing ? (
              <input className="input-admin" value={formData.first_name || ''} onChange={e => onChange('first_name', e.target.value)} />
            ) : (
              <p className="field-display">{formatFieldValue(employee?.first_name) || 'Not provided'}</p>
            )}
          </div>
          <div>
            <label className="field-label">Last Name</label>
            {isEditing ? (
              <input className="input-admin" value={formData.last_name || ''} onChange={e => onChange('last_name', e.target.value)} />
            ) : (
              <p className="field-display">{formatFieldValue(employee?.last_name) || 'Not provided'}</p>
            )}
          </div>
          <div>
            <label className="field-label">Alias</label>
            {isEditing ? (
              <input
                className="input-admin"
                value={formData.alias || ''}
                onChange={e => onChange('alias', e.target.value)}
                maxLength={100}
                placeholder="Workplace display name"
              />
            ) : (
              <p className="field-display">{formatFieldValue(employee?.alias) || 'Not provided'}</p>
            )}
            <p className="form-help">Optional. Does not change legal name.</p>
          </div>
          <div>
            <label className="field-label">Gender</label>
            {isEditing ? (
              <FormSelect
                options={[
                  { value: '', label: 'Select' },
                  { value: 'M', label: 'Male' },
                  { value: 'F', label: 'Female' },
                  { value: 'O', label: 'Other' },
                ]}
                value={formData.hrprofile_gender || ''}
                onChange={(val) => onChange('hrprofile_gender', val)}
                theme="admin"
              />
            ) : (
              <p className="field-display">{getGenderDisplay(hrProfile?.hrprofile_gender) || 'Not provided'}</p>
            )}
          </div>
          <div>
            {isEditing ? (
              <>
                <label className="field-label">Date of Birth</label>
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(formData.hrprofile_date_of_birth)}
                  onChange={(date) => onChange('hrprofile_date_of_birth', date ? toLocalISODate(date) : '')}
                  isClearable
                  placeholderText="Select date"
                />
              </>
            ) : (
              <SharedSensitiveField
                label="Date of Birth"
                value={hrProfile?.hrprofile_date_of_birth ? formatDate(hrProfile.hrprofile_date_of_birth) : null}
                fieldName="date_of_birth"
                type="date"
              />
            )}
          </div>
          <div>
            <label className="field-label">Nationality</label>
            {isEditing ? (
              <input className="input-admin" value={formData.hrprofile_nationality || ''} onChange={e => onChange('hrprofile_nationality', e.target.value)} placeholder="e.g. Singaporean" />
            ) : (
              <p className="field-display">{formatFieldValue(hrProfile?.hrprofile_nationality)}</p>
            )}
          </div>
          <CountryOfBirthField
            theme="admin"
            isEditing={isEditing}
            value={formData.hrprofile_country_of_birth}
            onChange={(val) => onChange('hrprofile_country_of_birth', val || '')}
            displayValue={hrProfile?.hrprofile_country_of_birth || 'Not provided'}
          />
          <div>
            <label className="field-label">
              Marital Status
              {isEditing && <span className="text-red-500"> *</span>}
            </label>
            {isEditing ? (
              <FormSelect
                options={MARITAL_STATUS_OPTIONS}
                value={formData.hrprofile_marital_status || ''}
                onChange={(val) => onChange('hrprofile_marital_status', val)}
                placeholder="Select marital status"
                theme="admin"
              />
            ) : (
              <p className="field-display">
                {getMaritalStatusLabel(hrProfile?.hrprofile_marital_status) || 'Not provided'}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <SharedSensitiveField
              label="Name as per NRIC/FIN"
              value={hrProfile?.hrprofile_name_as_per_nric_fin}
              fieldName="name_as_per_nric_fin"
              type="name"
              theme="admin"
            />
            <StatusBadge
              status={hrProfile?.hrprofile_name_as_per_nric_fin_verified_at ? 'approved' : 'pending'}
              label={
                hrProfile?.hrprofile_name_as_per_nric_fin_verified_at
                  ? `Verified ${formatDate(hrProfile.hrprofile_name_as_per_nric_fin_verified_at)}`
                  : 'Provisional or missing'
              }
              showIcon
            />
          </div>
          <div>
            {isEditing ? (
              <>
                <label className="field-label">NRIC/FIN Number</label>
                <input className="input-admin" value={formData.hrprofile_nric_fin_number || ''} onChange={e => onChange('hrprofile_nric_fin_number', e.target.value)} />
              </>
            ) : (
              <SharedSensitiveField label="NRIC/FIN Number" value={hrProfile?.hrprofile_nric_fin_number} fieldName="nric_fin" type="nric" />
            )}
          </div>
          <div>
            <FieldLabelRow label="Race" />
            {isEditing ? (
              <FormSelect
                options={[
                  { value: '', label: 'Select' },
                  ...RACE_OPTIONS,
                ]}
                value={formData.hrprofile_race || ''}
                onChange={(val) => onChange('hrprofile_race', val)}
                theme="admin"
              />
            ) : (
              <p className="field-display">{formatFieldValue(hrProfile?.hrprofile_race) || 'Not disclosed'}</p>
            )}
            <p className="form-help">For MOM statistical reporting.</p>
          </div>
          <div>
            <FieldLabelRow label="Religion" />
            {isEditing ? (
              <FormSelect
                options={RELIGION_OPTIONS}
                value={formData.hrprofile_religion || RELIGION_DEFAULT}
                onChange={(val) => onChange('hrprofile_religion', val)}
                theme="admin"
              />
            ) : (
              <p className="field-display">{formatFieldValue(hrProfile?.hrprofile_religion) || 'Not disclosed'}</p>
            )}
            <p className="form-help">For workplace accommodations only. Not used for MOM race reporting.</p>
          </div>
        </div>
      </div>

      {/* Identity Documents Section — view-mode labels all use FieldLabelRow
          size="comfortable" so a micro edit on Security Bond shares the same
          32px label track as every sibling (never a one-off layout exception). */}
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2 min-w-0">
            <Shield className="w-5 h-5 text-red-600 shrink-0" />
            Pass & Immigration
          </h3>
          {!isEditing && bondEditing && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleBondEditCancel}
                disabled={bondSaving}
                className="btn btn-sm btn-admin-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBondEditSave}
                disabled={bondSaving}
                className="btn btn-sm btn-admin-primary"
              >
                {bondSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            {isEditing ? (
              <label className="field-label">Employment Pass Type</label>
            ) : (
              <FieldLabelRow label="Employment Pass Type" size={PASS_LABEL_SIZE} />
            )}
            {isEditing ? (
              <FormSelect
                options={[
                  { value: '', label: 'Select' },
                  { value: 'citizen', label: 'Singapore Citizen' },
                  { value: 'pr', label: 'Permanent Resident' },
                  { value: 'ep', label: 'Employment Pass' },
                  { value: 'sp', label: 'S Pass' },
                  { value: 'wp', label: 'Work Permit' },
                  { value: 'dp', label: "Dependant's Pass" },
                  { value: 'ltvp', label: 'Long Term Visit Pass' },
                  { value: 'ltvp_plus', label: 'LTVP+' },
                  { value: 'other', label: 'Other' },
                ]}
                value={formData.hrprofile_employment_pass_type || ''}
                onChange={(val) => onChange('hrprofile_employment_pass_type', val)}
                theme="admin"
              />
            ) : (
              <p className="field-display">
                {getEmploymentPassTypeDisplay(hrProfile?.hrprofile_employment_pass_type) || 'Not provided'}
              </p>
            )}
          </div>
          {(isEditing ? formData.hrprofile_employment_pass_type === 'pr' : hrProfile?.hrprofile_employment_pass_type === 'pr') && (
            <div>
              {isEditing ? (
                <label className="field-label">PR Obtained Date</label>
              ) : (
                <FieldLabelRow label="PR Obtained Date" size={PASS_LABEL_SIZE} />
              )}
              {isEditing ? (
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(formData.hrprofile_pr_obtained_date)}
                  onChange={(date) => onChange('hrprofile_pr_obtained_date', date ? toLocalISODate(date) : '')}
                  isClearable
                  placeholderText="Select date"
                />
              ) : (
                <p className="field-display">
                  {hrProfile?.hrprofile_pr_obtained_date ? formatDate(hrProfile.hrprofile_pr_obtained_date) : 'Not provided'}
                </p>
              )}
            </div>
          )}

          {(isEditing
            ? formData.hrprofile_employment_pass_type && !['citizen', 'pr'].includes(formData.hrprofile_employment_pass_type)
            : hrProfile?.hrprofile_employment_pass_type && !['citizen', 'pr'].includes(hrProfile.hrprofile_employment_pass_type)
          ) && (
            <>
              <div>
                {isEditing ? (
                  <>
                    <label className="field-label">Pass Issue Date</label>
                    <DatePickerField
                      theme="admin"
                      selected={parseLocalDate(formData.hrprofile_pass_issue_date)}
                      onChange={(date) => onChange('hrprofile_pass_issue_date', date ? toLocalISODate(date) : '')}
                      isClearable
                      placeholderText="Select date"
                    />
                  </>
                ) : (
                  renderPassDateField({
                    label: 'Pass Issue Date',
                    value: hrProfile?.hrprofile_pass_issue_date,
                    fieldName: 'pass_issue_date',
                    kind: 'issued',
                  })
                )}
              </div>
              <div>
                {isEditing ? (
                  <>
                    <label className="field-label">Pass Expiry Date</label>
                    <DatePickerField
                      theme="admin"
                      selected={parseLocalDate(formData.hrprofile_pass_expiry_date)}
                      onChange={(date) => onChange('hrprofile_pass_expiry_date', date ? toLocalISODate(date) : '')}
                      isClearable
                      placeholderText="Select date"
                    />
                  </>
                ) : (
                  renderPassDateField({
                    label: 'Pass Expiry Date',
                    value: hrProfile?.hrprofile_pass_expiry_date,
                    fieldName: 'pass_expiry_date',
                  })
                )}
              </div>
              {['s_pass', 'wp'].includes(
                isEditing
                  ? formData.hrprofile_employment_pass_type
                  : hrProfile?.hrprofile_employment_pass_type
              ) && (
              <div>
                {isEditing ? (
                  <>
                    <label className="field-label">
                      {formData.hrprofile_employment_pass_type === 's_pass'
                        ? 'S Pass Number'
                        : 'Work Permit Number'}
                    </label>
                    <input className="input-admin" value={formData.hrprofile_wp_number || ''} onChange={e => onChange('hrprofile_wp_number', e.target.value)} />
                  </>
                ) : (
                  <SharedSensitiveField
                    label={hrProfile?.hrprofile_employment_pass_type === 's_pass' ? 'S Pass Number' : 'Work Permit Number'}
                    value={hrProfile?.hrprofile_wp_number}
                    fieldName="wp_number"
                    type="wp_number"
                  />
                )}
              </div>
              )}
              {(isEditing ? formData.hrprofile_employment_pass_type === 'wp' : hrProfile?.hrprofile_employment_pass_type === 'wp') && (
              <div>
                {isEditing ? (
                  <>
                    <label className="field-label">Security Bond Expiry Date</label>
                    <DatePickerField
                      theme="admin"
                      selected={parseLocalDate(formData.security_bond_expiry_date)}
                      onChange={(date) => onChange('security_bond_expiry_date', date ? toLocalISODate(date) : '')}
                      placeholderText="Select date"
                    />
                  </>
                ) : bondEditing ? (
                  <>
                    <FieldLabelRow label="Security Bond Expiry" size={PASS_LABEL_SIZE} />
                    <DatePickerField
                      theme="admin"
                      selected={parseLocalDate(bondDate)}
                      onChange={(date) => setBondDate(date ? toLocalISODate(date) : '')}
                      placeholderText="Select date"
                    />
                  </>
                ) : (
                  renderPassDateField({
                    label: 'Security Bond Expiry',
                    value: employee?.security_bond_expiry_date,
                    fieldName: 'security_bond_expiry',
                    action: (
                      <button
                        type="button"
                        onClick={openBondEdit}
                        className="icon-button-admin icon-button-sm"
                        title="Edit security bond expiry"
                        aria-label="Edit security bond expiry"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    ),
                  })
                )}
              </div>
              )}
              <div>
                {isEditing ? (
                  <label className="field-label">Work Permit Application Date</label>
                ) : (
                  <FieldLabelRow label="Work Permit Application Date" size={PASS_LABEL_SIZE} />
                )}
                {isEditing ? (
                  <DatePickerField
                    theme="admin"
                    selected={parseLocalDate(formData.hrprofile_wp_application_date)}
                    onChange={(date) => onChange('hrprofile_wp_application_date', date ? toLocalISODate(date) : '')}
                    isClearable
                    placeholderText="Select date"
                  />
                ) : (
                  <p className="field-display">
                    {hrProfile?.hrprofile_wp_application_date ? formatDate(hrProfile.hrprofile_wp_application_date) : 'Not provided'}
                  </p>
                )}
              </div>
              <div>
                {isEditing ? (
                  <>
                    <label className="field-label">Passport Number</label>
                    <input className="input-admin" value={formData.hrprofile_passport_number || ''} onChange={e => onChange('hrprofile_passport_number', e.target.value)} />
                  </>
                ) : (
                  <SharedSensitiveField label="Passport Number" value={hrProfile?.hrprofile_passport_number} fieldName="passport_number" type="passport" />
                )}
              </div>
              <div>
                {isEditing ? (
                  <>
                    <label className="field-label">Passport Expiry Date</label>
                    <DatePickerField
                      theme="admin"
                      selected={parseLocalDate(formData.hrprofile_passport_expiry_date)}
                      onChange={(date) => onChange('hrprofile_passport_expiry_date', date ? toLocalISODate(date) : '')}
                      isClearable
                      placeholderText="Select date"
                    />
                  </>
                ) : (
                  renderPassDateField({
                    label: 'Passport Expiry Date',
                    value: hrProfile?.hrprofile_passport_expiry_date,
                    fieldName: 'passport_expiry_date',
                  })
                )}
              </div>
              <div>
                {isEditing ? (
                  <label className="field-label">Renewal Reminder Days</label>
                ) : (
                  <FieldLabelRow label="Renewal Reminder Days" size={PASS_LABEL_SIZE} />
                )}
                {isEditing ? (
                  <input type="number" className="input-admin" value={formData.hrprofile_renewal_reminder_days || ''} onChange={e => onChange('hrprofile_renewal_reminder_days', e.target.value)} placeholder="90" />
                ) : (
                  <p className="field-display">
                    {hrProfile?.hrprofile_pass_renewal_reminder_days || 90} days
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Phone className="w-5 h-5 text-red-600" />
          Contact Information
        </h3>
        
        {/* Work Contact */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Work Contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Work Email</label>
              {isEditing ? (
                <>
                  <input
                    type="email"
                    className={`input-admin ${
                      workEmailErr
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : ''
                    }`}
                    value={formData.work_email || ''}
                    onChange={e => onChange('work_email', e.target.value)}
                    placeholder={`name@${COMPANY_EMAIL_DOMAIN}`}
                  />
                  {workEmailErr ? (
                    <p className="mt-1 text-xs text-red-600">{workEmailErr}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      Company mailbox (@{COMPANY_EMAIL_DOMAIN}) only. Leave blank if none.
                      Personal addresses go under Personal Email.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="field-display">{employee?.work_email || 'Not provided'}</p>
                  {storedWorkEmailInvalid && (
                    <p className="mt-1 text-xs text-amber-700 flex items-start gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      Not a company @{COMPANY_EMAIL_DOMAIN} address — move personal mail to Personal Email and clear this field.
                    </p>
                  )}
                </>
              )}
            </div>
            <div>
              <label className="field-label">Work Phone</label>
              {isEditing ? (
                <>
                  <input type="tel" className="input-admin" value={formData.work_phone_number || ''} onChange={e => onChange('work_phone_number', e.target.value)} placeholder="e.g. 9123 4567" />
                  <p className="mt-1 text-xs text-gray-500">
                    {isSgOfficeDid(formData.work_phone_number)
                      ? 'This is the desk number, also kept on Direct line. A company mobile replaces this field when a SIM is assigned.'
                      : 'Company mobile. A person-held SIM is changed in the Asset Portal.'}
                  </p>
                </>
              ) : (
                <>
                  <p className="field-display">
                    {splitWorkContact(employee).mobile
                      ? formatPhoneForDisplay(splitWorkContact(employee).mobile)
                      : 'Not provided'}
                  </p>
                  {!splitWorkContact(employee).mobile && splitWorkContact(employee).did && (
                    <p className="mt-1 text-xs text-gray-500">
                      No company mobile. The desk number is the direct line.
                    </p>
                  )}
                </>
              )}
            </div>
            <div>
              <label className="field-label">Direct line</label>
              {isEditing ? (
                <>
                  <input type="tel" className="input-admin" value={formData.did_number || ''} onChange={e => onChange('did_number', e.target.value)} placeholder="e.g. 6481 8423" />
                  <p className="mt-1 text-xs text-gray-500">
                    Office DID, usually 6xxx. Stays when a company mobile replaces Work Phone.
                  </p>
                </>
              ) : (
                <p className="field-display">{employee?.did_number ? formatPhoneForDisplay(employee.did_number) : 'Not provided'}</p>
              )}
            </div>
            <div>
              <label className="field-label">Extension</label>
              {isEditing ? (
                <>
                  <input className="input-admin" value={formData.extension_number || ''} onChange={e => onChange('extension_number', e.target.value)} placeholder="e.g. 201" />
                  <p className="mt-1 text-xs text-gray-500">
                    Rings after the direct line. It is not part of the mobile.
                  </p>
                </>
              ) : (
                <p className="field-display">{employee?.extension_number || 'Not provided'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Personal Contact */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Personal Contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              {isEditing ? (
                <>
                  <label className="field-label">Personal Email</label>
                  <input type="email" className="input-admin" value={formData.hrprofile_personal_email || ''} onChange={e => onChange('hrprofile_personal_email', e.target.value)} />
                </>
              ) : (
                <SharedSensitiveField label="Personal Email" value={hrProfile?.hrprofile_personal_email} fieldName="personal_email" type="email" />
              )}
            </div>
            <div>
              {isEditing ? (
                <>
                  <label className="field-label">Personal Phone</label>
                  <input type="tel" className="input-admin" value={formData.hrprofile_personal_phone_number || ''} onChange={e => onChange('hrprofile_personal_phone_number', e.target.value)} />
                </>
              ) : (
                <SharedSensitiveField label="Personal Phone" value={hrProfile?.hrprofile_personal_phone_number} fieldName="personal_phone" type="phone" />
              )}
            </div>
          </div>
        </div>

        {/* Address Section
            HR mental model:
            1. Address type — Residential XOR Dormitory
            2. Residential → Singapore | Overseas (shared editor)
            3. Dormitory → Singapore postal lookup only (local housing) */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            Address
          </h4>

          {(() => {
            const resolvedType = isEditing
              ? addressTypeEdit
              : (hrProfile?.primary_address?.address_type || 'residential');
            const isDormitory = resolvedType === 'dormitory';
            const typeLabel = isDormitory ? 'Dormitory Address' : 'Residential Address';
            const overseasLocation = inferResidentialAddressLocation({
              address: hrProfile?.hrprofile_residential_address || hrProfile?.primary_address?.address,
              postalCode: hrProfile?.hrprofile_residential_postal_code || hrProfile?.primary_address?.postal_code,
              location: hrProfile?.hrprofile_residential_address_location || hrProfile?.primary_address?.location,
              country: hrProfile?.hrprofile_residential_address_country || hrProfile?.primary_address?.country,
            });

            if (isEditing) {
              return (
                <div className="space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <label className="form-label-admin mb-0.5">Address type</label>
                      <p className="form-help mb-0">
                        An employee has one address — residential or dormitory, not both.
                      </p>
                    </div>
                    <PillToggle
                      value={resolvedType}
                      onChange={handleAddressTypeChange}
                      options={[
                        { id: 'residential', label: 'Residential', icon: MapPin },
                        { id: 'dormitory', label: 'Dormitory', icon: Building },
                      ]}
                      variant="text"
                      theme="admin"
                      layout="sectionHeader"
                      ariaLabel="Address type"
                    />
                  </div>

                  {!isDormitory ? (
                    <ResidentialAddressEditor
                      isEditing
                      formData={formData}
                      setFormData={setFormData}
                      onChangeField={onChange}
                      primaryAddress={hrProfile?.primary_address}
                      theme="admin"
                      audience="hr"
                      showAddressTypeBadge={false}
                    />
                  ) : (
                    <div className="space-y-4">
                      <PostalCodeLookup
                        value={formData.hrprofile_dormitory_postal_code}
                        onAddressFound={(geocodedData) => {
                          onChange('hrprofile_dormitory_address', geocodedData.address);
                          onChange(
                            'hrprofile_dormitory_postal_code',
                            geocodedData.postal_code || ''
                          );
                          toast.success('Dormitory address auto-filled from postal code');
                        }}
                        onPostalCodeChange={(code) =>
                          onChange('hrprofile_dormitory_postal_code', code)
                        }
                        onError={(error) => toast.error(error)}
                        placeholder="Enter Singapore postal code (e.g., 018989)"
                        buttonText="Find Address"
                        isAdmin
                      />
                      <p className="form-help flex items-start gap-1.5">
                        <Lightbulb className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span>Dormitory addresses are Singapore-based — use 6-digit postal lookup.</span>
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <label className="form-label-admin">Dormitory Address</label>
                          <textarea
                            className="textarea-admin min-h-[60px]"
                            value={formData.hrprofile_dormitory_address || ''}
                            onChange={(e) => onChange('hrprofile_dormitory_address', e.target.value)}
                            rows={2}
                          />
                        </div>
                        <div>
                          <label className="form-label-admin">Unit / Room</label>
                          <input
                            className="input-admin"
                            value={formData.hrprofile_dorm_unit || ''}
                            onChange={(e) => onChange('hrprofile_dorm_unit', e.target.value)}
                            placeholder="e.g., #01-23"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // View mode — read-only; edit to change (same Singapore / Overseas path)
            return (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <SharedSensitiveField
                      label={typeLabel}
                      value={hrProfile?.primary_address?.address || hrProfile?.[isDormitory ? 'hrprofile_dormitory_address' : 'hrprofile_residential_address']}
                      fieldName="primary_address"
                      type="address"
                      multiline
                    />
                  </div>
                  <div className="flex flex-col justify-end">
                    <SharedSensitiveField
                      label={isDormitory ? 'Unit / Room' : 'Unit Number'}
                      value={
                        hrProfile?.primary_address?.unit_number ||
                        hrProfile?.[isDormitory ? 'hrprofile_dorm_unit' : 'hrprofile_residential_unit_number']
                      }
                      fieldName="primary_unit_number"
                      type="default"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {hrProfile?.primary_address?.address_type && (
                    <InfoBadge color={isDormitory ? 'orange' : 'blue'} label={typeLabel} />
                  )}
                  {!isDormitory && overseasLocation === 'overseas' && (
                    <InfoBadge
                      color="purple"
                      label={`Overseas · ${OVERSEAS_RESIDENTIAL_COUNTRY}`}
                    />
                  )}
                </div>
                <p className="form-help">
                  Click Edit to update address — Singapore postal lookup or Malaysia (overseas commute).
                </p>
              </div>
            );
          })()}
        </div>
      </div>

      <AccordionSection
        expanded={openPersonalSections.emergency}
        onToggle={() => togglePersonalSection('emergency')}
        theme="admin"
        contentVariant="padded"
        icon={<Contact className="w-4 h-4 text-red-600" />}
        iconBg="bg-red-50"
        title={
          <span className="inline-flex items-center gap-2 min-w-0">
            <span className="truncate">Emergency Contacts</span>
            {emergencyContactCount > 0 ? (
              <CountBadge
                count={emergencyContactCount}
                theme="admin"
                size="md"
                className="flex-shrink-0"
              />
            ) : null}
          </span>
        }
        trailing={
          <button
            type="button"
            onClick={() => emergencyAddHandlerRef.current?.()}
            className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
            aria-label="Add emergency contact"
            title="Add emergency contact"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            <span className="hidden sm:inline">Add Contact</span>
          </button>
        }
      >
        <EmployeeEmergencyContactsSection
          staffId={employee?.staff_id}
          hrProfile={hrProfile}
          embedded
          addHandlerRef={emergencyAddHandlerRef}
          onCountChange={setEmergencyContactCount}
        />
      </AccordionSection>

      {!isOutsourcedPersonal && (
        <AccordionSection
          expanded={openPersonalSections.family}
          onToggle={() => togglePersonalSection('family')}
          theme="admin"
          contentVariant="padded"
          icon={<Heart className="w-4 h-4 text-red-600" />}
          iconBg="bg-red-50"
          title={
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="truncate">Family & Dependents</span>
              {familyDependentCount > 0 ? (
                <CountBadge
                  count={familyDependentCount}
                  theme="admin"
                  size="md"
                  className="flex-shrink-0"
                />
              ) : null}
            </span>
          }
        >
          <EmployeeFamilyTab
            employee={employee}
            hrProfile={hrProfile}
            onUploadFamilyDocument={onUploadFamilyDocument}
            embedded
            onCountChange={setFamilyDependentCount}
          />
        </AccordionSection>
      )}

      {/* Driving — Accordion when licences exist; manager still mounts for count when none */}
      <div className={drivingLicenseCount > 0 ? '' : 'hidden'}>
        <AccordionSection
          expanded={openPersonalSections.driving}
          onToggle={() => togglePersonalSection('driving')}
          theme="admin"
          contentVariant="padded"
          icon={<Car className="w-4 h-4 text-red-600" />}
          iconBg="bg-red-50"
          title={
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="truncate">Driving Licences</span>
              {drivingLicenseCount > 0 ? (
                <CountBadge
                  count={drivingLicenseCount}
                  theme="admin"
                  size="md"
                  className="flex-shrink-0"
                />
              ) : null}
            </span>
          }
        >
          <DrivingLicenseManager
            staffId={employee?.staff_id}
            isEditing={false}
            readOnly={true}
            employmentPassType={hrProfile?.hrprofile_employment_pass_type}
            onCountChange={setDrivingLicenseCount}
          />
        </AccordionSection>
      </div>
      {drivingLicenseCount === 0 && (
        <div className="hidden" aria-hidden="true">
          <DrivingLicenseManager
            staffId={employee?.staff_id}
            isEditing={false}
            readOnly={true}
            employmentPassType={hrProfile?.hrprofile_employment_pass_type}
            onCountChange={setDrivingLicenseCount}
          />
        </div>
      )}
    </div>
  );
};

const isValidEmergencyPhone = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  try {
    return validatePhone(phone);
  } catch (error) {
    return phone.startsWith('+') && phone.length >= 8;
  }
};

// HR emergency-contact CRUD for the employee file.
// When embedded=true, AccordionSection owns the card chrome + title.
const EmployeeEmergencyContactsSection = ({
  staffId,
  hrProfile,
  embedded = false,
  addHandlerRef,
  onCountChange,
}) => {
  const { data: contacts = [], isLoading, isError } = useEmergencyContactsForStaff(staffId);
  const createMutation = useCreateEmergencyContact(staffId);
  const updateMutation = useUpdateEmergencyContact(staffId);
  const deleteMutation = useDeleteEmergencyContact(staffId);
  const [visibleFields, setVisibleFields] = React.useState({});
  const [isAdding, setIsAdding] = React.useState(false);
  const [editingContact, setEditingContact] = React.useState(null);
  const [formData, setFormData] = React.useState({
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_primary_phone: '',
    emergency_contact_secondary_phone: '',
    emergency_contact_email: '',
  });
  const [formErrors, setFormErrors] = React.useState({});
  const confirmCallbackRef = React.useRef(null);
  const [confirmDialog, setConfirmDialog] = React.useState(null);

  const isForeignEmployee = React.useMemo(() => {
    const passType = hrProfile?.hrprofile_employment_pass_type;
    return passType && !['citizen', 'pr'].includes(passType);
  }, [hrProfile?.hrprofile_employment_pass_type]);

  React.useEffect(() => {
    if (typeof onCountChange === 'function' && !isLoading) {
      onCountChange(contacts.length);
    }
  }, [contacts.length, isLoading, onCountChange]);

  const resetForm = React.useCallback(() => {
    setFormData({
      emergency_contact_name: '',
      emergency_contact_relationship: '',
      emergency_contact_primary_phone: '',
      emergency_contact_secondary_phone: '',
      emergency_contact_email: '',
    });
    setFormErrors({});
    setIsAdding(false);
    setEditingContact(null);
  }, []);

  const handleAddContact = React.useCallback(() => {
    resetForm();
    setIsAdding(true);
  }, [resetForm]);

  React.useLayoutEffect(() => {
    if (!addHandlerRef) return undefined;
    addHandlerRef.current = handleAddContact;
    return () => {
      addHandlerRef.current = null;
    };
  }, [addHandlerRef, handleAddContact]);

  const showConfirm = (config) => {
    return new Promise((resolve) => {
      confirmCallbackRef.current = resolve;
      setConfirmDialog(config);
    });
  };

  const toggleVisibility = (key) => {
    setVisibleFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const mask = (value, key, type = 'phone') => {
    if (!value) return 'Not provided';
    if (visibleFields[key]) return value;
    return maskValue(value, type);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.emergency_contact_name.trim()) {
      errors.emergency_contact_name = 'Name is required';
    }
    if (!formData.emergency_contact_relationship.trim()) {
      errors.emergency_contact_relationship = 'Relationship is required';
    } else if (!EMERGENCY_CONTACT_RELATIONSHIP_VALUES.has(formData.emergency_contact_relationship)) {
      errors.emergency_contact_relationship = formData.emergency_contact_relationship === 'parent'
        ? 'Please choose Father or Mother'
        : 'Please select a valid relationship';
    }
    if (!formData.emergency_contact_primary_phone.trim()) {
      errors.emergency_contact_primary_phone = 'Primary phone number is required';
    } else if (!isValidEmergencyPhone(formData.emergency_contact_primary_phone)) {
      errors.emergency_contact_primary_phone = 'Please enter a valid phone number in international format (e.g., +65 9000 0000)';
    }
    if (formData.emergency_contact_secondary_phone?.trim() && !isValidEmergencyPhone(formData.emergency_contact_secondary_phone)) {
      errors.emergency_contact_secondary_phone = 'Please enter a valid phone number in international format (e.g., +65 9000 0000)';
    }
    if (formData.emergency_contact_email?.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.emergency_contact_email)) {
        errors.emergency_contact_email = 'Please enter a valid email address';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const backendData = { ...formData };
    if (!backendData.emergency_contact_secondary_phone?.trim()) {
      delete backendData.emergency_contact_secondary_phone;
    }
    if (!backendData.emergency_contact_email?.trim()) {
      delete backendData.emergency_contact_email;
    }
    try {
      if (editingContact) {
        await updateMutation.mutateAsync({ id: editingContact.id, data: backendData });
      } else {
        await createMutation.mutateAsync(backendData);
      }
      resetForm();
    } catch (error) {
      if (error.response?.status === 400 && typeof error.response.data === 'object' && !error.response.data.detail) {
        const backendErrors = {};
        Object.keys(error.response.data).forEach((key) => {
          const errorMessage = Array.isArray(error.response.data[key])
            ? error.response.data[key][0]
            : error.response.data[key];
          backendErrors[key] = errorMessage;
        });
        setFormErrors(backendErrors);
      }
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      emergency_contact_name: contact.emergency_contact_name || '',
      emergency_contact_relationship: contact.emergency_contact_relationship || '',
      emergency_contact_primary_phone: contact.emergency_contact_primary_phone || '',
      emergency_contact_secondary_phone: contact.emergency_contact_secondary_phone || '',
      emergency_contact_email: contact.emergency_contact_email || '',
    });
    setFormErrors({});
    setIsAdding(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: 'Delete Emergency Contact',
      message: 'Are you sure you want to delete this emergency contact?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting emergency contact:', error);
    }
  };

  const chrome = (
    <>
      <EmergencyContactModal
        isOpen={isAdding}
        onClose={resetForm}
        formData={formData}
        formErrors={formErrors}
        editingContact={editingContact}
        saving={createMutation.isPending || updateMutation.isPending}
        isForeignEmployee={isForeignEmployee}
        onInputChange={handleInputChange}
        onSave={handleSave}
        theme="admin"
      />
      <ConfirmModal
        isOpen={!!confirmDialog}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || 'Confirm'}
        cancelLabel={confirmDialog?.cancelLabel || 'Cancel'}
        variant={confirmDialog?.variant || 'danger'}
        theme="admin"
        onConfirm={() => {
          confirmCallbackRef.current?.(true);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        onCancel={() => {
          confirmCallbackRef.current?.(false);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
      />
    </>
  );

  const body = (
    <>
      {chrome}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" />
          <span className="ml-3 text-gray-500 text-sm">Loading emergency contacts...</span>
        </div>
      )}

      {isError && (
        <div className="text-center py-6 text-sm text-red-600">
          <AlertCircle className="w-5 h-5 mx-auto mb-2" />
          Unable to load emergency contacts.
        </div>
      )}

      {!isLoading && !isError && contacts.length === 0 && (
        <div className="text-center py-8 bg-white rounded-xl border border-dashed border-gray-300">
          <Contact className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-medium text-gray-500">No emergency contacts on file</p>
          <p className="text-xs text-gray-400 mt-1">Add a contact for this employee, especially if they do not use self-service.</p>
        </div>
      )}

      {!isLoading && !isError && contacts.length > 0 && (
        <div className="space-y-3">
          {contacts.map((contact) => (
            <div key={contact.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 mb-2 flex-wrap min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900">{contact.emergency_contact_name || 'Unnamed'}</h4>
                  {contact.emergency_contact_relationship && (
                    <InfoBadge color="blue" label={formatEmergencyContactRelationship(contact.emergency_contact_relationship)} />
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(contact)}
                    className="icon-button-admin"
                    title="Edit contact"
                    aria-label="Edit emergency contact"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(contact.id)}
                    className="icon-button-danger"
                    title="Delete contact"
                    aria-label="Delete emergency contact"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="field-label">Primary Phone</label>
                  <div className="relative">
                    <p className="field-display field-display-sensitive">
                      {mask(contact.emergency_contact_primary_phone, `phone_${contact.id}`)}
                    </p>
                    {contact.emergency_contact_primary_phone && (
                      <SensitiveRevealButton
                        isVisible={!!visibleFields[`phone_${contact.id}`]}
                        onClick={() => toggleVisibility(`phone_${contact.id}`)}
                        theme="admin"
                        titleShow="Show"
                        titleHide="Hide"
                      />
                    )}
                  </div>
                </div>

                {contact.emergency_contact_secondary_phone && (
                  <div>
                    <label className="field-label">Secondary Phone</label>
                    <div className="relative">
                      <p className="field-display field-display-sensitive">
                        {mask(contact.emergency_contact_secondary_phone, `phone2_${contact.id}`)}
                      </p>
                      <SensitiveRevealButton
                        isVisible={!!visibleFields[`phone2_${contact.id}`]}
                        onClick={() => toggleVisibility(`phone2_${contact.id}`)}
                        theme="admin"
                        titleShow="Show"
                        titleHide="Hide"
                      />
                    </div>
                  </div>
                )}

                {contact.emergency_contact_email && (
                  <div>
                    <label className="field-label">Email</label>
                    <div className="relative">
                      <p className="field-display field-display-sensitive">
                        {mask(contact.emergency_contact_email, `email_${contact.id}`, 'email')}
                      </p>
                      <SensitiveRevealButton
                        isVisible={!!visibleFields[`email_${contact.id}`]}
                        onClick={() => toggleVisibility(`email_${contact.id}`)}
                        theme="admin"
                        titleShow="Show"
                        titleHide="Hide"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Contact className="w-5 h-5 text-red-600" />
        Emergency Contacts
      </h3>
      {body}
    </div>
  );
};

// ============================================================================
// EMPLOYEE STATUTORY OBLIGATIONS SECTION
// Parent wrapper: renders CPF and/or Foreign Worker Levy based on pass type
// ============================================================================

const EmployeeCPFSubSection = ({ hrProfile }) => {
  const [cpfRates, setCpfRates] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchCPFRates = async () => {
      if (!hrProfile?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await confidentialAPI.getCPFRates(hrProfile.id);
        setCpfRates(response.data);
      } catch (err) {
        console.error('Error fetching CPF rates:', err);
        setCpfRates({
          employee_rate: '20.0',
          employer_rate: '17.0',
          is_fallback: true,
          message: 'Using default rates'
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCPFRates();
  }, [hrProfile?.id]);

  return (
    <div className="bg-white rounded-lg p-3 border border-indigo-200">
      <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2 flex-wrap">
        CPF Contribution Rates
        {cpfRates?.citizenship_display && (
          <InfoBadge color="indigo" label={cpfRates.citizenship_display} />
        )}
        {cpfRates?.pr_year && (
          <InfoBadge color="indigo" label={`PR Year ${cpfRates.pr_year}`} />
        )}
        {cpfRates?.age && (
          <InfoBadge color="gray" label={`Age: ${cpfRates.age} (${cpfRates.age_band_display})`} />
        )}
      </h4>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="w-5 h-5 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600 text-sm">Loading CPF rates...</span>
        </div>
      )}

      {!loading && cpfRates?.is_fallback && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              {cpfRates.message || 'Using default rates. Actual rates will be calculated during payroll processing.'}
            </p>
          </div>
        </div>
      )}

      {!loading && cpfRates && !cpfRates.is_fallback && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-indigo-50 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-gray-600">Employee:</span>
              <span className="text-lg font-bold text-indigo-700">{cpfRates.employee_rate}%</span>
            </div>
            <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-gray-600">Employer:</span>
              <span className="text-lg font-bold text-green-700">{cpfRates.employer_rate}%</span>
            </div>
            <div className="flex items-center gap-2 bg-purple-50 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-gray-600">Total:</span>
              <span className="text-lg font-bold text-purple-700">
                {(parseFloat(cpfRates.employee_rate) + parseFloat(cpfRates.employer_rate)).toFixed(1)}%
              </span>
            </div>
          </div>

          {cpfRates.calculation_details && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-400">
                Calculation basis: {cpfRates.calculation_details}
                {cpfRates.effective_date && ` (as of ${formatDate(cpfRates.effective_date)})`}
              </p>
            </div>
          )}
        </>
      )}

      {!loading && cpfRates?.is_fallback && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-indigo-50 rounded-lg px-3 py-1.5">
            <span className="text-xs font-medium text-gray-600">Employee:</span>
            <span className="text-lg font-bold text-indigo-700">{cpfRates.employee_rate}%</span>
          </div>
          <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-1.5">
            <span className="text-xs font-medium text-gray-600">Employer:</span>
            <span className="text-lg font-bold text-green-700">{cpfRates.employer_rate}%</span>
          </div>
          <div className="flex items-center gap-2 bg-purple-50 rounded-lg px-3 py-1.5">
            <span className="text-xs font-medium text-gray-600">Total:</span>
            <span className="text-lg font-bold text-purple-700">
              {(parseFloat(cpfRates.employee_rate) + parseFloat(cpfRates.employer_rate)).toFixed(1)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const EmployeeLevySubSection = ({ hrProfile }) => {
  const [levyInfo, setLevyInfo] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchLevyInfo = async () => {
      if (!hrProfile?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await confidentialAPI.getLevyInfo(hrProfile.id);
        setLevyInfo(response.data);
      } catch (err) {
        console.error('Error fetching levy info:', err);
        setLevyInfo({ is_levy_applicable: true, has_active_rate: false, message: 'Failed to load levy information' });
      } finally {
        setLoading(false);
      }
    };
    fetchLevyInfo();
  }, [hrProfile?.id]);

  if (!loading && levyInfo && !levyInfo.is_levy_applicable) {
    return null;
  }

  const formatCurrencySGD = (amount) => {
    if (!amount) return 'Not set';
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amount);
  };

  return (
    <div className="bg-white rounded-lg p-3 border border-orange-200">
      <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2 flex-wrap">
        Foreign Worker Levy
        {levyInfo?.pass_type_display && (
          <InfoBadge color="orange" label={levyInfo.pass_type_display} />
        )}
        {levyInfo?.skill_level_display && (
          <InfoBadge color="blue" label={levyInfo.skill_level_display} />
        )}
      </h4>

      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="w-5 h-5 border-3 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600 text-sm">Loading levy information...</span>
        </div>
      )}

      {!loading && levyInfo?.has_active_rate && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-orange-50 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium text-gray-600">Monthly Rate:</span>
              <span className="text-lg font-bold text-orange-700">
                {formatCurrencySGD(levyInfo.levy_rate_monthly)}
              </span>
            </div>
            {levyInfo.source_country_category_display && (
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                <span className="text-xs font-medium text-gray-600">Source:</span>
                <span className="text-sm font-semibold text-gray-700">{levyInfo.source_country_category_display}</span>
              </div>
            )}
            {levyInfo.industry_sector && (
              <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
                <span className="text-xs font-medium text-gray-600">Sector:</span>
                <span className="text-sm font-semibold text-gray-700 capitalize">{levyInfo.industry_sector}</span>
              </div>
            )}
          </div>

          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-4 flex-wrap">
            {levyInfo.effective_from && (
              <p className="text-xs text-gray-400">Effective from: {formatDate(levyInfo.effective_from)}</p>
            )}
            {levyInfo.mom_reference_number && (
              <p className="text-xs text-gray-400">MOM Ref: {levyInfo.mom_reference_number}</p>
            )}
            {levyInfo.rate_source === 'levy_rate_lookup' && (
              <p className="text-xs text-amber-500 italic">{levyInfo.rate_source_note}</p>
            )}
            <p className="text-xs text-gray-400">Employer-paid — not deducted from salary</p>
          </div>
        </>
      )}

      {!loading && levyInfo && !levyInfo.has_active_rate && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              {levyInfo.message || 'No levy rate configured for this worker. Please set up a rate.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const EmployeeStatutoryObligationsSection = ({ hrProfile }) => {
  const passType = hrProfile?.hrprofile_employment_pass_type || '';
  const showCPF = ['citizen', 'pr'].includes(passType) || !passType;
  const showLevy = ['wp', 's_pass'].includes(passType);
  const showNone = !showCPF && !showLevy;

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Landmark className="w-5 h-5 text-red-600" />
        Statutory Obligations
      </h3>

      <div className="space-y-3">
        {showCPF && <EmployeeCPFSubSection hrProfile={hrProfile} />}
        {showLevy && <EmployeeLevySubSection hrProfile={hrProfile} />}
        {showNone && (
          <div className="bg-white rounded-lg p-4 border border-gray-200 flex items-center gap-3">
            <Shield className="w-5 h-5 text-gray-400" />
            <div>
              <span className="font-medium text-gray-700">No statutory obligations applicable</span>
              <p className="text-xs text-gray-400 mt-0.5">
                CPF contributions and foreign worker levy do not apply to this pass type.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// EMPLOYEE ALLOWANCES SECTION COMPONENT
// ============================================================================
export const EmployeeAllowancesSection = ({ employee, formatCurrency, readOnly = false, theme = 'admin' }) => {
  const [showInlineForm, setShowInlineForm] = React.useState(false);
  const [editingAllowance, setEditingAllowance] = React.useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = React.useState(null);
  const formRef = React.useRef(null);
  
  // Fetch allowances for this employee
  const [allowances, setAllowances] = React.useState([]);
  const [allowancesLoading, setAllowancesLoading] = React.useState(true);
  const [allowancesError, setAllowancesError] = React.useState(null);
  
  // Fetch allowance types for dropdown
  const [allowanceTypes, setAllowanceTypes] = React.useState([]);
  const [typesLoading, setTypesLoading] = React.useState(true);
  
  // Form state
  const [formData, setFormData] = React.useState({
    staff_allowance_type_id: '',
    staff_allowance_amount: '',
    staff_allowance_effective_start: '',
    staff_allowance_effective_end: '',
    staff_allowance_justification: '',
  });
  const [formErrors, setFormErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  
  // Fetch allowances
  const fetchAllowances = React.useCallback(async () => {
    if (!employee?.staff_id) {
      setAllowancesLoading(false);
      return;
    }
    
    setAllowancesLoading(true);
    setAllowancesError(null);
    
    try {
      const response = await confidentialAPI.getStaffAllowances({
        staff_allowance_staff_id: employee.staff_id
      });
      setAllowances(response.data?.results || response.data || []);
    } catch (error) {
      console.error('Error fetching allowances:', error);
      setAllowancesError('Failed to load allowances');
    } finally {
      setAllowancesLoading(false);
    }
  }, [employee?.staff_id]);
  
  // Fetch allowance types
  const fetchAllowanceTypes = React.useCallback(async () => {
    setTypesLoading(true);
    try {
      const response = await configAPI.getAllowanceTypes();
      setAllowanceTypes(response.data?.results || response.data || []);
    } catch (error) {
      console.error('Error fetching allowance types:', error);
    } finally {
      setTypesLoading(false);
    }
  }, []);
  
  React.useEffect(() => {
    fetchAllowances();
    fetchAllowanceTypes();
  }, [fetchAllowances, fetchAllowanceTypes]);
  
  // Reset form
  const resetForm = () => {
    setFormData({
      staff_allowance_type_id: '',
      staff_allowance_amount: '',
      staff_allowance_effective_start: '',
      staff_allowance_effective_end: '',
      staff_allowance_justification: '',
    });
    setFormErrors({});
    setEditingAllowance(null);
  };
  
  // Open inline form for adding
  const handleAddClick = () => {
    resetForm();
    setShowInlineForm(true);
    // Scroll to form after it renders
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };
  
  // Open inline form for editing
  const handleEditClick = (allowance) => {
    setFormData({
      staff_allowance_type_id: allowance.staff_allowance_type_id?.id || allowance.staff_allowance_type_id || '',
      staff_allowance_amount: allowance.staff_allowance_amount || '',
      staff_allowance_effective_start: allowance.staff_allowance_effective_start || '',
      staff_allowance_effective_end: allowance.staff_allowance_effective_end || '',
      staff_allowance_justification: allowance.staff_allowance_justification || '',
    });
    setEditingAllowance(allowance);
    setShowInlineForm(true);
    // Scroll to form after it renders
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };
  
  // Close inline form
  const handleCloseForm = () => {
    setShowInlineForm(false);
    resetForm();
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.staff_allowance_type_id) {
      errors.staff_allowance_type_id = 'Allowance type is required';
    }
    
    if (!formData.staff_allowance_amount || parseFloat(formData.staff_allowance_amount) <= 0) {
      errors.staff_allowance_amount = 'Amount must be greater than 0';
    }
    
    if (!formData.staff_allowance_effective_start) {
      errors.staff_allowance_effective_start = 'Effective start date is required';
    }
    
    // Validate allowance start date is not before employment start date
    if (formData.staff_allowance_effective_start && employee?.employment_start_date) {
      const allowanceStart = new Date(formData.staff_allowance_effective_start);
      const employmentStart = new Date(employee.employment_start_date);
      allowanceStart.setHours(0, 0, 0, 0);
      employmentStart.setHours(0, 0, 0, 0);
      
      if (allowanceStart < employmentStart) {
        errors.staff_allowance_effective_start = `Allowance start date cannot be before employment start date (${formatDate(employee.employment_start_date)})`;
      }
    }
    
    // Validate allowance end date is not after employment end date (if employment has ended)
    if (formData.staff_allowance_effective_end && employee?.employment_end_date) {
      const allowanceEnd = new Date(formData.staff_allowance_effective_end);
      const employmentEnd = new Date(employee.employment_end_date);
      allowanceEnd.setHours(0, 0, 0, 0);
      employmentEnd.setHours(0, 0, 0, 0);
      
      if (allowanceEnd > employmentEnd) {
        errors.staff_allowance_effective_end = `Allowance end date cannot be after employment end date (${formatDate(employee.employment_end_date)})`;
      }
    }
    
    if (formData.staff_allowance_effective_end && formData.staff_allowance_effective_start) {
      if (new Date(formData.staff_allowance_effective_end) < new Date(formData.staff_allowance_effective_start)) {
        errors.staff_allowance_effective_end = 'End date must be after start date';
      }
    }
    
    if (!formData.staff_allowance_justification) {
      errors.staff_allowance_justification = 'Justification is required for audit purposes';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      const payload = {
        staff_allowance_staff_id: employee.staff_id,
        staff_allowance_type_id: parseInt(formData.staff_allowance_type_id),
        staff_allowance_amount: formData.staff_allowance_amount,
        staff_allowance_effective_start: formData.staff_allowance_effective_start,
        staff_allowance_effective_end: formData.staff_allowance_effective_end || null,
        staff_allowance_justification: formData.staff_allowance_justification,
      };
      
      if (editingAllowance) {
        await confidentialAPI.updateStaffAllowance(editingAllowance.id, payload);
        toast.success('Allowance updated successfully');
      } else {
        await confidentialAPI.createStaffAllowance(payload);
        toast.success('Allowance created successfully');
      }
      
      handleCloseForm();
      fetchAllowances();
    } catch (error) {
      console.error('Error saving allowance:', error);
      toast.error(error.response?.data?.detail || 'Failed to save allowance');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Delete allowance
  const handleDelete = async (id) => {
    try {
      await confidentialAPI.deleteStaffAllowance(id);
      toast.success('Allowance deleted successfully');
      setDeleteConfirmId(null);
      fetchAllowances();
    } catch (error) {
      console.error('Error deleting allowance:', error);
      toast.error('Failed to delete allowance');
    }
  };
  
  // Format date for display
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    return formatDate(dateStr);
  };
  
  // Get allowance type name
  const getTypeName = (allowance) => {
    if (allowance.staff_allowance_type_id?.allowance_type_name) {
      return allowance.staff_allowance_type_id.allowance_type_name;
    }
    const type = allowanceTypes.find(t => t.id === allowance.staff_allowance_type_id);
    return type?.allowance_type_name || 'Unknown';
  };
  
  // Get justification options based on allowance type
  const getJustificationOptions = (allowanceTypeId) => {
    if (!allowanceTypeId) return [];
    
    const type = allowanceTypes.find(t => t.id === parseInt(allowanceTypeId));
    if (!type) return [];
    
    const typeName = type.allowance_type_name;
    
    // Map allowance types to their justification options
    const justificationMap = {
      'Car Allowance': [
        'Asset Maintenance'
      ],
      'Mobile Phone Allowance': [
        'Accessibility Requirement',
        'Field/Mobile Role',
        'BYOD Policy Compliance',
        'Data & Connectivity Costs'
      ],
      'Director Fee': [
        'Governance & Oversight',
        'Fiduciary & Legal Risk',
        'Specialized Expertise',
        'Independent Advice'
      ]
    };
    
    return justificationMap[typeName] || [];
  };
  
  // Get default amount for allowance type
  const getDefaultAmount = (allowanceTypeId) => {
    if (!allowanceTypeId) return '';
    
    const type = allowanceTypes.find(t => t.id === parseInt(allowanceTypeId));
    if (!type) return '';
    
    const typeName = type.allowance_type_name;
    
    // Map allowance types to their default amounts
    const amountMap = {
      'Car Allowance': '350',
      'Mobile Phone Allowance': '15',
      'Director Fee': '2000'
    };
    
    return amountMap[typeName] || '';
  };
  
  // Auto-populate amount when allowance type is selected (only if amount is empty)
  React.useEffect(() => {
    if (formData.staff_allowance_type_id && !formData.staff_allowance_amount) {
      const defaultAmount = getDefaultAmount(formData.staff_allowance_type_id);
      if (defaultAmount) {
        setFormData(prev => ({ ...prev, staff_allowance_amount: defaultAmount }));
      }
    }
  }, [formData.staff_allowance_type_id]);
  
  // Clear justification when allowance type changes (if current value is not in new options)
  React.useEffect(() => {
    if (formData.staff_allowance_type_id) {
      const options = getJustificationOptions(formData.staff_allowance_type_id);
      if (formData.staff_allowance_justification && !options.includes(formData.staff_allowance_justification)) {
        setFormData(prev => ({ ...prev, staff_allowance_justification: '' }));
      }
    }
  }, [formData.staff_allowance_type_id]);
  
  // Check if allowance is active
  const isAllowanceActive = (allowance) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(allowance.staff_allowance_effective_start);
    const resolvedEnd = allowance.effective_end_resolved ? new Date(allowance.effective_end_resolved) : null;

    return start <= today && (!resolvedEnd || resolvedEnd >= today);
  };
  
  // Separate active and inactive allowances
  const activeAllowances = allowances.filter(isAllowanceActive);
  const inactiveAllowances = allowances.filter(a => !isAllowanceActive(a));
  
  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <Award className="w-5 h-5 text-red-600" />
          Benefits & Allowances
        </h3>
        {!readOnly && !showInlineForm && (
          <button
            onClick={handleAddClick}
            className="px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <Plus className="w-4 h-4" />
            Add Allowance
          </button>
        )}
      </div>
      
      {/* Inline Add/Edit Form */}
      {!readOnly && showInlineForm && (
        <div 
          ref={formRef}
          className="bg-white rounded-xl border-2 border-purple-300 shadow-sm mb-4 overflow-hidden transition-all duration-300"
        >
          {/* Form Header */}
          <div className="bg-purple-100 px-4 py-3 flex items-center justify-between border-b border-purple-200">
            <h4 className="font-medium text-purple-900 flex items-center gap-2">
              {editingAllowance ? (
                <>
                  <Edit className="w-4 h-4" />
                  Edit Allowance: {getTypeName(editingAllowance)}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add New Allowance
                </>
              )}
            </h4>
            <button
              onClick={handleCloseForm}
              className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-200 rounded-full transition-colors"
              title="Cancel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Form Body */}
          <form onSubmit={handleSubmit} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Allowance Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allowance Type <span className="text-red-500">*</span>
                </label>
                <FormSelect
                  options={[
                    { value: '', label: 'Select type...' },
                    ...allowanceTypes.map(type => ({ value: type.id, label: type.allowance_type_name }))
                  ]}
                  value={formData.staff_allowance_type_id}
                  onChange={(val) => setFormData(prev => ({ ...prev, staff_allowance_type_id: val }))}
                  theme="admin"
                  disabled={typesLoading}
                  className={formErrors.staff_allowance_type_id ? 'border-red-500' : ''}
                />
                {formErrors.staff_allowance_type_id && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.staff_allowance_type_id}</p>
                )}
              </div>
              
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Amount (SGD) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.staff_allowance_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, staff_allowance_amount: e.target.value }))}
                    className={`input-admin w-full pl-7 ${formErrors.staff_allowance_amount ? 'border-red-500' : ''}`}
                    placeholder="0.00"
                  />
                </div>
                {formErrors.staff_allowance_amount && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.staff_allowance_amount}</p>
                )}
              </div>
              
              {/* Effective From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective From <span className="text-red-500">*</span>
                </label>
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(formData.staff_allowance_effective_start)}
                  onChange={(date) => setFormData(prev => ({ ...prev, staff_allowance_effective_start: date ? toLocalISODate(date) : '' }))}
                  minDate={parseLocalDate(employee?.employment_start_date)}
                  maxDate={parseLocalDate(employee?.employment_end_date)}
                  placeholderText="Select date"
                  className={formErrors.staff_allowance_effective_start ? 'border-red-500' : ''}
                />
                {formErrors.staff_allowance_effective_start && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.staff_allowance_effective_start}</p>
                )}
                {employee?.employment_start_date && (
                  <p className="mt-1 text-xs text-gray-500">
                    Earliest date: {formatDate(employee.employment_start_date)}
                  </p>
                )}
              </div>
              
              {/* Effective Until */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective Until <span className="text-gray-400 text-xs font-normal">(optional)</span>
                </label>
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(formData.staff_allowance_effective_end)}
                  onChange={(date) => setFormData(prev => ({ ...prev, staff_allowance_effective_end: date ? toLocalISODate(date) : '' }))}
                  minDate={parseLocalDate(formData.staff_allowance_effective_start || employee?.employment_start_date)}
                  maxDate={parseLocalDate(employee?.employment_end_date)}
                  isClearable
                  placeholderText="Select date"
                  className={formErrors.staff_allowance_effective_end ? 'border-red-500' : ''}
                />
                {formErrors.staff_allowance_effective_end && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.staff_allowance_effective_end}</p>
                )}
                {employee?.employment_end_date && (
                  <p className="mt-1 text-xs text-gray-500">
                    Latest date: {formatDate(employee.employment_end_date)}
                  </p>
                )}
              </div>
              
              {/* Justification - Full Width */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Justification <span className="text-red-500">*</span>
                </label>
                {formData.staff_allowance_type_id ? (
                  <>
                    <FormSelect
                      options={[
                        { value: '', label: 'Select justification...' },
                        ...(() => {
                          const opts = getJustificationOptions(formData.staff_allowance_type_id);
                          const currentValue = formData.staff_allowance_justification;
                          const allOpts = currentValue && !opts.includes(currentValue)
                            ? [...opts, currentValue]
                            : opts;
                          return allOpts.map(opt => ({ value: opt, label: opt }));
                        })()
                      ]}
                      value={formData.staff_allowance_justification}
                      onChange={(val) => setFormData(prev => ({ ...prev, staff_allowance_justification: val }))}
                      theme="admin"
                      className={formErrors.staff_allowance_justification ? 'border-red-500' : ''}
                    />
                    {formErrors.staff_allowance_justification && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.staff_allowance_justification}</p>
                    )}
                  </>
                ) : (
                  <>
                    <FormSelect
                      options={[{ value: '', label: 'Please select an allowance type first' }]}
                      value=""
                      onChange={() => {}}
                      theme="admin"
                      disabled
                    />
                    <p className="mt-1 text-xs text-gray-500">Select an allowance type to see available justifications</p>
                  </>
                )}
              </div>
            </div>
            
            {/* Pro-rating Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mt-4">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> Allowances are automatically pro-rated for partial months based on the employee's work schedule.
              </p>
            </div>
            
            {/* Form Actions */}
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
              <button
                type="button"
                onClick={handleCloseForm}
                className="px-4 py-2 rounded-full text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-full text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 inline-flex items-center gap-2"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  editingAllowance ? 'Update Allowance' : 'Add Allowance'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Loading State */}
      {allowancesLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-gray-600">Loading allowances...</span>
        </div>
      )}
      
      {/* Error State */}
      {allowancesError && !allowancesLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
          <p className="text-red-700">{allowancesError}</p>
          <button
            onClick={fetchAllowances}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      )}
      
      {/* Empty State */}
      {!allowancesLoading && !allowancesError && allowances.length === 0 && !showInlineForm && (
        <div className="bg-white rounded-lg p-6 border text-center">
          <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-3">
            {readOnly ? 'No allowances assigned' : 'No allowances assigned to this employee'}
          </p>
          {!readOnly && (
            <button
              onClick={handleAddClick}
              className="text-sm text-red-600 hover:text-red-800 font-medium"
            >
              + Add first allowance
            </button>
          )}
        </div>
      )}
      
      {/* Allowances List */}
      {!allowancesLoading && !allowancesError && allowances.length > 0 && (
        <div className="space-y-4">
          {/* Active Allowances */}
          {activeAllowances.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <CheckCircle className={`w-4 h-4 ${theme === 'admin' ? 'text-red-500' : 'text-blue-500'}`} />
                Active Allowances
                <CountBadge count={activeAllowances.length} theme={theme === 'admin' ? 'admin' : 'employee'} size="md" max={9999} />
              </h4>
              <div className="space-y-2">
                {activeAllowances.map((allowance) => (
                  <div
                    key={allowance.id}
                    className="bg-white rounded-lg p-4 border border-green-200 flex items-start justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{getTypeName(allowance)}</span>
                        {allowance.staff_allowance_approved_by_staff_id ? (
                          <StatusBadge status="approved" showIcon />
                        ) : (
                          <StatusBadge status="pending" showIcon />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-gray-500">
                        <span>{formatDateDisplay(allowance.staff_allowance_effective_start)} → {allowance.effective_end_resolved ? formatDateDisplay(allowance.effective_end_resolved) : 'Ongoing'}</span>
                        {allowance.is_capped_by_employment_end && (
                          <span title="End date derived from employment end date">
                            <InfoBadge color="orange" label="Employment End" />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className="text-lg font-semibold text-gray-900 font-mono">
                        {formatCurrency(allowance.staff_allowance_amount)}/mo
                      </span>
                      {!readOnly && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEditClick(allowance)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                            title="Edit allowance"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(allowance.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                            title="Delete allowance"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Inactive/Expired Allowances */}
          {inactiveAllowances.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                <Clock className={`w-4 h-4 ${theme === 'admin' ? 'text-red-400' : 'text-blue-400'}`} />
                Past/Future Allowances
                <CountBadge count={inactiveAllowances.length} theme={theme === 'admin' ? 'admin' : 'employee'} size="md" max={9999} />
              </h4>
              <div className="space-y-2">
                {inactiveAllowances.map((allowance) => (
                  <div
                    key={allowance.id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex items-start justify-between gap-3 opacity-75"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-700">{getTypeName(allowance)}</span>
                        {new Date(allowance.staff_allowance_effective_start) > new Date() ? (
                          <InfoBadge color="blue" label="Future" />
                        ) : (
                          <StatusBadge status="expired" />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-gray-500">
                        <span>{formatDateDisplay(allowance.staff_allowance_effective_start)} → {allowance.effective_end_resolved ? formatDateDisplay(allowance.effective_end_resolved) : 'Ongoing'}</span>
                        {allowance.is_capped_by_employment_end && (
                          <span title="End date derived from employment end date">
                            <InfoBadge color="orange" label="Employment End" />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className="text-lg font-semibold text-gray-600 font-mono">
                        {formatCurrency(allowance.staff_allowance_amount)}/mo
                      </span>
                      {!readOnly && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEditClick(allowance)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                            title="Edit allowance"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(allowance.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                            title="Delete allowance"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Delete Confirmation Modal - z-[10000] to appear above employee profile modal (z-9999) */}
      {deleteConfirmId && (
        <Modal
          isOpen
          onClose={() => setDeleteConfirmId(null)}
          size="sm"
          theme="admin"
          className="max-w-md"
          customHeader={(
            <div className="modal-header">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="modal-title">Delete Allowance</h3>
              </div>
            </div>
          )}
          footer={(
            <>
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="btn btn-admin-outline"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                className="btn btn-danger"
              >
                Delete
              </button>
            </>
          )}
        >
          <p className="text-gray-600">
            Are you sure you want to delete this allowance? This action cannot be undone.
          </p>
        </Modal>
      )}
      
    </div>
  );
};

const DEDUCTION_TYPE_OPTIONS = [
  { value: 'SEASON_PARKING', label: 'Season Parking' },
  { value: 'MOBILE_PHONE_EXCESS', label: 'Mobile Phone Excess' },
];

const DEDUCTION_FREQUENCY_OPTIONS = [
  { value: 'RECURRING', label: 'Recurring' },
  { value: 'AD_HOC', label: 'Ad-Hoc' },
];

const DEDUCTION_TYPE_ICONS = {
  SEASON_PARKING: Car,
  MOBILE_PHONE_EXCESS: Smartphone,
};

const DEDUCTION_STATUS_MAP = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

export const EmployeeDeductionsSection = ({ employee, formatCurrency, readOnly = false, theme = 'admin' }) => {
  const [showInlineForm, setShowInlineForm] = React.useState(false);
  const [editingDeduction, setEditingDeduction] = React.useState(null);
  const [confirmAction, setConfirmAction] = React.useState(null);
  const formRef = React.useRef(null);

  const [deductions, setDeductions] = React.useState([]);
  const [deductionsLoading, setDeductionsLoading] = React.useState(true);
  const [deductionsError, setDeductionsError] = React.useState(null);

  const [formData, setFormData] = React.useState({
    deduction_type: '',
    frequency: '',
    amount: '',
    effective_start_date: '',
    effective_end_date: '',
    remarks: '',
  });
  const [formErrors, setFormErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);

  const fetchDeductions = React.useCallback(async () => {
    if (!employee?.staff_id) {
      setDeductionsLoading(false);
      return;
    }

    setDeductionsLoading(true);
    setDeductionsError(null);

    try {
      const response = await confidentialAPI.getPayrollDeductions({
        employee_staff_id: employee.staff_id,
      });
      setDeductions(response.data?.results || response.data || []);
    } catch (error) {
      console.error('Error fetching deductions:', error);
      setDeductionsError('Failed to load deductions');
    } finally {
      setDeductionsLoading(false);
    }
  }, [employee?.staff_id]);

  React.useEffect(() => {
    fetchDeductions();
  }, [fetchDeductions]);

  const resetForm = () => {
    setFormData({
      deduction_type: '',
      frequency: '',
      amount: '',
      effective_start_date: '',
      effective_end_date: '',
      remarks: '',
    });
    setFormErrors({});
    setEditingDeduction(null);
  };

  const handleAddClick = () => {
    resetForm();
    setShowInlineForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleEditClick = (deduction) => {
    setFormData({
      deduction_type: deduction.deduction_type || '',
      frequency: deduction.frequency || '',
      amount: deduction.amount || '',
      effective_start_date: deduction.effective_start_date || '',
      effective_end_date: deduction.effective_end_date || '',
      remarks: deduction.remarks || '',
    });
    setEditingDeduction(deduction);
    setShowInlineForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleCloseForm = () => {
    setShowInlineForm(false);
    resetForm();
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.deduction_type) {
      errors.deduction_type = 'Deduction type is required';
    }

    if (!formData.frequency) {
      errors.frequency = 'Frequency is required';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Amount must be greater than 0';
    }

    if (!formData.effective_start_date) {
      errors.effective_start_date = 'Start date is required';
    }

    if (formData.effective_start_date && employee?.employment_start_date) {
      const dedStart = new Date(formData.effective_start_date);
      const empStart = new Date(employee.employment_start_date);
      dedStart.setHours(0, 0, 0, 0);
      empStart.setHours(0, 0, 0, 0);
      if (dedStart < empStart) {
        errors.effective_start_date = `Start date cannot be before employment start date (${formatDate(employee.employment_start_date)})`;
      }
    }

    if (formData.frequency === 'AD_HOC' && !formData.effective_end_date) {
      errors.effective_end_date = 'End date is required for ad-hoc deductions';
    }

    if (formData.effective_end_date && formData.effective_start_date) {
      if (new Date(formData.effective_end_date) < new Date(formData.effective_start_date)) {
        errors.effective_end_date = 'End date must be after start date';
      }
    }

    if (formData.effective_end_date && employee?.employment_end_date) {
      const dedEnd = new Date(formData.effective_end_date);
      const empEnd = new Date(employee.employment_end_date);
      dedEnd.setHours(0, 0, 0, 0);
      empEnd.setHours(0, 0, 0, 0);
      if (dedEnd > empEnd) {
        errors.effective_end_date = `End date cannot be after employment end date (${formatDate(employee.employment_end_date)})`;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        employee_staff_id: employee.staff_id,
        deduction_type: formData.deduction_type,
        frequency: formData.frequency,
        amount: parseFloat(formData.amount).toFixed(2),
        effective_start_date: formData.effective_start_date,
        effective_end_date: formData.effective_end_date || null,
        remarks: formData.remarks,
      };

      if (editingDeduction) {
        await confidentialAPI.updatePayrollDeduction(editingDeduction.id, payload);
        toast.success('Deduction updated successfully');
      } else {
        await confidentialAPI.createPayrollDeduction(payload);
        toast.success('Deduction created successfully');
      }

      handleCloseForm();
      fetchDeductions();
    } catch (error) {
      console.error('Error saving deduction:', error);
      const detail = error.response?.data;
      if (detail && typeof detail === 'object') {
        const fieldErrors = {};
        Object.entries(detail).forEach(([key, val]) => {
          fieldErrors[key] = Array.isArray(val) ? val.join(' ') : String(val);
        });
        setFormErrors(fieldErrors);
      } else {
        toast.error('Failed to save deduction');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (deduction, newStatus) => {
    try {
      await confidentialAPI.updatePayrollDeduction(deduction.id, { status: newStatus });
      toast.success(`Deduction ${newStatus.toLowerCase()}`);
      fetchDeductions();
    } catch {
      toast.error('Failed to update deduction status');
    } finally {
      setConfirmAction(null);
    }
  };

  const getTypeName = (deduction) => {
    const opt = DEDUCTION_TYPE_OPTIONS.find(o => o.value === deduction.deduction_type);
    return opt?.label || deduction.deduction_type_display || deduction.deduction_type || 'Unknown';
  };

  const isDeductionActive = (deduction) => {
    if (deduction.status !== 'ACTIVE') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(deduction.effective_start_date);
    const resolvedEnd = deduction.effective_end_resolved ? new Date(deduction.effective_end_resolved) : null;
    return start <= today && (!resolvedEnd || resolvedEnd >= today);
  };

  const isDeductionPausedOrPending = (deduction) => {
    if (deduction.status === 'PAUSED') return true;
    if (deduction.status === 'ACTIVE') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = new Date(deduction.effective_start_date);
      return start > today;
    }
    return false;
  };

  const activeDeductions = deductions.filter(d => isDeductionActive(d) || isDeductionPausedOrPending(d));
  const inactiveDeductions = deductions.filter(d => !isDeductionActive(d) && !isDeductionPausedOrPending(d));

  const isAdmin = theme === 'admin';
  const themeAccent = isAdmin ? 'red' : 'blue';

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <MinusCircle className="w-5 h-5 text-red-600" />
          Fixed Deductions
        </h3>
        {!readOnly && !showInlineForm && (
          <button
            onClick={handleAddClick}
            className={`btn ${isAdmin ? 'btn-admin-primary' : 'btn-employee-primary'}`}
          >
            <Plus className="w-4 h-4" />
            Add Deduction
          </button>
        )}
      </div>

      {/* Inline Add/Edit Form */}
      {!readOnly && showInlineForm && (
        <div
          ref={formRef}
          className="bg-white rounded-xl border-2 border-amber-300 shadow-sm mb-4 overflow-hidden transition-all duration-300"
        >
          <div className="bg-amber-100 px-4 py-3 flex items-center justify-between border-b border-amber-200">
            <h4 className="font-medium text-amber-900 flex items-center gap-2">
              {editingDeduction ? (
                <>
                  <Edit className="w-4 h-4" />
                  Edit Deduction: {getTypeName(editingDeduction)}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add New Deduction
                </>
              )}
            </h4>
            <button
              onClick={handleCloseForm}
              className="p-1 text-amber-600 hover:text-amber-800 hover:bg-amber-200 rounded-full transition-colors"
              title="Cancel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Deduction Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deduction Type <span className="text-red-500">*</span>
                </label>
                <FormSelect
                  options={[
                    { value: '', label: 'Select type...' },
                    ...DEDUCTION_TYPE_OPTIONS,
                  ]}
                  value={formData.deduction_type}
                  onChange={(val) => setFormData(prev => ({ ...prev, deduction_type: val }))}
                  theme="admin"
                  className={formErrors.deduction_type ? 'border-red-500' : ''}
                />
                {formErrors.deduction_type && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.deduction_type}</p>
                )}
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Frequency <span className="text-red-500">*</span>
                </label>
                <FormSelect
                  options={[
                    { value: '', label: 'Select frequency...' },
                    ...DEDUCTION_FREQUENCY_OPTIONS,
                  ]}
                  value={formData.frequency}
                  onChange={(val) => setFormData(prev => ({ ...prev, frequency: val }))}
                  theme="admin"
                  className={formErrors.frequency ? 'border-red-500' : ''}
                />
                {formErrors.frequency && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.frequency}</p>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (SGD) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    className={`input-admin w-full pl-7 ${formErrors.amount ? 'border-red-500' : ''}`}
                    placeholder="0.00"
                  />
                </div>
                {formErrors.amount && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.amount}</p>
                )}
              </div>

              {/* Placeholder for grid alignment */}
              <div className="hidden md:block" />

              {/* Effective From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective From <span className="text-red-500">*</span>
                </label>
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(formData.effective_start_date)}
                  onChange={(date) => setFormData(prev => ({ ...prev, effective_start_date: date ? toLocalISODate(date) : '' }))}
                  minDate={parseLocalDate(employee?.employment_start_date)}
                  maxDate={parseLocalDate(employee?.employment_end_date)}
                  placeholderText="Select date"
                  className={formErrors.effective_start_date ? 'border-red-500' : ''}
                />
                {formErrors.effective_start_date && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.effective_start_date}</p>
                )}
                {employee?.employment_start_date && (
                  <p className="mt-1 text-xs text-gray-500">
                    Earliest date: {formatDate(employee.employment_start_date)}
                  </p>
                )}
              </div>

              {/* Effective Until */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective Until
                  {formData.frequency === 'AD_HOC' ? (
                    <span className="text-red-500 ml-1">*</span>
                  ) : (
                    <span className="text-gray-400 text-xs font-normal ml-1">(optional)</span>
                  )}
                </label>
                <DatePickerField
                  theme="admin"
                  selected={parseLocalDate(formData.effective_end_date)}
                  onChange={(date) => setFormData(prev => ({ ...prev, effective_end_date: date ? toLocalISODate(date) : '' }))}
                  minDate={parseLocalDate(formData.effective_start_date || employee?.employment_start_date)}
                  maxDate={parseLocalDate(employee?.employment_end_date)}
                  isClearable
                  placeholderText={formData.frequency === 'RECURRING' ? 'Leave blank for ongoing' : 'Select date'}
                  className={formErrors.effective_end_date ? 'border-red-500' : ''}
                />
                {formErrors.effective_end_date && (
                  <p className="mt-1 text-xs text-red-500">{formErrors.effective_end_date}</p>
                )}
              </div>

              {/* Remarks - Full Width */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks <span className="text-gray-400 text-xs font-normal">(optional)</span>
                </label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  className="textarea-admin w-full"
                  placeholder="e.g. Season parking at HQ basement B2"
                  rows={2}
                />
              </div>
            </div>

            {/* Contextual hints */}
            {formData.frequency === 'AD_HOC' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mt-4">
                <p className="text-xs text-orange-700">
                  <strong>Note:</strong> Ad-hoc deductions require an end date. Set it to the last day of the target payroll month.
                </p>
              </div>
            )}

            {formData.frequency === 'RECURRING' && !formData.effective_end_date && formData.effective_start_date && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mt-4">
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> This recurring deduction will auto-apply to every payroll run from {formatDate(formData.effective_start_date)} onwards until paused or cancelled.
                </p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
              <button
                type="button"
                onClick={handleCloseForm}
                className="btn btn-admin-outline"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-admin-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  editingDeduction ? 'Update Deduction' : 'Add Deduction'
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Loading State */}
      {deductionsLoading && (
        <div className="flex items-center justify-center py-8">
          <div className={`w-6 h-6 border-4 border-amber-600 border-t-transparent rounded-full animate-spin`}></div>
          <span className="ml-3 text-gray-600">Loading deductions...</span>
        </div>
      )}

      {/* Error State */}
      {deductionsError && !deductionsLoading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <AlertCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
          <p className="text-red-700">{deductionsError}</p>
          <button onClick={fetchDeductions} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
            Try again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!deductionsLoading && !deductionsError && deductions.length === 0 && !showInlineForm && (
        <div className="bg-white rounded-lg p-6 border text-center">
          <MinusCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-3">
            {readOnly ? 'No deductions assigned' : 'No deductions assigned to this employee'}
          </p>
          {!readOnly && (
            <button onClick={handleAddClick} className={`text-sm text-${themeAccent}-600 hover:text-${themeAccent}-800 font-medium`}>
              + Add first deduction
            </button>
          )}
        </div>
      )}

      {/* Deductions List */}
      {!deductionsLoading && !deductionsError && deductions.length > 0 && (
        <div className="space-y-4">
          {/* Active / Paused Deductions */}
          {activeDeductions.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <CheckCircle className={`w-4 h-4 ${isAdmin ? 'text-red-500' : 'text-blue-500'}`} />
                Active Deductions
                <CountBadge count={activeDeductions.length} theme={isAdmin ? 'admin' : 'employee'} size="md" max={9999} />
              </h4>
              <div className="space-y-2">
                {activeDeductions.map((deduction) => {
                  const TypeIcon = DEDUCTION_TYPE_ICONS[deduction.deduction_type] || MinusCircle;
                  const isFuture = new Date(deduction.effective_start_date) > new Date();
                  return (
                    <div
                      key={deduction.id}
                      className={`bg-white rounded-lg p-4 border ${deduction.status === 'PAUSED' ? 'border-orange-200' : 'border-green-200'} flex items-start justify-between gap-3`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <TypeIcon className="w-4 h-4 text-gray-500" />
                          <span className="font-medium text-gray-900">{getTypeName(deduction)}</span>
                          <StatusBadge status={DEDUCTION_STATUS_MAP[deduction.status] || deduction.status} showIcon />
                          {isFuture && <InfoBadge color="blue" label="Future" />}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            deduction.frequency === 'RECURRING'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {deduction.frequency === 'RECURRING' ? 'Recurring' : 'Ad-Hoc'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-500">
                          <span>{formatDate(deduction.effective_start_date)} → {deduction.effective_end_resolved ? formatDate(deduction.effective_end_resolved) : 'Ongoing'}</span>
                          {deduction.is_capped_by_employment_end && (
                            <span title="End date derived from employment end date">
                              <InfoBadge color="orange" label="Employment End" />
                            </span>
                          )}
                        </div>
                        {deduction.remarks && (
                          <div className="text-xs text-gray-400 mt-1">{deduction.remarks}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <span className="text-lg font-semibold text-gray-900 font-mono">
                          {formatCurrency(deduction.amount)}/mo
                        </span>
                        {!readOnly && (
                          <div className="flex items-center gap-1">
                            {deduction.status === 'ACTIVE' && (
                              <button
                                onClick={() => setConfirmAction({ type: 'pause', deduction })}
                                className={`p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-${themeAccent}-500 focus-visible:ring-offset-1`}
                                title="Pause deduction"
                              >
                                <Pause className="w-4 h-4" />
                              </button>
                            )}
                            {deduction.status === 'PAUSED' && (
                              <button
                                onClick={() => setConfirmAction({ type: 'resume', deduction })}
                                className={`p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-${themeAccent}-500 focus-visible:ring-offset-1`}
                                title="Resume deduction"
                              >
                                <Play className="w-4 h-4" />
                              </button>
                            )}
                            {(deduction.status === 'ACTIVE' || deduction.status === 'PAUSED') && (
                              <>
                                <button
                                  onClick={() => handleEditClick(deduction)}
                                  className={`p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-${themeAccent}-500 focus-visible:ring-offset-1`}
                                  title="Edit deduction"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setConfirmAction({ type: 'cancel', deduction })}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                                  title="Cancel deduction"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Inactive / Completed / Cancelled Deductions */}
          {inactiveDeductions.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                <Clock className={`w-4 h-4 ${isAdmin ? 'text-red-400' : 'text-blue-400'}`} />
                Past / Cancelled Deductions
                <CountBadge count={inactiveDeductions.length} theme={isAdmin ? 'admin' : 'employee'} size="md" max={9999} />
              </h4>
              <div className="space-y-2">
                {inactiveDeductions.map((deduction) => {
                  const TypeIcon = DEDUCTION_TYPE_ICONS[deduction.deduction_type] || MinusCircle;
                  return (
                    <div
                      key={deduction.id}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex items-start justify-between gap-3 opacity-75"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <TypeIcon className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-700">{getTypeName(deduction)}</span>
                          <StatusBadge status={DEDUCTION_STATUS_MAP[deduction.status] || deduction.status} />
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-500">
                          <span>{formatDate(deduction.effective_start_date)} → {deduction.effective_end_resolved ? formatDate(deduction.effective_end_resolved) : 'Ongoing'}</span>
                          {deduction.is_capped_by_employment_end && (
                            <span title="End date derived from employment end date">
                              <InfoBadge color="orange" label="Employment End" />
                            </span>
                          )}
                        </div>
                        {deduction.remarks && (
                          <div className="text-xs text-gray-400 mt-1">{deduction.remarks}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <span className="text-lg font-semibold text-gray-600 font-mono">
                          {formatCurrency(deduction.amount)}/mo
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm Action Modals */}
      {confirmAction?.type === 'pause' && (
        <Modal
          isOpen
          onClose={() => setConfirmAction(null)}
          size="sm"
          theme="admin"
          className="max-w-md"
          customHeader={(
            <div className="modal-header">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Pause className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="modal-title">Pause Deduction</h3>
              </div>
            </div>
          )}
          footer={(
            <>
              <button onClick={() => setConfirmAction(null)} className="btn btn-admin-outline">Keep Active</button>
              <button onClick={() => handleStatusChange(confirmAction.deduction, 'PAUSED')} className="btn btn-admin-primary">Pause</button>
            </>
          )}
        >
          <p className="text-gray-600">
            Pause the <strong>{getTypeName(confirmAction.deduction)}</strong> deduction
            ({formatCurrency(confirmAction.deduction.amount)}/mo)? It will not be applied in future payroll runs until resumed.
          </p>
        </Modal>
      )}

      {confirmAction?.type === 'resume' && (
        <Modal
          isOpen
          onClose={() => setConfirmAction(null)}
          size="sm"
          theme="admin"
          className="max-w-md"
          customHeader={(
            <div className="modal-header">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Play className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="modal-title">Resume Deduction</h3>
              </div>
            </div>
          )}
          footer={(
            <>
              <button onClick={() => setConfirmAction(null)} className="btn btn-admin-outline">Cancel</button>
              <button onClick={() => handleStatusChange(confirmAction.deduction, 'ACTIVE')} className="btn btn-success">Resume</button>
            </>
          )}
        >
          <p className="text-gray-600">
            Resume the <strong>{getTypeName(confirmAction.deduction)}</strong> deduction
            ({formatCurrency(confirmAction.deduction.amount)}/mo)? It will be applied in the next payroll run.
          </p>
        </Modal>
      )}

      {confirmAction?.type === 'cancel' && (
        <Modal
          isOpen
          onClose={() => setConfirmAction(null)}
          size="sm"
          theme="admin"
          className="max-w-md"
          customHeader={(
            <div className="modal-header">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="modal-title">Cancel Deduction</h3>
              </div>
            </div>
          )}
          footer={(
            <>
              <button onClick={() => setConfirmAction(null)} className="btn btn-admin-outline">Keep</button>
              <button onClick={() => handleStatusChange(confirmAction.deduction, 'CANCELLED')} className="btn btn-danger">Cancel Deduction</button>
            </>
          )}
        >
          <p className="text-gray-600">
            Permanently cancel the <strong>{getTypeName(confirmAction.deduction)}</strong> deduction
            ({formatCurrency(confirmAction.deduction.amount)}/mo)? This cannot be undone.
          </p>
        </Modal>
      )}
    </div>
  );
};

const EmployeeCompensationBenefitsTab = ({ employee, hrProfile, onRecordSalaryChange }) => {
  const [sensitiveFieldsVisible, setSensitiveFieldsVisible] = React.useState({});
  const [salaryHistoryVisible, setSalaryHistoryVisible] = React.useState(false);
  
  // Fetch current active salary separately
  const { data: currentSalary, isLoading: salaryLoading } = useCurrentSalary(employee?.staff_id);
  
  // Fetch calculated rates (overtime, rest day rates)
  const currentYear = new Date().getFullYear();
  const { data: calculatedRates, isLoading: ratesLoading } = useCalculatedRates(employee?.staff_id, currentYear);

  // Salary history from employment movements (salary_change + movements that carried a new salary).
  const { data: salaryMovements = [], isLoading: salaryHistoryLoading } = useEmployeeMovements(employee?.staff_id);
  const salaryHistory = React.useMemo(() => {
    return (salaryMovements || [])
      .filter((m) => (
        m.change_type === 'salary_change'
        || m.to_basic_salary != null
        || m.from_basic_salary != null
      ))
      .slice()
      .sort((a, b) => String(b.effective_from || '').localeCompare(String(a.effective_from || '')));
  }, [salaryMovements]);
  
  // Determine if OT eligible - handle both boolean and string values, default to false if undefined
  const isOtEligible = calculatedRates?.is_ot_eligible === true || calculatedRates?.is_ot_eligible === 'true';
  
  // Compose hrProfile with salary data (salary fields take precedence)
  const hrProfileWithSalary = React.useMemo(() => {
    if (!hrProfile && !currentSalary) return null;
    
    return {
      ...hrProfile,
      ...(currentSalary || {}), // Salary fields override HRProfile fields
      // Payment details: always read from HRProfile — it is the source of truth written by the
      // bank-statement approval flow. EmployeeSalary payroll_bank_* fields may contain stale
      // or seeded data and must not override HR-approved values.
      hrprofile_bank_name: hrProfile?.hrprofile_bank_name,
      hrprofile_bank_account_number: hrProfile?.hrprofile_bank_account_number,
      hrprofile_branch_code: hrProfile?.hrprofile_branch_code,
      hrprofile_pay_mode: hrProfile?.hrprofile_pay_mode,
    };
  }, [hrProfile, currentSalary]);
  
  const toggleFieldVisibility = (fieldName) => {
    setSensitiveFieldsVisible(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };
  
  const formatCurrency = (amount) => {
    if (!amount) return 'Not set';
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount);
  };

  // Rate Card Component with Masking Support
  const RateCard = ({ 
    label, 
    value, 
    fieldName, 
    color = 'text-gray-900',
    formula,
    additionalInfo,
    unit = ''
  }) => {
    const isVisible = sensitiveFieldsVisible[fieldName];
    const displayValue = value ? parseFloat(value).toFixed(2) : null;
    const maskedValue = maskValue('_', 'currency');
    
    return (
      <div className="bg-white rounded-lg p-4 border border-gray-200 relative">
        <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
        <div className="relative">
          <p className={`text-xl font-bold font-mono ${color} ${displayValue ? 'pr-10' : ''}`}>
            {displayValue ? (
              isVisible ? (
                unit ? `$${displayValue}${unit}` : `$${displayValue}`
              ) : (
                maskedValue
              )
            ) : 'N/A'}
          </p>
          {displayValue && (
            <SensitiveRevealButton
              isVisible={!!isVisible}
              onClick={() => toggleFieldVisibility(fieldName)}
              theme="admin"
              positionClass="absolute right-0 top-0"
            />
          )}
        </div>
        {/* Formula and additional info - only show when visible */}
        {isVisible && formula && (
          <p className="text-xs text-gray-500 mt-1">{formula}</p>
        )}
        {isVisible && additionalInfo && (
          <p className="text-xs text-gray-500 mt-1">{additionalInfo}</p>
        )}
        {!isVisible && displayValue && (
          <p className="text-xs text-gray-400 mt-1 italic">Formula hidden</p>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <DollarSign className="w-6 h-6 text-red-600 shrink-0" />
          <h2 className="text-lg font-semibold text-gray-900">Compensation & Benefits</h2>
        </div>
        {typeof onRecordSalaryChange === 'function' && (
          <button
            type="button"
            onClick={onRecordSalaryChange}
            className="btn btn-sm btn-admin-primary shrink-0"
            title="Opens Record Employment Event on the Employment tab, preset to Salary Change"
          >
            <ArrowRightLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Record salary change</span>
            <span className="sm:hidden">Salary change</span>
          </button>
        )}
      </div>

      {/* Salary & Compensation Section */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-red-600" />
          Salary & Compensation
        </h3>
        
        {/* Security hint — muted helper; masking + eye icon carry the primary signal */}
        <p className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
          <Shield className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span>Masked by default · Use the eye icon to reveal · Access is audited</span>
        </p>

        {salaryLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">Loading salary information...</span>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <SharedSensitiveField
            label="Basic Salary (Monthly)"
            value={hrProfileWithSalary?.payroll_basic_salary}
            fieldName="basic_salary"
            type="currency"
          />
          <div>
            <label className="field-label">Pay Type</label>
            <p className="field-display capitalize">
                {hrProfileWithSalary?.payroll_pay_type?.replace('_', ' ') || 'Not specified'}
            </p>
          </div>
          <div>
            <label className="field-label">Pay Frequency</label>
            <p className="field-display capitalize">
                {hrProfileWithSalary?.payroll_pay_frequency?.replace('_', ' ') || 'Not specified'}
            </p>
          </div>
        </div>
        )}

        {/* Salary history from employment movements */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h4 className="text-sm font-semibold text-gray-900">Salary History</h4>
            {salaryHistory.length > 0 && (
              <button
                type="button"
                onClick={() => setSalaryHistoryVisible((v) => !v)}
                className="icon-button-admin icon-button-sm"
                title={salaryHistoryVisible ? 'Hide salary amounts' : 'Show salary amounts'}
                aria-label={salaryHistoryVisible ? 'Hide salary amounts' : 'Show salary amounts'}
              >
                {salaryHistoryVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
          {salaryHistoryLoading ? (
            <p className="text-xs text-gray-500">Loading salary history…</p>
          ) : salaryHistory.length === 0 ? (
            <p className="text-xs text-gray-500">No salary changes recorded yet. Use Record salary change to capture an effective-dated raise.</p>
          ) : (
            <ul className="space-y-2">
              {salaryHistory.map((m) => {
                const amount = m.to_basic_salary ?? m.from_basic_salary;
                return (
                  <li key={m.id || `${m.effective_from}-${amount}`} className="flex items-center justify-between gap-3 text-sm bg-white border border-gray-200 rounded-xl px-3 py-2">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{m.effective_from ? formatDate(m.effective_from) : '—'}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {m.change_type_display || m.change_type?.replace(/_/g, ' ') || 'Salary change'}
                        {m.change_reason ? ` · ${m.change_reason}` : ''}
                      </p>
                    </div>
                    <p className="font-mono text-gray-900 shrink-0">
                      {amount != null && amount !== ''
                        ? (salaryHistoryVisible
                          ? `$${parseFloat(amount).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : maskValue('_', 'currency'))
                        : '—'}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Calculated Rates Section */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Clock className="w-5 h-5 text-red-600" />
          Calculated Rates ({currentYear})
          {calculatedRates?.work_week_type && (
            <span className="ml-2"><InfoBadge color="amber" label={calculatedRates.work_week_type === '5_day' ? '5-Day Week' : calculatedRates.work_week_type === '5_half_day' ? '5.5-Day Week' : calculatedRates.work_week_type === '6_day' ? '6-Day Week' : calculatedRates.work_week_type} /></span>
          )}
          {calculatedRates?.pay_type && (
            <span className="ml-1"><InfoBadge color="blue" label={calculatedRates.pay_type?.charAt(0).toUpperCase() + calculatedRates.pay_type?.slice(1)} /></span>
          )}
        </h3>
        
        {/* Context hint — muted helper; rate cards + formula expander carry the detail */}
        <p className="text-xs text-gray-500 mb-4">
          Auto-calculated from salary and work schedule
          {!ratesLoading && calculatedRates && (
            isOtEligible
              ? ' · Used for OT, rest day, and public holiday pay'
              : ' · Daily rate for proration · Non-workman · OIL instead of OT, rest day, or PH pay'
          )}
        </p>

        {ratesLoading ? (
          <div className="flex items-center justify-center py-6">
            <div className="w-5 h-5 border-3 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-2 text-gray-600 text-sm">Calculating rates...</span>
          </div>
        ) : calculatedRates ? (
          <>
            <div className={`grid grid-cols-1 md:grid-cols-2 ${isOtEligible ? 'lg:grid-cols-4' : 'lg:grid-cols-1'} gap-4`}>
              {/* Daily Rate - Always shown */}
              <RateCard
                label="Daily Rate"
                value={calculatedRates.daily_rate}
                fieldName="daily_rate"
                color="text-gray-900"
                formula={
                  calculatedRates.pay_type === 'hourly' 
                    ? 'Hourly × 8 hrs'
                    : calculatedRates.daily_rate_month
                      ? `Monthly Salary / Working Days (${calculatedRates.daily_rate_month})`
                      : 'Monthly Salary / Working Days'
                }
                additionalInfo={
                  calculatedRates.daily_rate_month ? `For ${calculatedRates.daily_rate_month}` : 
                  !isOtEligible ? 'Used for proration & deductions' : null
                }
              />
              
              {/* Overtime Hourly Rate - Only for OT-eligible employees */}
              {isOtEligible && (
                <RateCard
                  label="Overtime Hourly Rate"
                  value={calculatedRates.overtime_hourly_rate}
                  fieldName="overtime_hourly_rate"
                  color="text-green-600"
                  formula={
                    calculatedRates.pay_type === 'hourly' 
                      ? 'Hourly × 1.5'
                      : '(Salary × 18) ÷ 2288'
                  }
                  unit="/hr"
                />
              )}
              
              {/* Rest Day Daily Rate - Only for OT-eligible employees */}
              {isOtEligible && (
                <RateCard
                  label="Rest Day Daily Rate"
                  value={calculatedRates.rest_day_daily_rate}
                  fieldName="rest_day_daily_rate"
                  color="text-blue-600"
                  formula={
                    calculatedRates.pay_type === 'hourly'
                      ? 'Hourly × 8 × 1.5'
                      : `(Salary × 18) ÷ ${calculatedRates.total_working_days || '261'}`
                  }
                  unit="/day"
                />
              )}
              
              {/* Rest Day Hourly Rate - Only for OT-eligible employees */}
              {isOtEligible && (
                <RateCard
                  label="Rest Day Hourly Rate"
                  value={calculatedRates.rest_day_hourly_rate}
                  fieldName="rest_day_hourly_rate"
                  color="text-purple-600"
                  formula={
                    calculatedRates.pay_type === 'hourly'
                      ? 'Hourly × 1.5'
                      : 'Rest Day Daily Rate ÷ 8'
                  }
                  unit="/hr"
                />
              )}
            </div>
          </>
        ) : (
          <div className="bg-white rounded-lg p-4 border border-amber-200 text-center">
            <p className="text-gray-500 text-sm">
              Unable to calculate rates. Please ensure the employee has an active salary record 
              and MOM Working Days data is available for {currentYear}.
            </p>
          </div>
        )}
        
        {calculatedRates?.calculation_details && (
          <details className="mt-4">
            <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
              View calculation formula
            </summary>
            <div className="mt-2 text-xs text-gray-500 bg-white rounded p-3 border font-mono">
              {/* Only show calculation details if at least one rate is visible */}
              {Object.keys(sensitiveFieldsVisible).some(key => 
                ['daily_rate', 'overtime_hourly_rate', 'rest_day_daily_rate', 'rest_day_hourly_rate'].includes(key) &&
                sensitiveFieldsVisible[key]
              ) ? (
                <p>{calculatedRates.calculation_details}</p>
              ) : (
                <p className="text-gray-400 italic">
                  Calculation details are hidden. Reveal at least one rate to view formulas.
                </p>
              )}
            </div>
          </details>
        )}
      </div>

      {/* Payment Details Section */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-red-600" />
          Payment Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="field-label">Bank Name</label>
            <p className="field-display">{hrProfileWithSalary?.hrprofile_bank_name || 'Not provided'}</p>
          </div>
          <SharedSensitiveField
            label="Account Number"
            value={hrProfileWithSalary?.hrprofile_bank_account_number}
            fieldName="bank_account"
            type="account"
          />
          <div className="space-y-1.5">
            <SharedSensitiveField
              label="Account Holder Name"
              value={hrProfileWithSalary?.hrprofile_bank_account_holder_name}
              fieldName="bank_account_holder_name"
              type="name"
              theme="admin"
            />
            <StatusBadge
              status={hrProfileWithSalary?.hrprofile_bank_account_holder_name_verified_at ? 'approved' : 'pending'}
              label={
                hrProfileWithSalary?.hrprofile_bank_account_holder_name_verified_at
                  ? `Verified ${formatDate(hrProfileWithSalary.hrprofile_bank_account_holder_name_verified_at)}`
                  : 'Unverified — bank statement required'
              }
              showIcon
            />
          </div>
          <div>
            <label className="field-label">Branch Code</label>
            <p className="field-display">{hrProfileWithSalary?.hrprofile_branch_code || 'Not provided'}</p>
          </div>
          <div>
            <label className="field-label">Payment Mode</label>
            <p className="field-display capitalize">
              {({ giro: 'GIRO', bank_transfer: 'Bank Transfer', cheque: 'Cheque' }[hrProfileWithSalary?.hrprofile_pay_mode] || hrProfileWithSalary?.hrprofile_pay_mode?.replace('_', ' ') || 'Not provided')}
            </p>
          </div>
        </div>
      </div>

      {/* Benefits & Allowances Section */}
      <EmployeeAllowancesSection employee={employee} formatCurrency={formatCurrency} theme="admin" />

      {/* Fixed Deductions Section */}
      <EmployeeDeductionsSection employee={employee} formatCurrency={formatCurrency} theme="admin" />

      {/* Statutory Obligations Section (CPF + Levy, conditional by pass type) */}
      <EmployeeStatutoryObligationsSection hrProfile={hrProfile} />
    </div>
  );
};

/* EmployeeLegalComplianceTab removed - content merged into Personal tab as "Compliance Information" section */

const formatStaffPickerLabel = (emp) => {
  if (!emp) return '';
  const legal = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  if (emp.alias?.trim()) return `${emp.alias.trim()} (${legal})`;
  return legal || emp.staff_id || '';
};

const EmployeeEmploymentTab = ({
  employee,
  hrProfile,
  isEditing,
  formData,
  onChange,
  departments = [],
  employees = [],
  workScheduleTemplates = [],
  onEmployeeRefresh,
  onMarkInactive,
  markInactivePending = false,
  pendingEventPreset = null,
  onPendingEventPresetConsumed,
}) => {
  const calculateDaysRemaining = (endDate) => getCalendarDaysUntil(endDate);

  const isProbationEndingSoon = (endDate, thresholdDays = 30) => {
    const d = calculateDaysRemaining(endDate);
    return d !== null && d > 0 && d <= thresholdDays;
  };

  const isProbationCompleted = (endDate) => {
    const d = calculateDaysRemaining(endDate);
    return d !== null && d < 0;
  };

  // ── Employment Movement state ─────────────────────────────
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changeForm, setChangeForm] = useState({
    change_type: '',
    effective_from: '',
    designation: '',
    department_id: '',
    trade_category_id: '',
    reporting_officer_id: '',
    employment_type: '',
    employment_status: '',
    basic_salary: '',
    change_reason: '',
    restart_probation: false,
    probation_period_months: 3,
  });
  const [currentBasicSalary, setCurrentBasicSalary] = useState(null);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [changeSaving, setChangeSaving] = useState(false);
  const [changeTypeManual, setChangeTypeManual] = useState(false);
  const [cancellingScheduled, setCancellingScheduled] = useState(false);
  const [datesEditing, setDatesEditing] = useState(false);
  const [datesForm, setDatesForm] = useState({ employment_start_date: '', employment_end_date: '' });
  const [datesSaving, setDatesSaving] = useState(false);
  const [showAmendProbationModal, setShowAmendProbationModal] = useState(false);
  const [amendProbationForm, setAmendProbationForm] = useState({
    new_probation_end_date: '',
    reason: '',
    amendment_type: 'extension',
  });
  const [amendProbationSaving, setAmendProbationSaving] = useState(false);
  const [roCandidates, setRoCandidates] = useState([]);
  const [roCandidatesLoading, setRoCandidatesLoading] = useState(false);
  const [selectedRoSnapshot, setSelectedRoSnapshot] = useState(null);
  const roSearchSeqRef = useRef(0);

  const passType = isEditing
    ? (formData.hrprofile_employment_pass_type || hrProfile?.hrprofile_employment_pass_type)
    : hrProfile?.hrprofile_employment_pass_type;
  // MOM designation applies to all foreign pass holders (WP / S Pass / EP),
  // regardless of classification (EP holders are staff, not workers).
  const showMomOccupation = ['wp', 's_pass', 'ep'].includes(passType);
  const { data: momDesignations = [] } = useMOMDesignations(passType, { enabled: showMomOccupation });
  const momDesignationOptions = useMemo(
    () => momDesignations.map((d) => ({
      value: d.designation_name,
      label: d.designation_name,
    })),
    [momDesignations],
  );
  // EP uses a creatable combobox (type-a-new-one + pick-a-suggestion).
  const momDesignationItems = useMemo(
    () => momDesignations.map((d) => ({ id: d.designation_name, name: d.designation_name })),
    [momDesignations],
  );

  const fetchRoCandidates = useCallback(async (search = '') => {
    const seq = ++roSearchSeqRef.current;
    setRoCandidatesLoading(true);
    try {
      const params = {
        page_size: 50,
        employment_status: 'active',
      };
      const trimmed = search.trim();
      if (trimmed) params.search = trimmed;
      const res = await staffAPI.getStaffList(params);
      if (seq !== roSearchSeqRef.current) return;
      const results = (res.data?.results || res.data || [])
        .filter((emp) => emp.staff_id !== employee?.staff_id);
      setRoCandidates(results);
    } catch (err) {
      if (seq !== roSearchSeqRef.current) return;
      console.error('Reporting officer search failed:', err);
      setRoCandidates([]);
    } finally {
      if (seq === roSearchSeqRef.current) setRoCandidatesLoading(false);
    }
  }, [employee?.staff_id]);

  const selectedRoDisplay = useMemo(() => {
    if (!changeForm.reporting_officer_id) return undefined;
    const fromResults = roCandidates.find((emp) => emp.staff_id === changeForm.reporting_officer_id);
    if (fromResults) return formatStaffPickerLabel(fromResults);
    if (employee?.reporting_officer?.staff_id === changeForm.reporting_officer_id) {
      return formatStaffPickerLabel(employee.reporting_officer);
    }
    if (selectedRoSnapshot?.staff_id === changeForm.reporting_officer_id) {
      return formatStaffPickerLabel(selectedRoSnapshot);
    }
    return changeForm.reporting_officer_id;
  }, [changeForm.reporting_officer_id, roCandidates, employee?.reporting_officer, selectedRoSnapshot]);

  const loadMovements = useCallback(async () => {
    if (!employee?.staff_id) return;
    setMovementsLoading(true);
    try {
      const res = await staffAPI.getEmploymentMovements(employee.staff_id);
      setMovements(res.data || []);
    } catch {
      setMovements([]);
    } finally {
      setMovementsLoading(false);
    }
  }, [employee?.staff_id]);

  useEffect(() => { loadMovements(); }, [loadMovements]);

  useEffect(() => {
    if (isEditing) setDatesEditing(false);
  }, [isEditing]);

  useEffect(() => {
    setDatesEditing(false);
    setDatesForm({ employment_start_date: '', employment_end_date: '' });
  }, [employee?.staff_id]);

  const latestMovement = movements[0];
  const effectiveSince = latestMovement?.effective_from;

  const fetchCurrentBasicSalary = useCallback(async () => {
    if (!employee?.staff_id) {
      setCurrentBasicSalary(null);
      return '';
    }
    setSalaryLoading(true);
    try {
      const res = await confidentialAPI.getEmployeeSalary({
        payroll_staff_id: employee.staff_id,
        is_active: true,
      });
      const rows = res.data?.results || res.data || [];
      const active = Array.isArray(rows) ? rows[0] : null;
      const amount = active?.payroll_basic_salary;
      const str = amount != null && amount !== '' ? String(amount) : '';
      setCurrentBasicSalary(str || null);
      return str;
    } catch {
      setCurrentBasicSalary(null);
      return '';
    } finally {
      setSalaryLoading(false);
    }
  }, [employee?.staff_id]);

  const openChangeModal = async (presetType = '') => {
    const reportingOfficerId = employee?.reporting_officer?.staff_id || '';
    const salaryStr = await fetchCurrentBasicSalary();
    const defaultRestart = presetType === 'transfer' || employee?.employment_status === 'probation';
    setChangeForm({
      change_type: presetType || '',
      effective_from: '',
      designation: employee?.designation || '',
      department_id: employee?.department?.id || '',
      trade_category_id: employee?.trade_category?.id || '',
      reporting_officer_id: reportingOfficerId,
      employment_type: employee?.employment_type || 'full_time',
      employment_status: employee?.employment_status || 'active',
      basic_salary: salaryStr,
      change_reason: '',
      restart_probation: defaultRestart && presetType === 'transfer',
      probation_period_months: employee?.probation_period_months || 3,
    });
    setSelectedRoSnapshot(employee?.reporting_officer || null);
    setRoCandidates([]);
    // When deep-linked from a specific field, treat the type as user-chosen so
    // the auto-derive effect doesn't reset it before any field is edited.
    setChangeTypeManual(!!presetType);
    setShowChangeModal(true);
  };

  // Deep-link from Compensation → "Record salary change"
  React.useEffect(() => {
    if (!pendingEventPreset) return;
    let cancelled = false;
    (async () => {
      await openChangeModal(pendingEventPreset);
      if (!cancelled && typeof onPendingEventPresetConsumed === 'function') {
        onPendingEventPresetConsumed();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once per preset hand-off
  }, [pendingEventPreset]);

  const scheduledMovement = useMemo(
    () => (movements || []).find((m) => m.is_scheduled),
    [movements],
  );

  const probationStartDate = employee?.probation_start_date || employee?.employment_start_date || '';
  const hasPendingProbationRestart = !!scheduledMovement?.to_probation_start_date;

  const openAmendProbationModal = () => {
    const currentEnd = hrProfile?.hrprofile_probation_end_date || '';
    setAmendProbationForm({
      new_probation_end_date: currentEnd,
      reason: '',
      amendment_type: 'extension',
    });
    setShowAmendProbationModal(true);
  };

  const handleAmendProbationSave = async () => {
    if (!employee?.staff_id) return;
    const newEnd = amendProbationForm.new_probation_end_date || '';
    const reason = (amendProbationForm.reason || '').trim();
    if (!newEnd) {
      toast.error('New probation end date is required');
      return;
    }
    if (reason.length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setAmendProbationSaving(true);
    try {
      await staffAPI.amendProbation(employee.staff_id, {
        new_probation_end_date: newEnd,
        reason,
        amendment_type: amendProbationForm.amendment_type || 'extension',
      });
      toast.success('Probation end date amended');
      setShowAmendProbationModal(false);
      if (onEmployeeRefresh) onEmployeeRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0] : (detail || 'Failed to amend probation end date'));
    } finally {
      setAmendProbationSaving(false);
    }
  };

  const amendProbationDelta = useMemo(() => {
    const currentEnd = hrProfile?.hrprofile_probation_end_date;
    const newEnd = amendProbationForm.new_probation_end_date;
    if (!newEnd) return null;
    const newDate = parseLocalDate(newEnd);
    if (!newDate) return null;
    const start = parseLocalDate(probationStartDate);
    const currentDate = currentEnd ? parseLocalDate(currentEnd) : null;
    const today = startOfDay(new Date());
    let daysDelta = null;
    if (currentDate) {
      daysDelta = Math.round((newDate - currentDate) / (1000 * 60 * 60 * 24));
    }
    let totalMonths = null;
    if (start) {
      for (let n = 1; n <= 24; n += 1) {
        if (toLocalISODate(addMonths(start, n)) === newEnd) {
          totalMonths = n;
          break;
        }
      }
    }
    return {
      daysDelta,
      totalMonths,
      isPast: isAfter(today, newDate),
      endsToday: toLocalISODate(today) === newEnd,
      beyondTwelveMonths: start ? isAfter(newDate, addMonths(start, 12)) : false,
      pastEmploymentEnd: employee?.employment_end_date
        ? isAfter(newDate, parseLocalDate(employee.employment_end_date))
        : false,
      notOnProbation: employee?.employment_status !== 'probation',
    };
  }, [
    amendProbationForm.new_probation_end_date,
    hrProfile?.hrprofile_probation_end_date,
    probationStartDate,
    employee?.employment_end_date,
    employee?.employment_status,
  ]);

  const handleCancelScheduled = async () => {
    if (!employee?.staff_id || !scheduledMovement?.id) return;
    setCancellingScheduled(true);
    try {
      await staffAPI.cancelScheduledEmploymentChange(employee.staff_id, scheduledMovement.id);
      toast.success('Scheduled employment change cancelled');
      loadMovements();
      if (onEmployeeRefresh) onEmployeeRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0] : (detail || 'Failed to cancel scheduled change'));
    } finally {
      setCancellingScheduled(false);
    }
  };

  const changeOriginalValues = useMemo(() => ({
    designation: employee?.designation || '',
    department_id: employee?.department?.id || '',
    trade_category_id: employee?.trade_category?.id || '',
    reporting_officer_id: employee?.reporting_officer?.staff_id || '',
    employment_type: employee?.employment_type || 'full_time',
    employment_status: employee?.employment_status || 'active',
    basic_salary: currentBasicSalary ?? '',
  }), [employee?.designation, employee?.department?.id, employee?.trade_category?.id, employee?.reporting_officer?.staff_id, employee?.employment_type, employee?.employment_status, currentBasicSalary]);

  const dirtyFields = useMemo(() => {
    const dirty = {};
    dirty.designation = changeForm.designation !== changeOriginalValues.designation;
    dirty.department_id = changeForm.department_id !== changeOriginalValues.department_id;
    dirty.trade_category_id = changeForm.trade_category_id !== changeOriginalValues.trade_category_id;
    dirty.reporting_officer_id = changeForm.reporting_officer_id !== changeOriginalValues.reporting_officer_id;
    dirty.employment_type = changeForm.employment_type !== changeOriginalValues.employment_type;
    dirty.employment_status = changeForm.employment_status !== changeOriginalValues.employment_status;
    dirty.basic_salary = String(changeForm.basic_salary ?? '') !== String(changeOriginalValues.basic_salary ?? '');
    dirty.restart_probation = !!changeForm.restart_probation;
    return dirty;
  }, [changeForm, changeOriginalValues]);

  const hasAnyFieldChanged = Object.entries(dirtyFields)
    .filter(([key]) => key !== 'restart_probation')
    .some(([, v]) => v) || !!changeForm.restart_probation;

  const computedProbationEnd = useMemo(() => {
    if (!changeForm.restart_probation || !changeForm.effective_from) return null;
    try {
      const start = parseLocalDate(changeForm.effective_from);
      if (!start) return null;
      const months = Number(changeForm.probation_period_months) || 3;
      return addMonths(start, months);
    } catch {
      return null;
    }
  }, [changeForm.restart_probation, changeForm.effective_from, changeForm.probation_period_months]);

  const isFutureEffective = useMemo(() => {
    if (!changeForm.effective_from) return false;
    try {
      const eff = parseLocalDate(changeForm.effective_from);
      if (!eff) return false;
      return isAfter(startOfDay(eff), startOfDay(new Date()));
    } catch {
      return false;
    }
  }, [changeForm.effective_from]);

  const showProbationSection = (
    changeForm.change_type === 'transfer'
    || employee?.employment_status === 'probation'
    || changeForm.restart_probation
  );

  useEffect(() => {
    if (changeTypeManual || !showChangeModal) return;

    const AUTO_DERIVE_MAP = {
      designation: 'redesignation',
      department_id: 'transfer',
      trade_category_id: 'trade_change',
      reporting_officer_id: 'ro_change',
      employment_type: 'type_change',
      employment_status: 'status_change',
      basic_salary: 'salary_change',
    };

    const changedKeys = Object.entries(dirtyFields).filter(([, v]) => v).map(([k]) => k);

    if (changedKeys.length === 1 && AUTO_DERIVE_MAP[changedKeys[0]]) {
      const derived = AUTO_DERIVE_MAP[changedKeys[0]];
      setChangeForm((f) => ({
        ...f,
        change_type: derived,
        // Department-only transfer defaults to restarting probation for
        // staff still on probation — HR can uncheck in the Probation section.
        restart_probation: derived === 'transfer'
          && (employee?.employment_status === 'probation' || f.restart_probation)
          ? true
          : f.restart_probation,
      }));
    } else if (changedKeys.length === 0) {
      setChangeForm(f => ({ ...f, change_type: '' }));
    }
    // 2+ fields changed or ambiguous → leave user's selection alone
  }, [dirtyFields, changeTypeManual, showChangeModal]);

  const handleSaveChange = async () => {
    if (!changeForm.change_type) {
      toast.error('Please select a change type');
      return;
    }
    if (!changeForm.effective_from) {
      toast.error('Effective date is required');
      return;
    }
    if (!hasAnyFieldChanged) {
      toast.error('No fields have been changed');
      return;
    }
    setChangeSaving(true);
    try {
      const payload = {
        change_type: changeForm.change_type,
        effective_from: changeForm.effective_from,
        change_reason: changeForm.change_reason,
      };
      if (changeForm.designation !== (employee?.designation || '')) {
        payload.designation = changeForm.designation;
      }
      if (changeForm.department_id !== (employee?.department?.id || '')) {
        payload.department_id = changeForm.department_id || null;
      }
      if (changeForm.trade_category_id !== (employee?.trade_category?.id || '')) {
        payload.trade_category_id = changeForm.trade_category_id || null;
      }
      if (changeForm.reporting_officer_id !== (employee?.reporting_officer?.staff_id || '')) {
        payload.reporting_officer_id = changeForm.reporting_officer_id || null;
      }
      if (changeForm.employment_type !== (employee?.employment_type || '')) {
        payload.employment_type = changeForm.employment_type;
      }
      if (changeForm.employment_status !== (employee?.employment_status || '')) {
        payload.employment_status = changeForm.employment_status;
      }
      if (dirtyFields.basic_salary && changeForm.basic_salary !== '') {
        payload.new_basic_salary = parseFloat(changeForm.basic_salary);
      }
      if (changeForm.restart_probation) {
        payload.restart_probation = true;
        payload.probation_period_months = Number(changeForm.probation_period_months) || 3;
      }
      const res = await staffAPI.recordEmploymentChange(employee.staff_id, payload);
      const scheduled = res?.data?.is_scheduled || res?.data?.applied_at == null;
      toast.success(
        scheduled
          ? `Employment change scheduled for ${formatDate(changeForm.effective_from)}`
          : 'Employment change recorded',
      );
      setShowChangeModal(false);
      loadMovements();
      if (onEmployeeRefresh) onEmployeeRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0] : (detail || 'Failed to record change'));
    } finally {
      setChangeSaving(false);
    }
  };

  const { data: tradeCategories = [] } = useTradeCategories();
  const { data: distinctDesignations = [] } = useDistinctDesignations();

  const CHANGE_TYPE_OPTIONS = [
    { value: 'promotion', label: 'Promotion' },
    { value: 'transfer', label: 'Department Transfer' },
    { value: 'redesignation', label: 'Redesignation' },
    { value: 'trade_change', label: 'Trade Category Change' },
    { value: 'ro_change', label: 'Reporting Officer Change' },
    { value: 'type_change', label: 'Employment Type Change' },
    { value: 'status_change', label: 'Employment Status Change' },
    { value: 'lateral_move', label: 'Lateral Move' },
    { value: 'correction', label: 'Data Correction' },
    { value: 'salary_change', label: 'Salary Change' },
  ];

  const formatSalaryDisplay = (value) => {
    if (value == null || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return `$${num.toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const openDatesEdit = () => {
    setDatesForm({
      employment_start_date: employee?.employment_start_date || '',
      employment_end_date: employee?.employment_end_date || '',
    });
    setDatesEditing(true);
  };

  const handleDatesEditCancel = () => {
    setDatesEditing(false);
    setDatesForm({ employment_start_date: '', employment_end_date: '' });
  };

  const handleDatesEditSave = async () => {
    const startVal = datesForm.employment_start_date || '';
    const endVal = datesForm.employment_end_date || '';
    const origStart = employee?.employment_start_date || '';
    const origEnd = employee?.employment_end_date || '';

    if (startVal && endVal && endVal < startVal) {
      toast.error('Employment end date cannot be before employment start date');
      return;
    }

    const payload = {};
    if (startVal !== origStart) {
      payload.employment_start_date = startVal || null;
    }
    if (endVal !== origEnd) {
      payload.employment_end_date = endVal || null;
    }

    if (Object.keys(payload).length === 0) {
      toast('No changes to save');
      setDatesEditing(false);
      return;
    }

    setDatesSaving(true);
    try {
      await staffAPI.updateStaff(employee.staff_id, payload);
      toast.success('Employment dates updated');
      setDatesEditing(false);
      if (onEmployeeRefresh) await onEmployeeRefresh();
    } catch (err) {
      const data = err?.response?.data;
      const pickMsg = (val) => (Array.isArray(val) ? val[0] : val);
      const detail = pickMsg(data?.employment_end_date)
        || pickMsg(data?.employment_start_date)
        || pickMsg(data?.work_schedule_template_id)
        || pickMsg(data?.detail)
        || (data && typeof data === 'object'
          ? pickMsg(Object.values(data).find((v) => typeof v === 'string' || Array.isArray(v)))
          : null);
      toast.error(detail || 'Failed to update employment dates');
    } finally {
      setDatesSaving(false);
    }
  };

  const isDatesFieldEditing = isEditing || datesEditing;
  const activeStartDate = isEditing ? formData.employment_start_date : datesForm.employment_start_date;
  const activeEndDate = isEditing ? formData.employment_end_date : datesForm.employment_end_date;
  const handleActiveStartChange = (val) => {
    if (isEditing) onChange('employment_start_date', val);
    else setDatesForm((f) => ({ ...f, employment_start_date: val }));
  };
  const handleActiveEndChange = (val) => {
    if (isEditing) onChange('employment_end_date', val);
    else setDatesForm((f) => ({ ...f, employment_end_date: val }));
  };

  const isOutsourcedHolder = employee?.employment_classification === 'outsourced';

  // Inline affordance for change-tracked fields. These fields are intentionally
  // read-only because edits to them are real employment events (promotions,
  // transfers, RO/status changes) that must be captured as effective-dated
  // EmploymentMovement records rather than silently overwriting the value.
  // Icon-only (ArrowRightLeft, not pencil) so field shortcuts don't compete
  // with the tab's primary "Record Employment Event" CTA.
  const RECORD_CHANGE_ARIA = {
    status_change: 'Record status change',
    transfer: 'Record transfer',
    redesignation: 'Record redesignation',
    ro_change: 'Record reporting officer change',
    type_change: 'Record employment type change',
  };
  const RecordChangeLink = ({ presetType }) => {
    const ariaLabel = RECORD_CHANGE_ARIA[presetType] || 'Record employment event';
    return (
      <button
        type="button"
        onClick={() => openChangeModal(presetType)}
        className="icon-button-admin icon-button-sm flex-shrink-0"
        title={ariaLabel}
        aria-label={ariaLabel}
      >
        <ArrowRightLeft className="w-3.5 h-3.5" />
      </button>
    );
  };

  // Field-label header for change-tracked fields. The lock icon teaches HR
  // (without needing to read the helper text) that this value is not freely
  // editable: changes become effective-dated employment events, never silent
  // overwrites. Outsourced holders have no EmploymentMovement path — plain labels.
  // Employment Dates grid uses comfortable (32px) label rows — same contract as
  // Employment Pass — because TrackedFieldLabel / Probation carry micro actions.
  const DATES_LABEL_SIZE = 'comfortable';

  const TrackedFieldLabel = ({ label, presetType }) => {
    if (isOutsourcedHolder) {
      return <FieldLabelRow label={label} size={DATES_LABEL_SIZE} />;
    }
    return (
      <FieldLabelRow
        label={label}
        size={DATES_LABEL_SIZE}
        action={<RecordChangeLink presetType={presetType} />}
      >
        <span title="Tracked field — changes are recorded as an effective-dated employment event (via Record Employment Event), not edited directly.">
          <Lock className="w-3 h-3 text-gray-400" aria-hidden="true" />
        </span>
      </FieldLabelRow>
    );
  };

  const employmentDatesSection = (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2 min-w-0">
          <Calendar className="w-5 h-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Employment Dates</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isOutsourcedHolder ? (
                <>
                  End date is the <span className="font-medium text-gray-600">last day of engagement (inclusive)</span>.
                  {' '}Then set Employment Status to <span className="font-medium text-gray-600">Inactive</span>.
                </>
              ) : (
                <>
                  End date is the <span className="font-medium text-gray-600">last working day (inclusive)</span>.
                </>
              )}
            </p>
          </div>
        </div>
        {!isEditing && (
          datesEditing ? (
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleDatesEditCancel}
                disabled={datesSaving}
                className="btn btn-sm btn-admin-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDatesEditSave}
                disabled={datesSaving}
                className="btn btn-sm btn-admin-primary"
              >
                {datesSaving ? 'Saving...' : 'Save Dates'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={openDatesEdit}
              className="icon-button-admin flex-shrink-0"
              title="Edit employment dates"
              aria-label="Edit employment dates"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          )
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <FieldLabelRow label="Employment Start Date" size={DATES_LABEL_SIZE} />
          {isDatesFieldEditing ? (
            <DatePickerField
              theme="admin"
              selected={parseLocalDate(activeStartDate)}
              onChange={(date) => handleActiveStartChange(date ? toLocalISODate(date) : '')}
              placeholderText="Select date"
            />
          ) : (
            <p className="field-display">{employee?.employment_start_date ? formatDate(employee.employment_start_date) : 'Not specified'}</p>
          )}
        </div>
        <div>
          <FieldLabelRow label="System Join Date" size={DATES_LABEL_SIZE} />
          <p className="field-display">{employee?.date_joined ? formatDate(employee.date_joined) : 'Not specified'}</p>
        </div>
        <div>
          <FieldLabelRow label="Employment End Date" size={DATES_LABEL_SIZE} />
          {isDatesFieldEditing ? (
            <DatePickerField
              theme="admin"
              selected={parseLocalDate(activeEndDate)}
              onChange={(date) => handleActiveEndChange(date ? toLocalISODate(date) : '')}
              isClearable
              placeholderText="Select date"
              minDate={parseLocalDate(activeStartDate || employee?.employment_start_date)}
            />
          ) : (
            <p className={`field-display ${
              employee?.employment_end_date ? 'border-orange-200 bg-orange-50 text-orange-900' : ''
            }`}>
              {employee?.employment_end_date ? formatDate(employee.employment_end_date) : 'Not set'}
            </p>
          )}
          {!isDatesFieldEditing && employee?.employment_end_date && (
            <p className="text-xs text-gray-500 mt-1">
              {isOutsourcedHolder
                ? 'Last day of engagement (inclusive)'
                : 'Last day on payroll and attendance records'}
            </p>
          )}
        </div>
        <div>
          {isOutsourcedHolder ? (
            <FieldLabelRow label="Employment Status" size={DATES_LABEL_SIZE} />
          ) : (
            <TrackedFieldLabel label="Employment Status" presetType="status_change" />
          )}
          <p className="field-display capitalize">{employee?.employment_status || 'Not specified'}</p>
          {isOutsourcedHolder && employee?.employment_status !== 'inactive' && onMarkInactive && (
            <button
              type="button"
              onClick={onMarkInactive}
              disabled={markInactivePending}
              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded disabled:opacity-50"
            >
              <ArrowRightLeft className="w-3 h-3" />
              {markInactivePending ? 'Updating…' : 'Set to Inactive'}
            </button>
          )}
        </div>
        {!isOutsourcedHolder && (
          <div>
            <FieldLabelRow
              label="Probation End Date"
              size={DATES_LABEL_SIZE}
              action={
                !isEditing ? (
                  <button
                    type="button"
                    onClick={openAmendProbationModal}
                    disabled={hasPendingProbationRestart}
                    title={
                      hasPendingProbationRestart
                        ? 'Cancel the scheduled employment change first'
                        : 'Amend probation end date'
                    }
                    aria-label="Amend probation end date"
                    className="icon-button-admin icon-button-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CalendarClock className="w-3.5 h-3.5" />
                  </button>
                ) : null
              }
            >
              {!isEditing && employee?.probation_extended && (
                <StatusBadge status="probation_extended" />
              )}
              {!isEditing && hrProfile?.hrprofile_probation_end_date && (
                <>
                  {isProbationCompleted(hrProfile.hrprofile_probation_end_date) && <StatusBadge status="completed" showIcon />}
                  {isProbationEndingSoon(hrProfile.hrprofile_probation_end_date) && <StatusBadge status="expiring_soon" label="Ending Soon" showIcon />}
                  {!isProbationCompleted(hrProfile.hrprofile_probation_end_date) && !isProbationEndingSoon(hrProfile.hrprofile_probation_end_date) && !employee?.probation_extended && <StatusBadge status="active" />}
                </>
              )}
            </FieldLabelRow>
            <p className={`field-display ${
              hrProfile?.hrprofile_probation_end_date
                ? isProbationCompleted(hrProfile.hrprofile_probation_end_date)
                  ? 'border-green-200 bg-green-50'
                  : isProbationEndingSoon(hrProfile.hrprofile_probation_end_date)
                    ? 'border-orange-200 bg-orange-50'
                    : ''
                : ''
            }`}>
              {hrProfile?.hrprofile_probation_end_date ? formatDate(hrProfile.hrprofile_probation_end_date) : 'Not provided'}
            </p>
            {hrProfile?.hrprofile_probation_end_date && (
              <p className="text-xs text-gray-500 mt-2">
                {(() => {
                  const d = calculateDaysRemaining(hrProfile.hrprofile_probation_end_date);
                  if (d === null) return '';
                  if (d < 0) return `Completed ${formatDaySpan(Math.abs(d), 'long')} ago`;
                  if (d === 0) return 'Ends today';
                  return `${formatDaySpan(d, 'long')} remaining`;
                })()}
              </p>
            )}
            {(hrProfile?.hrprofile_probation_extension_reason || employee?.probation_extension_reason) && (
              <p className="text-xs text-gray-500 mt-1">
                Reason: {hrProfile?.hrprofile_probation_extension_reason || employee?.probation_extension_reason}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <Briefcase className="w-6 h-6 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Employment Details</h2>
            {!isOutsourcedHolder && (
              <p className="text-xs text-gray-500 mt-0.5">
                Position, org events, and career timeline live here. Qualifications stay on Professional.
              </p>
            )}
          </div>
        </div>
        {!isOutsourcedHolder && (
          <button
            type="button"
            onClick={() => openChangeModal()}
            className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 self-start sm:self-auto sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
            title="Record a promotion, transfer, reporting-officer / type / status change or salary update — change one or several fields together on a single effective date."
            aria-label="Record Employment Event"
            disabled={!!scheduledMovement}
          >
            <ArrowRightLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Record Employment Event</span>
          </button>
        )}
      </div>

    {scheduledMovement && (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <CalendarClock className="w-5 h-5 text-blue-600" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-gray-900">Scheduled employment change</h3>
              <StatusBadge status="upcoming" label="Scheduled" />
            </div>
            <p className="text-sm text-gray-700 mt-1">
              {scheduledMovement.change_type_display} effective{' '}
              <span className="font-medium">{formatDate(scheduledMovement.effective_from)}</span>
              {scheduledMovement.from_department_display !== scheduledMovement.to_department_display && (
                <>
                  {' — '}
                  {scheduledMovement.from_department_display || '—'}
                  {' → '}
                  {scheduledMovement.to_department_display || '—'}
                </>
              )}
              {scheduledMovement.from_reporting_officer_display !== scheduledMovement.to_reporting_officer_display && (
                <>
                  {' · RO '}
                  {scheduledMovement.from_reporting_officer_display || '—'}
                  {' → '}
                  {scheduledMovement.to_reporting_officer_display || '—'}
                </>
              )}
              {scheduledMovement.to_probation_start_date && (
                <> · Probation restarts</>
              )}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {employee?.first_name || 'Employee'} stays in their current department and reporting line until that date. Applied automatically overnight.
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancelScheduled}
            disabled={cancellingScheduled}
            className="btn btn-sm btn-admin-secondary shrink-0"
          >
            {cancellingScheduled ? 'Cancelling…' : 'Cancel'}
          </button>
        </div>
      </div>
    )}

    {employmentDatesSection}

    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-red-600 shrink-0" aria-hidden="true" />
            Current Position
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {isOutsourcedHolder
              ? 'Not on payroll. Set an end date, then set Employment Status to Inactive — same Active / Inactive vocabulary as employees.'
              : (
                <>
                  Locked <Lock className="inline w-3 h-3 text-gray-400 align-text-bottom" /> fields need an effective-dated <span className="font-medium text-gray-600">Record Employment Event</span>. Join and probation milestones appear in Career Timeline & History below.
                </>
              )}
          </p>
        </div>
        {effectiveSince && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            Effective since {formatDate(effectiveSince)}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <FieldLabelRow label="Staff ID" size={DATES_LABEL_SIZE} />
          <p className="field-display font-mono">{employee?.staff_id}</p>
        </div>
        <div>
          <TrackedFieldLabel label="Department" presetType="transfer" />
          <p className="field-display">{employee?.department?.department_name || employee?.department || 'Not assigned'}</p>
        </div>
        <div>
          <TrackedFieldLabel label="Designation" presetType="redesignation" />
          <p className="field-display">{employee?.designation || 'Not specified'}</p>
        </div>
        {showMomOccupation && (
          <div>
            <FieldLabelRow label="MOM Designation" size={DATES_LABEL_SIZE} />
            {isEditing ? (
              passType === 'ep' ? (
                <SearchableCombobox
                  variant="form"
                  theme="admin"
                  items={momDesignationItems}
                  value={formData.mom_designation || ''}
                  onChange={(v) => onChange('mom_designation', v)}
                  valueFn={(d) => d.id}
                  displayFn={(d) => d.name}
                  displayValue={formData.mom_designation || undefined}
                  placeholder="Select or type MOM designation"
                  searchPlaceholder="Search designations…"
                  creatableLabel={(term) => `Use "${term}"`}
                  onCreateNew={(term) => term?.trim() && onChange('mom_designation', term.trim())}
                  emptyLabel="No saved designations yet — type to add one"
                  clearLabel="Clear"
                />
              ) : (
                <FormSelect
                  theme="admin"
                  options={momDesignationOptions}
                  value={formData.mom_designation || ''}
                  onChange={(v) => onChange('mom_designation', v)}
                  placeholder="Select MOM designation"
                />
              )
            ) : (
              <p className="field-display">{employee?.mom_designation || 'Not specified'}</p>
            )}
          </div>
        )}
        <div>
          <TrackedFieldLabel label="Reporting Officer" presetType="ro_change" />
          <p className="field-display">
            {employee?.reporting_officer 
              ? ((employee.reporting_officer.alias && employee.reporting_officer.alias.trim()) 
                  ? `${employee.reporting_officer.alias.trim()} (${[employee.reporting_officer.first_name, employee.reporting_officer.last_name].filter(Boolean).join(' ')})`
                  : `${employee.reporting_officer.first_name || ''} ${employee.reporting_officer.last_name || ''}`.trim())
              : 'No Reporting Officer assigned'}
          </p>
        </div>
        <div>
          <FieldLabelRow label="Employment Classification" size={DATES_LABEL_SIZE} />
          {isEditing ? (
            <FormSelect
              options={[
                { value: 'worker', label: 'Worker' },
                { value: 'staff', label: 'Staff' },
                { value: 'executive', label: 'Executive' },
              ]}
              value={formData.employment_classification || ''}
              onChange={(val) => onChange('employment_classification', val)}
              theme="admin"
            />
          ) : (
            <ClassificationBadge classification={employee?.employment_classification} />
          )}
        </div>
        <div>
          <TrackedFieldLabel label="Employment Type" presetType="type_change" />
          <InfoBadge color={employee?.employment_type === 'part_time' ? 'orange' : 'emerald'} label={employee?.employment_type === 'part_time' ? 'Part-Time' : 'Full-Time'} />
        </div>
        <div>
          <FieldLabelRow label="Attendance Tracking" size={DATES_LABEL_SIZE} />
          {isEditing ? (
            <FormSelect
              options={[
                { value: 'standard', label: 'Standard' },
                { value: 'flexible', label: 'Flexible (no absence flags)' },
                { value: 'exempt', label: 'Exempt (no tracking)' },
              ]}
              value={formData.attendance_tracking_mode || 'standard'}
              onChange={(val) => onChange('attendance_tracking_mode', val)}
              theme="admin"
            />
          ) : (
            <InfoBadge
              color={
                employee?.attendance_tracking_mode === 'exempt' ? 'red'
                : employee?.attendance_tracking_mode === 'flexible' ? 'orange'
                : 'emerald'
              }
              label={
                employee?.attendance_tracking_mode === 'exempt' ? 'Exempt'
                : employee?.attendance_tracking_mode === 'flexible' ? 'Flexible'
                : 'Standard'
              }
            />
          )}
        </div>
        <div>
          <FieldLabelRow label="Work Schedule Template" size={DATES_LABEL_SIZE} />
          {isEditing ? (
            <FormSelect
              options={[
                { value: '', label: 'Select Work Schedule Template' },
                ...workScheduleTemplates.map(template => ({
                  value: template.id,
                  label: formatWorkScheduleTemplateLabel(template),
                })),
              ]}
              value={formData.work_schedule_template_id || ''}
              onChange={(val) => onChange('work_schedule_template_id', val)}
              theme="admin"
              required={formData.employment_classification !== 'system'}
            />
          ) : (
            <>
              <p className="field-display">
                {employee?.work_schedule_template?.name || 'Not assigned'}
              </p>
              {(employee?.work_schedule_template?.schedule_type_display
                || employee?.work_schedule_template?.schedule_type) && (
                <p className="text-xs text-gray-500 mt-1">
                  {employee.work_schedule_template.schedule_type_display
                    || employee.work_schedule_template.schedule_type
                        ?.replace('_', ' ')
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                </p>
              )}
            </>
          )}
        </div>
        <div>
          <FieldLabelRow label="MOM Classification" size={DATES_LABEL_SIZE} />
          {isEditing ? (
            <FormSelect
              options={[
                { value: '', label: 'Select' },
                { value: 'workman', label: 'Workman' },
                { value: 'non_workman', label: 'Non-workman' },
              ]}
              value={formData.hrprofile_mom_employment_classification || ''}
              onChange={(val) => onChange('hrprofile_mom_employment_classification', val)}
              theme="admin"
            />
          ) : (
            <InfoBadge color={hrProfile?.hrprofile_mom_employment_classification === 'workman' ? 'blue' : hrProfile?.hrprofile_mom_employment_classification === 'non_workman' ? 'purple' : 'gray'} label={hrProfile?.hrprofile_mom_employment_classification === 'workman' ? 'Workman' : hrProfile?.hrprofile_mom_employment_classification === 'non_workman' ? 'Non-workman' : 'Not specified'} />
          )}
        </div>
        <div>
          <FieldLabelRow label="Portal Access" size={DATES_LABEL_SIZE} />
          {employee?.portal_groups?.length > 0 ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              {employee.portal_groups.map((group) => (
                <InfoBadge
                  key={group}
                  color={
                    group === 'HR' ? 'red'
                      : group === 'Manager' ? 'blue'
                        : group === 'CourseNominator' ? 'teal'
                          : 'gray'
                  }
                  label={group}
                />
              ))}
            </div>
          ) : (
            <InfoBadge color="gray" label="None" />
          )}
        </div>
      </div>
    </div>

    {!isOutsourcedHolder && (
      <CareerTimelineHistorySection
        employee={{
          ...employee,
          // Timeline builder reads probation fields from the employee object;
          // Employment UI sources them from hrProfile — merge so they stay in sync.
          hrprofile_probation_end_date:
            employee?.hrprofile_probation_end_date || hrProfile?.hrprofile_probation_end_date,
          probation_extended: employee?.probation_extended ?? hrProfile?.probation_extended,
          probation_extension_reason:
            employee?.probation_extension_reason || hrProfile?.hrprofile_probation_extension_reason,
          probation_start_date: employee?.probation_start_date || hrProfile?.probation_start_date,
        }}
        movements={movements}
        loading={movementsLoading}
        theme="admin"
      />
    )}

    <ProfileChangeLogSection staffId={employee?.staff_id} />

    {/* ── Record Employment Event Modal ───────────────────── */}
    {showChangeModal && (
      <Modal
        isOpen
        onClose={() => setShowChangeModal(false)}
        size="md"
        theme="admin"
        contentClassName="space-y-5"
        customHeader={(
          <div className="modal-header">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-red-600" />
              <h3 className="modal-title">Record Employment Event</h3>
            </div>
            <button onClick={() => setShowChangeModal(false)} className="modal-close-admin">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        footer={(
          <>
            <button
              onClick={() => setShowChangeModal(false)}
              className="btn btn-admin-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveChange}
              disabled={changeSaving || !changeForm.effective_from || !changeForm.change_type || !hasAnyFieldChanged}
              className="btn btn-admin-primary"
            >
              {changeSaving ? 'Saving...' : 'Save Change'}
            </button>
          </>
        )}
      >
              {/* ── Required ─────────────────────────── */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Change Type <span className="text-red-500">*</span></label>
                    <FormSelect
                      options={CHANGE_TYPE_OPTIONS}
                      value={changeForm.change_type}
                      onChange={(val) => {
                        setChangeTypeManual(true);
                        setChangeForm((f) => ({
                          ...f,
                          change_type: val,
                          restart_probation: val === 'transfer'
                            ? true
                            : f.restart_probation,
                        }));
                      }}
                      placeholder="Select change type..."
                      theme="admin"
                    />
                    {!changeForm.change_type && (
                      <p className="form-help">Auto-fills when you edit a single field below, or pick one here.</p>
                    )}
                  </div>
                  <div>
                    <label className="field-label">Effective From <span className="text-red-500">*</span></label>
                    <DatePickerField
                      theme="admin"
                      selected={changeForm.effective_from ? parseLocalDate(changeForm.effective_from) : null}
                      onChange={(date) => setChangeForm(f => ({ ...f, effective_from: date ? toLocalISODate(date) : '' }))}
                      placeholderText="Select effective date"
                    />
                  </div>
                </div>
              </div>

              {isFutureEffective && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                      <CalendarClock className="w-4 h-4 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Scheduled — applies automatically on {formatDate(changeForm.effective_from)}
                        </p>
                        <p className="text-sm text-gray-700 mt-0.5">
                          {(employee?.first_name || employee?.get_full_name || 'This employee')} stays in{' '}
                          {employee?.department?.department_name || 'their current department'}
                          {employee?.reporting_officer
                            ? ` under ${employee.reporting_officer.first_name || ''} ${employee.reporting_officer.last_name || ''}`.trim()
                            : ''}
                          {' '}until then.
                        </p>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1 border-t border-blue-100 pt-2">
                        <p>
                          <span className="font-medium text-gray-800">Changes on that date:</span>{' '}
                          {[
                            dirtyFields.department_id && 'department',
                            dirtyFields.reporting_officer_id && 'reporting officer',
                            dirtyFields.designation && 'designation',
                            changeForm.restart_probation && 'probation clock',
                          ].filter(Boolean).join(', ') || 'recorded fields'}
                        </p>
                        <p>
                          <span className="font-medium text-gray-800">Unchanged:</span>{' '}
                          hire date {employee?.employment_start_date ? formatDate(employee.employment_start_date) : '—'}
                          ; annual leave and sick-leave eligibility stay keyed to continuous service
                          {changeForm.restart_probation
                            ? '; dental stays blocked until the new probation end (confirmation-based)'
                            : ''}
                          .
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Position ─────────────────────────── */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Position</p>
                <div>
                  <label className="field-label flex items-center gap-2">
                    Designation
                    {dirtyFields.designation && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" title="Changed" />}
                  </label>
                  <SearchableCombobox
                    variant="form"
                    items={distinctDesignations}
                    value={changeForm.designation}
                    onChange={(val) => setChangeForm(f => ({ ...f, designation: val }))}
                    valueFn={(d) => d}
                    displayFn={(d) => d}
                    searchFn={(d, term) => d.toLowerCase().includes(term)}
                    placeholder="Search or type designation..."
                    emptyLabel="No matching designations"
                    creatableLabel="Use this designation"
                    onCreateNew={(term) => setChangeForm(f => ({ ...f, designation: term }))}
                    displayValue={changeForm.designation && !distinctDesignations.includes(changeForm.designation) ? changeForm.designation : undefined}
                    theme="admin"
                  />
                </div>
                <div>
                  <label className="field-label flex items-center gap-2">
                    Department
                    {dirtyFields.department_id && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" title="Changed" />}
                  </label>
                  <SearchableCombobox
                    variant="form"
                    items={departments}
                    value={changeForm.department_id}
                    onChange={(val) => setChangeForm(f => ({ ...f, department_id: val }))}
                    valueFn={(d) => d.id}
                    displayFn={(d) => d.department_name}
                    searchFn={(d, term) => d.department_name.toLowerCase().includes(term)}
                    clearLabel="No Department"
                    placeholder="Select department..."
                    theme="admin"
                  />
                </div>
                <div>
                  <label className="field-label flex items-center gap-2">
                    Trade Category
                    {dirtyFields.trade_category_id && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" title="Changed" />}
                  </label>
                  <SearchableCombobox
                    variant="form"
                    items={tradeCategories}
                    value={changeForm.trade_category_id}
                    onChange={(val) => setChangeForm(f => ({ ...f, trade_category_id: val }))}
                    valueFn={(tc) => tc.id}
                    displayFn={(tc) => tc.trade_code ? `${tc.trade_code} — ${tc.name}` : tc.name}
                    searchFn={(tc, term) => {
                      const t = term.toLowerCase();
                      return (tc.name || '').toLowerCase().includes(t) || (tc.trade_code || '').toLowerCase().includes(t);
                    }}
                    clearLabel="No Trade Category"
                    placeholder="Select trade category..."
                    theme="admin"
                  />
                </div>
              </div>

              {/* ── Compensation ───────────────────────── */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Compensation</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Current Basic Salary</label>
                    <p className="field-static">
                      {salaryLoading ? 'Loading...' : formatSalaryDisplay(currentBasicSalary)}
                    </p>
                  </div>
                  <div>
                    <label className="field-label flex items-center gap-2">
                      New Basic Salary
                      {dirtyFields.basic_salary && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" title="Changed" />}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-admin w-full"
                      value={changeForm.basic_salary}
                      onChange={(e) => setChangeForm((f) => ({ ...f, basic_salary: e.target.value }))}
                      placeholder={salaryLoading ? 'Loading...' : 'e.g. 1200'}
                      disabled={salaryLoading}
                    />
                  </div>
                </div>
              </div>

              {/* ── Reporting ────────────────────────── */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Reporting</p>
                <div>
                  <label className="field-label flex items-center gap-2">
                    Reporting Officer
                    {dirtyFields.reporting_officer_id && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" title="Changed" />}
                  </label>
                  <SearchableCombobox
                    variant="form"
                    items={roCandidates}
                    value={changeForm.reporting_officer_id}
                    onChange={(val) => {
                      setChangeForm((f) => ({ ...f, reporting_officer_id: val }));
                      const picked = roCandidates.find((emp) => emp.staff_id === val);
                      setSelectedRoSnapshot(picked || null);
                    }}
                    onSearch={fetchRoCandidates}
                    loading={roCandidatesLoading}
                    loadingLabel="Searching staff..."
                    displayValue={selectedRoDisplay}
                    valueFn={(emp) => emp.staff_id}
                    displayFn={formatStaffPickerLabel}
                    renderItem={({ item, itemValue, isSelected, onSelect }) => (
                      <button key={itemValue} type="button" onClick={onSelect}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-red-50 ${isSelected ? 'bg-red-50 font-medium' : ''}`}>
                        <span>{formatStaffPickerLabel(item)}</span>
                        <span className="text-xs text-gray-400 font-mono ml-2">{item.staff_id}</span>
                      </button>
                    )}
                    clearLabel="No Reporting Officer"
                    placeholder="Search by name or ID..."
                    theme="admin"
                  />
                </div>
              </div>

              {/* ── Probation ────────────────────────── */}
              {showProbationSection && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Probation</p>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                      checked={!!changeForm.restart_probation}
                      onChange={(e) => setChangeForm((f) => ({
                        ...f,
                        restart_probation: e.target.checked,
                      }))}
                    />
                    <span className="text-sm text-gray-800">
                      Restart probation from the effective date
                      {dirtyFields.restart_probation && (
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 ml-2 align-middle" title="Changed" />
                      )}
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Hire / service start date stays the same. Leave accrual and the 3-month service gate are unaffected.
                      </span>
                    </span>
                  </label>
                  {changeForm.restart_probation && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="field-label">Probation Period (months)</label>
                        <input
                          type="number"
                          min="1"
                          max="24"
                          className="input-admin w-full"
                          value={changeForm.probation_period_months}
                          onChange={(e) => setChangeForm((f) => ({
                            ...f,
                            probation_period_months: e.target.value,
                          }))}
                        />
                      </div>
                      <div>
                        <label className="field-label">New Probation End</label>
                        <p className="field-static">
                          {computedProbationEnd
                            ? formatDate(toLocalISODate(computedProbationEnd))
                            : '—'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Employment Terms ─────────────────── */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Employment Terms</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="field-label flex items-center gap-2">
                      Employment Type
                      {dirtyFields.employment_type && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" title="Changed" />}
                    </label>
                    <FormSelect
                      options={[
                        { value: 'full_time', label: 'Full-Time' },
                        { value: 'part_time', label: 'Part-Time' },
                      ]}
                      value={changeForm.employment_type}
                      onChange={(val) => setChangeForm(f => ({ ...f, employment_type: val }))}
                      theme="admin"
                    />
                  </div>
                  <div>
                    <label className="field-label flex items-center gap-2">
                      Employment Status
                      {dirtyFields.employment_status && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" title="Changed" />}
                    </label>
                    <FormSelect
                      options={[
                        { value: 'active', label: 'Active' },
                        { value: 'inactive', label: 'Inactive' },
                        { value: 'probation', label: 'On Probation' },
                        { value: 'notice', label: 'Serving Notice' },
                      ]}
                      value={changeForm.employment_status}
                      onChange={(val) => setChangeForm(f => ({ ...f, employment_status: val }))}
                      theme="admin"
                    />
                  </div>
                </div>
              </div>

              {/* ── Notes ────────────────────────────── */}
              <div>
                <label className="field-label">Reason</label>
                <textarea
                  className="textarea-admin w-full"
                  rows={2}
                  value={changeForm.change_reason}
                  onChange={e => setChangeForm(f => ({ ...f, change_reason: e.target.value }))}
                  placeholder="e.g. Annual promotion cycle, department restructuring..."
                />
              </div>
      </Modal>
    )}

    {/* ── Amend Probation Modal ───────────────────────────── */}
    {showAmendProbationModal && (
      <Modal
        isOpen
        onClose={() => setShowAmendProbationModal(false)}
        size="md"
        theme="admin"
        contentClassName="space-y-5"
        customHeader={(
          <div className="modal-header">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-red-600" />
              <h3 className="modal-title">Amend Probation</h3>
            </div>
            <button
              type="button"
              onClick={() => setShowAmendProbationModal(false)}
              className="modal-close-admin"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        footer={(
          <>
            <button
              type="button"
              onClick={() => setShowAmendProbationModal(false)}
              className="btn btn-admin-secondary"
              disabled={amendProbationSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAmendProbationSave}
              disabled={
                amendProbationSaving
                || !amendProbationForm.new_probation_end_date
                || (amendProbationForm.reason || '').trim().length < 5
              }
              className="btn btn-admin-primary"
            >
              {amendProbationSaving ? 'Saving...' : 'Save Amendment'}
            </button>
          </>
        )}
      >
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Probation Start</label>
              <p className="field-static">
                {probationStartDate ? formatDate(probationStartDate) : '—'}
              </p>
              {!employee?.probation_start_date && employee?.employment_start_date && (
                <p className="form-help">Falls back to employment start</p>
              )}
            </div>
            <div>
              <label className="form-label">Current End</label>
              <p className="field-static">
                {hrProfile?.hrprofile_probation_end_date
                  ? formatDate(hrProfile.hrprofile_probation_end_date)
                  : 'Not set'}
              </p>
            </div>
            <div>
              <label className="form-label">Current Period</label>
              <p className="field-static">
                {employee?.probation_period_months || 3} months
              </p>
            </div>
          </div>
        </div>

        <div>
          <label className="form-label">Amendment Type</label>
          <PillToggle
            value={amendProbationForm.amendment_type}
            onChange={(val) => setAmendProbationForm((f) => ({ ...f, amendment_type: val }))}
            options={[
              { id: 'extension', label: 'Extension' },
              { id: 'correction', label: 'Correction' },
            ]}
            variant="text"
            theme="admin"
            fullWidth
            ariaLabel="Amendment type"
          />
          <p className="form-help">
            {amendProbationForm.amendment_type === 'correction'
              ? 'Use Correction for a wrong date at hire/import — does not mark the record as extended.'
              : 'Use Extension when the probation period is being lengthened for performance or other reasons.'}
          </p>
        </div>

        <div>
          <label className="form-label">
            New Probation End Date <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {[1, 2, 3].map((months) => {
              const start = parseLocalDate(probationStartDate);
              if (!start) return null;
              // Presets extend from the current end when set, else start + current period.
              const baseEnd = hrProfile?.hrprofile_probation_end_date
                ? parseLocalDate(hrProfile.hrprofile_probation_end_date)
                : addMonths(start, employee?.probation_period_months || 3);
              if (!baseEnd) return null;
              const presetIso = toLocalISODate(addMonths(baseEnd, months));
              return (
                <button
                  key={months}
                  type="button"
                  className="btn btn-sm btn-admin-secondary"
                  onClick={() => {
                    setAmendProbationForm((f) => ({
                      ...f,
                      new_probation_end_date: presetIso,
                      amendment_type: f.amendment_type === 'correction' ? 'extension' : f.amendment_type,
                    }));
                  }}
                >
                  +{months} month{months > 1 ? 's' : ''}
                </button>
              );
            })}
          </div>
          <DatePickerField
            theme="admin"
            selected={parseLocalDate(amendProbationForm.new_probation_end_date)}
            onChange={(date) => {
              const iso = date ? toLocalISODate(date) : '';
              const currentEnd = hrProfile?.hrprofile_probation_end_date;
              setAmendProbationForm((f) => ({
                ...f,
                new_probation_end_date: iso,
                amendment_type:
                  iso && currentEnd && iso > currentEnd && f.amendment_type === 'correction'
                    ? 'extension'
                    : f.amendment_type,
              }));
            }}
            minDate={parseLocalDate(probationStartDate)}
            placeholderText="Select new end date"
          />
          {amendProbationDelta && amendProbationForm.new_probation_end_date && (
            <p className="text-sm text-gray-700 mt-2">
              {amendProbationDelta.daysDelta != null && amendProbationDelta.daysDelta !== 0 && (
                <>
                  {amendProbationDelta.daysDelta > 0 ? 'Extends' : 'Shortens'} probation by{' '}
                  {Math.abs(amendProbationDelta.daysDelta)}{' '}
                  {pluralizeDays(amendProbationDelta.daysDelta)}
                  {' · '}
                </>
              )}
              new end {formatDate(amendProbationForm.new_probation_end_date)}
              {amendProbationDelta.totalMonths != null && (
                <> · {amendProbationDelta.totalMonths} months total</>
              )}
            </p>
          )}
        </div>

        <div>
          <label className="form-label">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            className="textarea-admin w-full"
            rows={3}
            value={amendProbationForm.reason}
            onChange={(e) => setAmendProbationForm((f) => ({ ...f, reason: e.target.value }))}
            placeholder="e.g. Performance review — extend 3 months pending improvement plan"
          />
          {(amendProbationForm.reason || '').trim().length > 0
            && (amendProbationForm.reason || '').trim().length < 5 ? (
            <p className="text-xs text-red-600 mt-1">Reason must be at least 5 characters.</p>
          ) : (
            <p className="form-help">Visible to HR on this record and in the Profile Change Log.</p>
          )}
        </div>

        {(amendProbationDelta?.notOnProbation
          || amendProbationDelta?.isPast
          || amendProbationDelta?.beyondTwelveMonths
          || amendProbationDelta?.pastEmploymentEnd) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-amber-900">Please review before saving</p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Extending probation does not re-block dental or leave gates — confirmation and the
                  3-month service clock are separate. The employee will see the new end date on their Profile.
                </p>
              </div>
            </div>
            <ul className="text-xs text-amber-800 list-disc pl-5 space-y-1">
              {amendProbationDelta.notOnProbation && (
                <li>Employment status is not currently On Probation — this looks like a back-dated correction.</li>
              )}
              {amendProbationDelta.isPast && (
                <li>The new end date is in the past.</li>
              )}
              {amendProbationDelta.beyondTwelveMonths && (
                <li>The new end is more than 12 months after probation start.</li>
              )}
              {amendProbationDelta.pastEmploymentEnd && (
                <li>The new end is after the employment end date — save will be rejected.</li>
              )}
            </ul>
          </div>
        )}
      </Modal>
    )}
  </div>
  );
};

// Employee Family & Dependents Tab Component - HR View
const EmployeeFamilyTab = ({ employee, hrProfile, onUploadFamilyDocument, embedded = false, onCountChange }) => {
  const [dependents, setDependents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingDependent, setIsAddingDependent] = useState(false);
  const [editingDependent, setEditingDependent] = useState(null);
  const emptyForm = {
    dependent_full_name: '',
    dependent_date_of_birth: '',
    relationship_type: '',
    dependent_citizenship_status: '',
    dependent_nric_fin: '',
    dependent_passport_number: '',
    dependent_birth_certificate_number: '',
    dependent_school_name: '',
    dependent_school_level: '',
    dependent_medical_conditions: '',
    dependent_special_needs: false,
    is_financially_dependent: true,
    dependency_start_date: '',
    adoption_certificate_number: '',
    formal_intent_to_adopt_date: '',
    employee_consent_obtained: false,
    dependent_consent_obtained: false,
  };
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const confirmCallbackRef = useRef(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const showConfirm = (config) => {
    return new Promise((resolve) => {
      confirmCallbackRef.current = resolve;
      setConfirmDialog(config);
    });
  };

  const relationshipTypes = [
    { value: 'biological_child', label: 'Biological Child' },
    { value: 'adopted_child', label: 'Adopted Child' },
    { value: 'step_child', label: 'Step Child' },
    { value: 'foster_child', label: 'Foster Child' },
    { value: 'legal_guardian', label: 'Legal Guardianship' },
    { value: 'spouse', label: 'Spouse' },
    { value: 'parent', label: 'Parent' },
    { value: 'sibling', label: 'Sibling' },
    { value: 'other', label: 'Other Dependent' }
  ];

  const citizenshipStatuses = [
    { value: 'yes', label: 'Singapore Citizen' },
    { value: 'no', label: 'Not Singapore Citizen' }
  ];

  const schoolLevels = [
    { value: 'not_schooling', label: 'Not Schooling' },
    { value: 'kindergarten', label: 'Kindergarten' },
    { value: 'primary_school', label: 'Primary School' },
    { value: 'secondary_school', label: 'Secondary School' },
    { value: 'junior_college', label: 'Junior College' },
    { value: 'polytechnic', label: 'Polytechnic' },
    { value: 'university', label: 'University' },
    { value: 'graduated', label: 'Graduated' }
  ];

  useEffect(() => {
    if (employee?.staff_id) {
      fetchFamilyData();
    }
  }, [employee]);

  useEffect(() => {
    if (typeof onCountChange === 'function' && !loading) {
      onCountChange(dependents.length);
    }
  }, [dependents.length, loading, onCountChange]);

  const fetchFamilyData = async () => {
    try {
      setLoading(true);
      const dependentsResponse = await confidentialAPI.getEmployeeDependents({
        employee_staff_id: employee.staff_id,
      });
      const dependentsData = dependentsResponse.data?.results || dependentsResponse.data || [];
      setDependents(dependentsData);
    } catch (error) {
      console.error('Error fetching family data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const isChild = (dependent) => {
    return ['biological_child', 'adopted_child', 'step_child', 'foster_child'].includes(dependent.relationship_type);
  };

  const getYoungestChild = () => {
    const children = dependents.filter(dep => isChild(dep) && dep.dependent_date_of_birth);
    if (children.length === 0) return null;

    const sorted = [...children].sort((a, b) => {
      const dateA = new Date(a.dependent_date_of_birth);
      const dateB = new Date(b.dependent_date_of_birth);
      return dateB - dateA;
    });

    return sorted[0];
  };

  const resetForm = () => {
    const hasPrivacyPolicyConsent = hrProfile?.hrprofile_consent_obtained === true;
    setFormData({
      ...emptyForm,
      employee_consent_obtained: hasPrivacyPolicyConsent,
    });
    setFormErrors({});
    setIsAddingDependent(false);
    setEditingDependent(null);
  };

  const openManualAddForm = () => {
    const hasPrivacyPolicyConsent = hrProfile?.hrprofile_consent_obtained === true;
    setEditingDependent(null);
    setFormData({
      ...emptyForm,
      employee_consent_obtained: hasPrivacyPolicyConsent,
    });
    setFormErrors({});
    setIsAddingDependent(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.dependent_full_name.trim()) {
      errors.dependent_full_name = 'Full name is required';
    }
    if (!formData.dependent_date_of_birth) {
      errors.dependent_date_of_birth = 'Date of birth is required';
    }
    if (!formData.relationship_type) {
      errors.relationship_type = 'Relationship is required';
    }
    if (!formData.dependent_citizenship_status) {
      errors.dependent_citizenship_status = 'Citizenship status is required';
    }
    const hasPrivacyPolicyConsent = hrProfile?.hrprofile_consent_obtained === true;
    if (!formData.employee_consent_obtained && !hasPrivacyPolicyConsent) {
      errors.employee_consent_obtained = 'Employee consent is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveDependent = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      const payload = {
        ...formData,
        employee_staff_id: employee.staff_id,
      };
      if (editingDependent?.id) {
        await confidentialAPI.updateEmployeeDependent(editingDependent.id, payload);
        toast.success('Dependent updated');
      } else {
        await confidentialAPI.createEmployeeDependent(payload);
        toast.success('Dependent added');
      }
      resetForm();
      await fetchFamilyData();
    } catch (error) {
      console.error('Error saving dependent:', error);
      const apiErrors = error?.response?.data;
      if (apiErrors && typeof apiErrors === 'object') {
        const next = {};
        Object.entries(apiErrors).forEach(([key, val]) => {
          next[key] = Array.isArray(val) ? val[0] : String(val);
        });
        setFormErrors(next);
      }
      toast.error('Failed to save dependent');
    } finally {
      setSaving(false);
    }
  };

  const hasDependents = Array.isArray(dependents) && dependents.length > 0;
  const canUploadDocuments = employee?.employment_classification !== 'outsourced'
    && typeof onUploadFamilyDocument === 'function';
  const responsivePrimaryCta =
    'btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Loading family data...</span>
      </div>
    );
  }

  const actionButtons = hasDependents && !isAddingDependent ? (
    <div className="flex items-center gap-1 flex-shrink-0">
      <button
        type="button"
        onClick={openManualAddForm}
        className={canUploadDocuments ? 'icon-button-admin' : responsivePrimaryCta}
        title="Add without document"
        aria-label="Add without document"
      >
        <UserPlus className="w-4 h-4" />
        {!canUploadDocuments && <span className="hidden sm:inline">Add</span>}
      </button>
      {canUploadDocuments && (
        <button
          type="button"
          onClick={onUploadFamilyDocument}
          className={responsivePrimaryCta}
          aria-label="Upload family document"
          title="Upload family document"
        >
          <Upload className="w-4 h-4" />
          <span className="hidden sm:inline">Upload</span>
        </button>
      )}
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      {/* Section header — Accordion owns title when embedded */}
      {embedded ? (
        actionButtons && (
          <div className="flex items-center justify-end gap-2">
            {actionButtons}
          </div>
        )
      ) : (
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Heart className="w-5 h-5 text-red-600 flex-shrink-0" />
            <h3 className="text-base font-semibold text-gray-900 truncate">Family & Dependents</h3>
            {hasDependents && (
              <CountBadge count={dependents.length} theme="admin" size="md" className="flex-shrink-0" />
            )}
          </div>
          {actionButtons}
        </div>
      )}

      {/* Manual add / edit form (secondary path — no document) */}
      {isAddingDependent && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-1">
            {editingDependent ? 'Edit Dependent' : 'Add without document'}
          </h4>
          {!editingDependent && (
            <p className="text-xs text-gray-500 mb-3">
              Prefer uploading a birth or marriage certificate for leave-verified spouse/child records.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="field-label">Full Name <span className="text-red-500">*</span></label>
              <input
                name="dependent_full_name"
                className="input-admin"
                value={formData.dependent_full_name}
                onChange={handleFormChange}
                placeholder="Full name"
              />
              {formErrors.dependent_full_name && (
                <p className="text-xs text-red-600 mt-1">{formErrors.dependent_full_name}</p>
              )}
            </div>
            <div>
              <label className="field-label">Date of Birth <span className="text-red-500">*</span></label>
              <input
                type="date"
                name="dependent_date_of_birth"
                className="input-admin"
                value={formData.dependent_date_of_birth}
                onChange={handleFormChange}
              />
              {formErrors.dependent_date_of_birth && (
                <p className="text-xs text-red-600 mt-1">{formErrors.dependent_date_of_birth}</p>
              )}
            </div>
            <div>
              <label className="field-label">Relationship <span className="text-red-500">*</span></label>
              <FormSelect
                options={[{ value: '', label: 'Select relationship' }, ...relationshipTypes]}
                value={formData.relationship_type}
                onChange={(val) => setFormData((prev) => ({ ...prev, relationship_type: val }))}
              />
              {formErrors.relationship_type && (
                <p className="text-xs text-red-600 mt-1">{formErrors.relationship_type}</p>
              )}
            </div>
            <div>
              <label className="field-label">Citizenship <span className="text-red-500">*</span></label>
              <FormSelect
                options={[{ value: '', label: 'Select citizenship' }, ...citizenshipStatuses]}
                value={formData.dependent_citizenship_status}
                onChange={(val) => setFormData((prev) => ({ ...prev, dependent_citizenship_status: val }))}
              />
              {formErrors.dependent_citizenship_status && (
                <p className="text-xs text-red-600 mt-1">{formErrors.dependent_citizenship_status}</p>
              )}
            </div>
            <div>
              <label className="field-label">NRIC/FIN</label>
              <input
                name="dependent_nric_fin"
                className="input-admin"
                value={formData.dependent_nric_fin}
                onChange={handleFormChange}
                placeholder="Optional"
              />
              {formErrors.dependent_nric_fin && (
                <p className="text-xs text-red-600 mt-1">{formErrors.dependent_nric_fin}</p>
              )}
            </div>
            <div>
              <label className="field-label">Passport Number</label>
              <input
                name="dependent_passport_number"
                className="input-admin"
                value={formData.dependent_passport_number}
                onChange={handleFormChange}
                placeholder="Optional"
              />
              {formErrors.dependent_passport_number && (
                <p className="text-xs text-red-600 mt-1">{formErrors.dependent_passport_number}</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="employee_consent_obtained"
                  checked={formData.employee_consent_obtained}
                  onChange={handleFormChange}
                  className="w-5 h-5 mt-0.5 text-red-600 border-gray-300 rounded focus-visible:ring-red-500"
                />
                <span className="text-sm text-gray-700">
                  Employee consent obtained to store this dependent&apos;s information
                  {hrProfile?.hrprofile_consent_obtained && formData.employee_consent_obtained && (
                    <span className="text-xs text-green-600 ml-1">(via Privacy Policy)</span>
                  )}
                </span>
              </label>
              {formErrors.employee_consent_obtained && (
                <p className="text-xs text-red-600 mt-1">{formErrors.employee_consent_obtained}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={resetForm} className="btn btn-outline" disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveDependent}
              disabled={saving}
              className="btn btn-admin-primary"
            >
              {saving ? 'Saving...' : (editingDependent ? 'Update' : 'Save Dependent')}
            </button>
          </div>
        </div>
      )}

      {/* Dependents List */}
      <div className="space-y-3">
        {!hasDependents && !isAddingDependent ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No dependents recorded</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              {canUploadDocuments
                ? 'Upload a birth or marriage certificate to create a verified family record — or add without a document if needed.'
                : 'No dependents recorded for this employee yet.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-4">
              {canUploadDocuments && (
                <button
                  type="button"
                  onClick={onUploadFamilyDocument}
                  className="btn btn-admin-primary"
                >
                  <Upload className="w-4 h-4" />
                  Upload family document
                </button>
              )}
              <button
                type="button"
                onClick={openManualAddForm}
                className={canUploadDocuments ? 'btn btn-outline' : 'btn btn-admin-primary'}
              >
                <UserPlus className="w-4 h-4" />
                {canUploadDocuments ? 'Add without document' : 'Add Dependent'}
              </button>
            </div>
          </div>
        ) : !hasDependents && isAddingDependent ? null : (
          (() => {
            const youngestChild = getYoungestChild();
            return dependents.map((dependent) => {
              const age = calculateAge(dependent.dependent_date_of_birth);
              const dependentKey = `dependent_${dependent.id}`;
              const isYoungest = youngestChild && dependent.id === youngestChild.id;

              return (
                <div key={dependent.id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {dependent.dependent_full_name}
                        </h4>
                        <InfoBadge color="blue" label={relationshipTypes.find(r => r.value === dependent.relationship_type)?.label || dependent.relationship_type} />
                        {isYoungest && (
                          <InfoBadge color="green" label="Childcare" />
                        )}
                        {dependent.dependent_special_needs && (
                          <InfoBadge color="purple" label="Special Needs" />
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{age !== null ? `${age} years old` : 'Age unknown'}</span>
                          <span className="text-gray-400">({formatDate(dependent.dependent_date_of_birth)})</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Globe className="w-4 h-4 text-indigo-500" />
                          <span>{citizenshipStatuses.find(c => c.value === dependent.dependent_citizenship_status)?.label || dependent.dependent_citizenship_status}</span>
                        </div>

                        {dependent.dependent_nric_fin && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Shield className="w-4 h-4 text-gray-400" />
                            <span>NRIC: </span>
                            <SensitiveInlineToggle
                              value={dependent.dependent_nric_fin}
                              fieldName={`${dependentKey}_nric`}
                              type="nric"
                            />
                          </div>
                        )}

                        {dependent.dependent_school_name && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <GraduationCap className="w-4 h-4 text-orange-500" />
                            <span>{dependent.dependent_school_name}</span>
                            {dependent.dependent_school_level && (
                              <span className="text-gray-400">
                                ({schoolLevels.find(s => s.value === dependent.dependent_school_level)?.label})
                              </span>
                            )}
                          </div>
                        )}

                        {dependent.dependent_medical_conditions && (
                          <div className="md:col-span-3 flex items-start gap-2 text-gray-600">
                            <Heart className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                            <SensitiveInlineToggle
                              value={dependent.dependent_medical_conditions}
                              fieldName={`${dependentKey}_medical`}
                              type="medical"
                            />
                          </div>
                        )}
                      </div>

                    {/* Consent Status */}
                    <div className="mt-3 flex gap-3 text-xs">
                      <StatusBadge
                        status={dependent.employee_consent_obtained ? 'approved' : 'pending'}
                        label={`Employee Consent: ${dependent.employee_consent_obtained ? (dependent.consent_source === 'privacy_policy' ? 'Obtained (via Privacy Policy)' : 'Obtained') : 'Pending'}`}
                        showIcon
                      />
                      {dependent.dependent_consent_obtained && (
                        <StatusBadge status="approved" label="Dependent Consent: Obtained" showIcon />
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingDependent(dependent);
                        setFormData({
                          dependent_full_name: dependent.dependent_full_name || '',
                          dependent_date_of_birth: dependent.dependent_date_of_birth || '',
                          relationship_type: dependent.relationship_type || '',
                          dependent_citizenship_status: dependent.dependent_citizenship_status || '',
                          dependent_nric_fin: dependent.dependent_nric_fin || '',
                          dependent_passport_number: dependent.dependent_passport_number || '',
                          dependent_birth_certificate_number: dependent.dependent_birth_certificate_number || '',
                          dependent_school_name: dependent.dependent_school_name || '',
                          dependent_school_level: dependent.dependent_school_level || '',
                          dependent_medical_conditions: dependent.dependent_medical_conditions || '',
                          dependent_special_needs: dependent.dependent_special_needs || false,
                          is_financially_dependent: dependent.is_financially_dependent !== false,
                          dependency_start_date: dependent.dependency_start_date || '',
                          adoption_certificate_number: dependent.adoption_certificate_number || '',
                          formal_intent_to_adopt_date: dependent.formal_intent_to_adopt_date || '',
                          employee_consent_obtained: dependent.employee_consent_obtained || false,
                          dependent_consent_obtained: dependent.dependent_consent_obtained || false
                        });
                        setIsAddingDependent(true);
                      }}
                      className="icon-button-admin"
                      title="Edit dependent"
                      aria-label="Edit dependent"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const confirmed = await showConfirm({
                          title: 'Delete Dependent',
                          message: 'Are you sure you want to delete this dependent? This action cannot be undone.',
                          confirmLabel: 'Delete',
                          variant: 'danger',
                        });
                        if (confirmed) {
                          try {
                            await confidentialAPI.deleteEmployeeDependent(dependent.id);
                            toast.success('Dependent deleted');
                            await fetchFamilyData();
                          } catch (error) {
                            console.error('Error deleting dependent:', error);
                            toast.error('Failed to delete dependent');
                          }
                        }
                      }}
                      className="icon-button-danger"
                      title="Delete dependent"
                      aria-label="Delete dependent"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
            });
          })()
        )}
      </div>
      <ConfirmModal
        isOpen={!!confirmDialog}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || 'Confirm'}
        cancelLabel={confirmDialog?.cancelLabel || 'Cancel'}
        variant={confirmDialog?.variant || 'danger'}
        onConfirm={() => {
          confirmCallbackRef.current?.(true);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        onCancel={() => {
          confirmCallbackRef.current?.(false);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        theme="admin"
      />
    </div>
  );
};

// Employee Document Verification Tab Component
const EmployeeDocumentVerificationTab = ({ employee, hrProfile, activeTab }) => {
  const queryClient = useQueryClient();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [verificationModal, setVerificationModal] = useState({ open: false, documentApprovalId: null, documentType: null, initialOcrData: {}, viewMode: "edit" });
  const [nsVerificationModal, setNsVerificationModal] = useState({ open: false, documentApprovalId: null, leaveApplicationId: null });
  const [mcAdminVerificationModal, setMcAdminVerificationModal] = useState({ open: false, applicationId: null, leaveTypeName: '' });
  const [profileDocViewer, setProfileDocViewer] = useState({ open: false, document: null });

  const confirmCallbackRef = useRef(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const showConfirm = (config) => {
    return new Promise((resolve) => {
      confirmCallbackRef.current = resolve;
      setConfirmDialog(config);
    });
  };
  
  // Memoize config object to prevent unnecessary re-renders in useNestedTabPersistence
  const nestedTabConfig = useMemo(() => ({
    subTab: {
      default: 'verification',
      valid: ['verification', 'upload', 'letters'],
      param: 'subTab'
    },
    category: {
      default: 'all',
      valid: ['all', 'medical', 'banking', 'identity', 'family', 'education', 'professional', 'other'],
      param: 'category'
    }
  }), []);
  
  // Use nested tab persistence for sub-tabs and category filter
  const { subTab: subTabState, category: categoryState } = useNestedTabPersistence(
    'admin-employees-documents',
    activeTab === 'documents' ? activeTab : null,
    nestedTabConfig
  );
  const [subTab, setSubTab] = subTabState;
  const [selectedCategory, setSelectedCategory] = categoryState;
  const [letters, setLetters] = useState([]);
  const [lettersLoading, setLettersLoading] = useState(true);
  const [preview, setPreview] = useState({ open: false, html: '' });
  const [uploadingFiles, setUploadingFiles] = useState(new Set());

  const getDocumentTypeIcon = (docType) => {
    const map = {
      'id_card_front': { icon: IdCard, color: 'text-green-600', bg: 'bg-green-100' },
      'id_card_back': { icon: IdCard, color: 'text-emerald-600', bg: 'bg-emerald-100' },
      'passport': { icon: Globe, color: 'text-blue-600', bg: 'bg-blue-100' },
      'birth_certificate': { icon: Baby, color: 'text-green-600', bg: 'bg-green-100' },
      'marriage_certificate': { icon: Heart, color: 'text-pink-600', bg: 'bg-pink-100' },
      'education_certificate': { icon: GraduationCap, color: 'text-purple-600', bg: 'bg-purple-100' },
      'professional_certificate': { icon: Award, color: 'text-green-600', bg: 'bg-green-100' },
      'medical_certificate': { icon: HeartPulse, color: 'text-red-600', bg: 'bg-red-100' },
      'bank_statement': { icon: Receipt, color: 'text-emerald-600', bg: 'bg-emerald-100' },
      'ns_call_up': { icon: Shield, color: 'text-amber-600', bg: 'bg-amber-100' },
      'death_certificate': { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
      'adoption_papers': { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
      'flight_ticket': { icon: Plane, color: 'text-sky-600', bg: 'bg-sky-100' },
      'other_document': { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
    };
    return map[docType] || { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100' };
  };

  // Document categories with leave trigger information
  const documentCategories = {
    'family': {
      name: 'Family Documents',
      icon: Users,
      triggers: ['birth_certificate', 'marriage_certificate', 'death_certificate', 'adoption_papers'],
      leaveTypes: ['Maternity Leave', 'Paternity Leave', 'Childcare Leave', 'Marriage Leave', 'Compassionate Leave']
    },
    'medical': {
      name: 'Medical Documents',
      icon: HeartPulse,
      triggers: ['medical_certificate'],
      leaveTypes: ['Medical Leave', 'Hospitalisation Leave']
    },
    'identity': {
      name: 'Identity Documents',
      icon: Fingerprint,
      triggers: [],
      leaveTypes: []
    },
    'education': {
      name: 'Education Certificates',
      icon: GraduationCap,
      triggers: [],
      leaveTypes: []
    },
    'professional': {
      name: 'Professional Certificates',
      icon: Award,
      triggers: [],
      leaveTypes: []
    },
    'banking': {
      name: 'Banking Documents',
      icon: Landmark,
      triggers: ['bank_statement'],
      leaveTypes: []
    },
    'travel': {
      name: 'Travel Documents',
      icon: Plane,
      triggers: ['flight_ticket'],
      leaveTypes: ['Home Leave']
    },
    'other': {
      name: 'Other Documents',
      icon: FileText,
      triggers: [],
      leaveTypes: []
    }
  };

  useEffect(() => {
    if (employee?.staff_id) {
      fetchDocuments();
      fetchLetters();
    }
  }, [employee]);

  const fetchLetters = async () => {
    try {
      setLettersLoading(true);
      const res = await confidentialAPI.getHRLetters({ staff_id: employee.staff_id, ordering: '-generated_at' });
      const data = res.data?.results || res.data || [];
      setLetters(data);
    } catch (e) {
      console.error('Failed to fetch letters', e);
    } finally {
      setLettersLoading(false);
    }
  };

  const doPreview = async (id) => {
    try {
      const res = await confidentialAPI.previewHRLetter(id);
      setPreview({ open: true, html: res.data?.html || '' });
    } catch (e) {
      console.error('Preview failed', e);
    }
  };

  const doDownload = async (id) => {
    try {
      const res = await confidentialAPI.downloadHRLetter(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hr_letter_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      console.error('Download failed', e);
    }
  };

  const doRegenerate = async (id) => {
    try {
      setActionLoading((s) => ({ ...s, [id]: true }));
      await confidentialAPI.regenerateHRLetter(id);
      await fetchLetters();
    } catch (e) {
      console.error('Regenerate failed', e);
    } finally {
      setActionLoading((s) => ({ ...s, [id]: false }));
    }
  };

  // Map document_type -> display name
  const docTypeDisplayNames = {
    'birth_certificate': 'Birth Certificate',
    'interim_birth_notification': 'Interim Birth Notification',
    'adoption_papers': 'Adoption Papers',
    'marriage_certificate': 'Marriage Certificate',
    'death_certificate': 'Death Certificate',
    'id_card_front': 'NRIC / FIN (Front)',
    'id_card_back': 'NRIC / FIN (Back)',
    'education_certificate': 'Education Certificate',
    'professional_certificate': 'Professional Certificate',
    'ns_call_up': 'NS Call-up Order',
    'medical_certificate': 'Medical Certificate',
    'passport': 'Passport',
    'bank_statement': 'Bank Statement',
    'flight_ticket': 'Flight Ticket',
    'driving_license': 'Driving Licence',
    'other_document': 'Other Document'
  };

  // Map document_type -> category key (matches documentCategories)
  const docTypeCategoryMap = {
    'birth_certificate': 'family',
    'interim_birth_notification': 'family',
    'adoption_papers': 'family',
    'marriage_certificate': 'family',
    'death_certificate': 'family',
    'id_card_front': 'identity',
    'id_card_back': 'identity',
    'education_certificate': 'education',
    'professional_certificate': 'professional',
    'ns_call_up': 'identity',
    'medical_certificate': 'medical',
    'passport': 'identity',
    'bank_statement': 'banking',
    'flight_ticket': 'travel',
    'driving_license': 'identity',
    'other_document': 'other'
  };

  // Leave-triggering document types
  const leaveTriggering = new Set(['birth_certificate', 'marriage_certificate', 'interim_birth_notification']);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const staffId = employee?.staff_id;

      // Use the unified endpoint to get ALL document types in one call
      const res = await api.get('/document-approvals/all/', { params: { staff_id: staffId } });
      const allDocs = res?.data?.all || [];

      const mapped = allDocs.map((doc) => ({
        id: doc.id,
        name: doc.document_info?.filename || docTypeDisplayNames[doc.document_type] || doc.document_type,
        displayName: (() => {
          if (doc.document_type === 'education_certificate' && doc.ocr_summary?.qualification_name) {
            return doc.ocr_summary.qualification_name;
          }
          return docTypeDisplayNames[doc.document_type] || doc.document_type?.replace(/_/g, ' ');
        })(),
        certShortName: doc.document_type === 'professional_certificate'
          ? (doc.ocr_summary?.cert_short_name || null)
          : null,
        category: docTypeCategoryMap[doc.document_type] || 'identity',
        approvalType: doc.document_type,
        uploadedAt: doc.document_info?.uploaded_at || doc.submitted_at,
        status: mapHrDocumentListStatus(doc.status, doc.document_type),
        rawStatus: doc.status,
        documentApprovalId: typeof doc.id === 'number' ? doc.id : null,
        documentType: doc.document_type,
        isProfileSourced: doc.source === 'profile_record',
        profileRecordId: doc.profile_record_id || null,
        profileRecordType: doc.profile_record_type || null,
        hasDocumentFile: Boolean(
          doc.has_document_file
          || (doc.document_info?.file_path || '').trim()
        ),
        filePath: (doc.document_info?.file_path || '').trim() || null,
        // Legacy boolean flags for backward compat
        isChildCertificate: doc.document_type === 'birth_certificate',
        isMarriageCertificate: doc.document_type === 'marriage_certificate',
        verifiedBy: doc.approved_by_staff_id || null,
        verifiedByName: doc.approved_by_name || null,
        verifiedAt: doc.approved_at || null,
        submittedBy: doc.submitted_by_staff_id || null,
        notes: doc.notes || '',
        rejectionReason: doc.rejection_reason || '',
        triggeredLeaveTypes: leaveTriggering.has(doc.document_type) ? [doc.document_type] : [],
        ocrSummary: doc.ocr_summary || {},
        leaveApplicationId: doc.leave_application_id || null,
        leaveTypeName: doc.leave_type_name || null,
      }));

      setDocuments(keepLatestIdCardPerSide(mapped));
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentAction = async (documentId, action, reason = '') => {
    try {
      setActionLoading(prev => ({ ...prev, [documentId]: true }));
      // Placeholder for non-special docs; child/marriage certs use the review modal below
      const response = await api.post(`/documents/${documentId}/${action}/`, {
        reason: reason,
        verified_by: 'Current HR User'
      });
      setDocuments(prev => prev.map(doc => 
        doc.id === documentId 
          ? { 
              ...doc, 
              status: action === 'approve' ? 'approved' : 'rejected',
              verifiedBy: 'Current HR User',
              verifiedAt: new Date().toISOString(),
              rejectionReason: action === 'reject' ? reason : undefined
            }
          : doc
      ));
      alert(`Document ${action}d successfully!`);
    } catch (error) {
      console.error(`Error ${action}ing document:`, error);
      alert(`Error ${action}ing document. Please try again.`);
    } finally {
      setActionLoading(prev => ({ ...prev, [documentId]: false }));
    }
  };

  const handleOpenVerificationModal = (document, documentType = null, viewMode = 'edit') => {
    if (document.documentApprovalId) {
      const resolvedType = documentType || document.documentType || document.approvalType
        || (document.isMarriageCertificate ? 'marriage_certificate' : 'birth_certificate');

      if (resolvedType === 'other_document') {
        handleViewProfileDocument(document);
        return;
      }
      
      // Route NS Call-up documents to the dedicated NS verification modal
      if (resolvedType === 'ns_call_up') {
        setNsVerificationModal({
          open: true,
          documentApprovalId: document.documentApprovalId,
          leaveApplicationId: document.leaveApplicationId || null
        });
        return;
      }
      
      setVerificationModal({
        open: true,
        documentApprovalId: document.documentApprovalId,
        documentType: resolvedType,
        initialOcrData: {},
        viewMode: viewMode,
        applicationId: document.leaveApplicationId || null
      });
    }
  };

  const handleDownloadDocument = async (document) => {
    try {
      setActionLoading(prev => ({ ...prev, [`dl_${document.id}`]: true }));

      let res;
      if (document.isProfileSourced && document.profileRecordId) {
        if (document.profileRecordType === 'professional_certificate') {
          res = await api.downloadCertificate(document.profileRecordId);
        } else if (document.profileRecordType === 'education') {
          res = await api.downloadEducation(document.profileRecordId);
        } else {
          throw new Error('Unsupported profile record type');
        }
      } else {
        res = await confidentialAPI.downloadDocumentApproval(document.documentApprovalId || document.id);
      }

      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.name || `document_${document.id}`;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Document downloaded');
    } catch (e) {
      console.error('Download failed', e);
      const detail = e?.response?.data?.error;
      toast.error(detail || 'Failed to download document');
    } finally {
      setActionLoading(prev => ({ ...prev, [`dl_${document.id}`]: false }));
    }
  };

  const handleViewProfileDocument = (document) => {
    if (!document?.hasDocumentFile || !document?.filePath) {
      toast.info('No scan on file — upload the certificate under Documents → Upload to attach a copy.');
      return;
    }
    setProfileDocViewer({ open: true, document });
  };

  const handleVerificationComplete = (result) => {
    const verifiedDocumentType = verificationModal.documentType;
    // Refresh documents list after verification
    fetchDocuments();
    setVerificationModal({ open: false, documentApprovalId: null, documentType: null, initialOcrData: {}, viewMode: 'edit', applicationId: null });
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ['pendingDocApprovalsSummary'] });
      if (['education_certificate', 'professional_certificate'].includes(verifiedDocumentType)) {
        // Backend signal refreshes classification eligibility; invalidate the whole
        // Skills Upgrading tree so missing-certs / pathways / levy queue refetch.
        queryClient.invalidateQueries({ queryKey: ['skills-upgrading'] });
        // Professional tab cert list (timeline) must also refresh immediately.
        queryClient.invalidateQueries({ queryKey: ['timeline', 'certificates'] });
        queryClient.invalidateQueries({ queryKey: ['timeline', 'education'] });
      }
      toast.success(result.message);
    }
  };

  const buildDeleteConfirmMessage = (document) => {
    const label =
      document.ocrSummary?.cert_name
      || document.ocrSummary?.qualification_name
      || document.displayName
      || document.name
      || 'this document';
    const isLeaveEvidence = [
      'medical_certificate',
      'ns_call_up',
      'flight_ticket',
      'death_certificate',
    ].includes(document.documentType);
    const isFamilyCert = ['birth_certificate', 'marriage_certificate'].includes(
      document.documentType
    );
    const parts = [
      `Permanently delete "${label}" from this employee's file?`,
      'This cannot be undone.',
    ];
    if (document.isProfileSourced && !document.hasDocumentFile) {
      parts.push('This is a record-only credential (no scan on file).');
    }
    if (['education_certificate', 'professional_certificate'].includes(document.documentType)) {
      parts.push('Skills Upgrading eligibility may update after removal.');
    }
    if (isFamilyCert) {
      parts.push(
        'Dependents or leave entitlements created from this certificate are not removed automatically — review Family / Leave if needed.'
      );
    }
    if (isLeaveEvidence && document.leaveApplicationId) {
      parts.push(
        'This document is linked to a leave application. Deletion will be blocked until the leave is amended or cancelled.'
      );
    }
    return parts.join(' ');
  };

  const handleDeleteDocument = async (document) => {
    const confirmed = await showConfirm({
      title: 'Delete Document',
      message: buildDeleteConfirmMessage(document),
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    const loadingKey = `del_${document.id}`;
    setActionLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const payload = { staff_id: employee.staff_id };
      if (document.isProfileSourced && document.profileRecordId) {
        payload.profile_record_type =
          document.profileRecordType
          || (document.documentType === 'education_certificate'
            ? 'education'
            : 'professional_certificate');
        payload.profile_record_id = document.profileRecordId;
      } else if (document.documentApprovalId) {
        payload.document_approval_id = document.documentApprovalId;
      } else {
        toast.error('Cannot delete this document — no system record id found.');
        return;
      }

      await api.hrDeleteEmployeeDocument(payload);
      toast.success('Document deleted successfully');
      await fetchDocuments();
      if (['education_certificate', 'professional_certificate'].includes(document.documentType)) {
        queryClient.invalidateQueries({ queryKey: ['skills-upgrading'] });
        queryClient.invalidateQueries({ queryKey: ['timeline', 'certificates'] });
        queryClient.invalidateQueries({ queryKey: ['timeline', 'education'] });
      }
    } catch (error) {
      console.error('HR document delete failed', error);
      toast.error(
        error?.response?.data?.error || 'Failed to delete document. Please try again.'
      );
    } finally {
      setActionLoading((prev) => {
        const next = { ...prev };
        delete next[loadingKey];
        return next;
      });
    }
  };

  // Color helpers (matching Profile.js dropzone pattern)
  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      amber: 'bg-amber-50 border-amber-200 text-amber-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      gray: 'bg-gray-50 border-gray-200 text-gray-800'
    };
    return colors[color] || colors.gray;
  };

  const getIconColorClass = (color) => {
    const colors = {
      blue: 'text-blue-600',
      purple: 'text-purple-600',
      green: 'text-green-600',
      red: 'text-red-600',
      pink: 'text-pink-600',
      amber: 'text-amber-600',
      orange: 'text-orange-600',
      indigo: 'text-indigo-600',
      teal: 'text-teal-600',
      gray: 'text-gray-600'
    };
    return colors[color] || colors.gray;
  };

  // Admin upload categories — each type gets its own dropzone card.
  // Ordered by how often HR actually uploads each doc, most frequent first:
  //   1) Recurring sick-leave MCs (uploaded constantly)
  //   2) Career certs — professional/safety licences renew & get added often
  //   3) Life-event / leave-triggering docs (birth, marriage, NS)
  //   4) One-time onboarding identity & banking docs (rarely re-uploaded)
  const adminUploadCategories = [
    {
      id: 'mc_outpatient',
      name: 'Outpatient Sick Leave',
      description: 'Upload medical certificate on behalf of employee for outpatient sick leave',
      icon: Thermometer,
      color: 'orange',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'mc_outpatient',
      leaveTypes: ['Outpatient Sick Leave']
    },
    {
      id: 'mc_hospitalisation',
      name: 'Hospitalisation Leave',
      description: 'Upload medical certificate on behalf of employee for hospitalisation leave',
      icon: Hospital,
      color: 'red',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'mc_hospitalisation',
      leaveTypes: ['Hospitalisation Leave']
    },
    {
      id: 'professional_certificate',
      name: 'Professional Certificate',
      description: 'Upload professional certifications or licenses',
      icon: Award,
      color: 'green',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'professional_certificate'
    },
    {
      id: 'education_certificate',
      name: 'Education Certificate',
      description: 'Upload degree, diploma, or other education certificates',
      icon: GraduationCap,
      color: 'purple',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'education_certificate'
    },
    {
      id: 'child_certificate',
      name: 'Birth Certificate',
      description: 'Upload child\'s birth certificate for leave eligibility',
      icon: Baby,
      color: 'pink',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'child_certificate',
      leaveTypes: ['Childcare Leave', 'Paternity Leave', 'Maternity Leave', 'Shared Parental Leave']
    },
    {
      id: 'interim_birth_notification',
      name: 'Interim Birth Notification',
      description: "Birth certificate not ready yet? Upload a hospital discharge letter or maternity MC to provisionally unlock Paternity Leave & SPL.",
      icon: FileText,
      color: 'indigo',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'interim_birth_notification',
      leaveTypes: ['Paternity Leave', 'Shared Parental Leave'],
      interimBadge: true,
    },
    {
      id: 'marriage_certificate',
      name: 'Marriage Certificate',
      description: 'Upload marriage certificate for leave eligibility',
      icon: Heart,
      color: 'pink',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'marriage_certificate',
      leaveTypes: ['Marriage Leave', 'Paternity Leave', 'Shared Parental Leave']
    },
    {
      id: 'death_certificate',
      name: 'Compassionate Leave',
      description: "Upload the death certificate on behalf of an employee to support a compassionate leave claim.",
      icon: Heart,
      color: 'gray',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'death_certificate',
      leaveTypes: ['Compassionate Leave']
    },
    {
      id: 'ns_document',
      name: 'NS Call-up Order',
      description: 'Upload National Service call-up order for NS leave',
      icon: Shield,
      color: 'amber',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'ns_document',
      leaveTypes: ['NS Leave']
    },
    {
      id: 'bank_statement',
      name: 'Bank Statement',
      description: 'Upload bank statement for OCR extraction of bank details',
      icon: Receipt,
      color: 'green',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'bank_statement'
    },
    {
      id: 'id_card_front',
      name: 'NRIC / FIN (Front)',
      description: 'Upload the front of the employee\'s NRIC or FIN card',
      icon: IdCard,
      color: 'blue',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'id_card_front'
    },
    {
      id: 'id_card_back',
      name: 'NRIC / FIN (Back)',
      description: 'Upload the back of the employee\'s NRIC or FIN card',
      icon: IdCard,
      color: 'blue',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'id_card_back'
    },
    {
      id: 'passport',
      name: 'Passport',
      description: 'Upload passport for OCR extraction of identity details',
      icon: Globe,
      color: 'blue',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'passport'
    },
    {
      id: 'driving_license',
      name: 'Driving Licence',
      description: 'Upload the employee\'s Singapore driving licence for OCR extraction of all classes.',
      icon: Car,
      color: 'teal',
      accept: '.pdf,.jpg,.jpeg,.png',
      workflowType: 'driving_license'
    }
  ];

  const handleAdminFileUpload = async (files, workflowType) => {
    const file = files[0];
    if (!file) return;

    // File validation
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PDF, JPG, PNG.');
      return;
    }

    const targetStaffId = employee.staff_id;

    // Pre-upload duplicate detection: check for approved/active documents
    const typeMap = {
      'child_certificate': 'birth_certificate',
      'interim_birth_notification': 'interim_birth_notification',
      'marriage_certificate': 'marriage_certificate',
      'id_card_front': 'id_card_front',
      'id_card_back': 'id_card_back',
      'education_certificate': 'education_certificate',
      'professional_certificate': 'professional_certificate',
      'ns_document': 'ns_call_up',
      'bank_statement': 'bank_statement',
      'passport': 'passport'
    };
    const docTypeForCheck = typeMap[workflowType];
    const existingOfType = documents.filter(d => d.approvalType === docTypeForCheck);
    let confirmAdditionalChild = false;
    let confirmedAdditionalInstance = false;
    if (existingOfType.length > 0) {
      const approvedDocs = existingOfType.filter(d => d.status === 'approved');
      const pendingDocs = existingOfType.filter(d =>
        ['pending', 'pending_verification', 'ocr_completed', 'submitted', 'processing', 'processing_ocr'].includes(d.status)
      );

      if (pendingDocs.length > 0) {
        const unrestrictedMultiTypes = ['professional_certificate', 'education_certificate'];
        // Employees hold many professional/education certificates. A pending
        // review of one must not block HR from uploading another distinct file.
        // Backend still blocks exact-file (hash) duplicates.
        if (unrestrictedMultiTypes.includes(workflowType)) {
          // No confirmation needed for inherently multi-instance qualifications.
        } else if (workflowType === 'child_certificate') {
          const confirmed = await showConfirm({
            title: 'Additional Birth Certificate',
            message: `This employee already has ${pendingDocs.length} birth certificate(s) awaiting HR verification. Is this certificate for a different child?`,
            confirmLabel: 'Yes, Different Child',
            cancelLabel: 'Cancel',
            variant: 'primary',
          });
          if (!confirmed) return;
          confirmAdditionalChild = true;
          confirmedAdditionalInstance = true;
        } else if (workflowType === 'interim_birth_notification') {
          const confirmed = await showConfirm({
            title: 'Additional Birth Notification',
            message: `This employee already has ${pendingDocs.length} interim birth notification(s) awaiting verification. Is this for a different birth event?`,
            confirmLabel: 'Yes, Different Event',
            cancelLabel: 'Cancel',
            variant: 'primary',
          });
          if (!confirmed) return;
          confirmedAdditionalInstance = true;
        } else if (workflowType === 'ns_document') {
          const confirmed = await showConfirm({
            title: 'Additional NS Call-up Order',
            message: `This employee already has ${pendingDocs.length} NS Call-up Order(s) awaiting verification. Is this for a different call-up period?`,
            confirmLabel: 'Yes, Different Call-up',
            cancelLabel: 'Cancel',
            variant: 'primary',
          });
          if (!confirmed) return;
          confirmedAdditionalInstance = true;
        } else {
          toast.error(
            `A ${(docTypeForCheck || '').replace(/_/g, ' ')} is already being processed. Please wait for it to complete before uploading another.`,
            { duration: 5000 }
          );
          return;
        }
      }

      if (approvedDocs.length > 0) {
        const unrestrictedMultiTypes = ['professional_certificate', 'education_certificate'];
        if (unrestrictedMultiTypes.includes(workflowType)) {
          // Professional/education certs are inherently multi-instance
          // (e.g., PMP + AWS, Bachelor's + Master's). The backend hash
          // check still blocks exact-file duplicates.
        } else if (workflowType === 'child_certificate') {
          if (!confirmedAdditionalInstance) {
            const confirmed = await showConfirm({
              title: 'Additional Birth Certificate',
              message: `This employee already has ${approvedDocs.length} approved birth certificate(s). Is this certificate for a different child?`,
              confirmLabel: 'Yes, Different Child',
              cancelLabel: 'Cancel',
              variant: 'primary',
            });
            if (!confirmed) return;
          }
          confirmAdditionalChild = true;
        } else if (workflowType === 'interim_birth_notification') {
          if (!confirmedAdditionalInstance) {
            const confirmed = await showConfirm({
              title: 'Additional Birth Notification',
              message: `This employee already has ${approvedDocs.length} approved interim birth notification(s). Is this for a different birth event?`,
              confirmLabel: 'Yes, Different Event',
              cancelLabel: 'Cancel',
              variant: 'primary',
            });
            if (!confirmed) return;
          }
        } else if (workflowType === 'ns_document') {
          if (!confirmedAdditionalInstance) {
            const confirmed = await showConfirm({
              title: 'Additional NS Call-up Order',
              message: `This employee already has ${approvedDocs.length} approved NS Call-up Order(s). Is this for a different call-up period?`,
              confirmLabel: 'Yes, Different Call-up',
              cancelLabel: 'Cancel',
              variant: 'primary',
            });
            if (!confirmed) return;
          }
        } else {
          toast.error(`An approved ${(docTypeForCheck || '').replace(/_/g, ' ')} already exists for this employee.`, { duration: 5000 });
          return;
        }
      }
    }

    // Build FormData with target_staff_id for admin upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_staff_id', targetStaffId);
    if (confirmAdditionalChild) {
      formData.append('confirm_additional_child', 'true');
    }

    // Determine endpoint based on workflow type
    let endpoint, documentType, cardSide;
    switch (workflowType) {
      case 'child_certificate':
        endpoint = '/child-certificate/upload/';
        documentType = 'birth_certificate';
        formData.append('child_name', '');
        formData.append('notes', 'Uploaded by HR Admin');
        break;
      case 'interim_birth_notification':
        endpoint = '/interim-birth-notification/upload/';
        documentType = 'interim_birth_notification';
        formData.append('notes', 'Uploaded by HR Admin');
        break;
      case 'marriage_certificate':
        endpoint = '/marriage-certificate/upload/';
        documentType = 'marriage_certificate';
        formData.append('spouse_name', '');
        formData.append('marriage_date', '');
        formData.append('notes', 'Uploaded by HR Admin');
        break;
      case 'id_card_front':
        endpoint = '/id-card/upload/';
        documentType = 'id_card_front';
        cardSide = 'front';
        formData.append('card_side', 'front');
        break;
      case 'id_card_back':
        endpoint = '/id-card/upload/';
        documentType = 'id_card_back';
        cardSide = 'back';
        formData.append('card_side', 'back');
        break;
      case 'education_certificate':
        endpoint = '/education-certificate/upload/';
        documentType = 'education_certificate';
        break;
      case 'professional_certificate':
        endpoint = '/professional-certificate/upload/';
        documentType = 'professional_certificate';
        break;
      case 'ns_document':
        endpoint = '/ns-document/upload/';
        documentType = 'ns_call_up';
        break;
      case 'bank_statement':
        endpoint = '/bank-statement/upload/';
        documentType = 'bank_statement';
        break;
      case 'passport':
        endpoint = '/passport/upload/';
        documentType = 'passport';
        break;
      case 'death_certificate':
        endpoint = '/death-certificate/upload/';
        documentType = 'death_certificate';
        break;
      case 'driving_license':
        endpoint = '/driving-license/upload/';
        documentType = 'driving_license';
        break;
      default:
        toast.error('Unknown document type');
        return;
    }

    setUploadingFiles(prev => { const s = new Set(prev); s.add(workflowType); return s; });

    try {
      const response = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(`Document uploaded for ${employee.first_name || targetStaffId}`);
        const docApprovalId = response.data.document_approval_id;
        const staffId = response.data.staff_id || targetStaffId;

        // Interim birth notification: no OCR step — open verification modal immediately
        if (workflowType === 'interim_birth_notification') {
          setVerificationModal({
            open: true,
            documentApprovalId: docApprovalId,
            documentType: 'interim_birth_notification',
            initialOcrData: {},
            viewMode: 'edit',
          });
          fetchDocuments();
          return;
        }

        // Send to n8n for OCR processing
        const loadingToast = toast.loading('Analysing document...');
        let ocrData = {};

        try {
          if (workflowType === 'child_certificate') {
            const n8nResp = await api.sendChildCertToN8N(docApprovalId, file, staffId);
            const raw = n8nResp?.data;
            const first = Array.isArray(raw) ? raw[0] : raw;
            const payload = (first?.data) ? first.data : first || {};
            const extracted = payload.ocr_data || payload.extracted_data || payload.birth_certificate_data || null;
            if (extracted && Object.keys(extracted).length > 0) {
              ocrData = {
                child_full_name: extracted.child_full_name || extracted.child_name || '',
                child_date_of_birth: extracted.child_date_of_birth || extracted.date_of_birth || '',
                mother_full_name: extracted.mother_full_name || extracted.mother_name || '',
                father_full_name: extracted.father_full_name || extracted.father_name || '',
                birth_certificate_number: extracted.birth_certificate_number || extracted.certificate_number || '',
                child_citizenship_status: extracted.child_citizenship_status || extracted.citizenship || '',
                child_place_of_birth: extracted.child_place_of_birth || extracted.place_of_birth || '',
                confidence_score: extracted.confidence_score || 95.0
              };
              await api.post('/birth-certificate/ocr-webhook/', { document_approval_id: docApprovalId, ocr_data: ocrData });
            }

          } else if (workflowType === 'marriage_certificate') {
            const n8nResp = await api.sendMarriageCertToN8N(docApprovalId, file, staffId);
            const raw = n8nResp?.data;
            const first = Array.isArray(raw) ? raw[0] : raw;
            const payload = (first?.data) ? first.data : first || {};
            const extracted = payload.marriage_data || payload.ocr_data || payload.extracted_data || payload.marriage_certificate_data || null;
            if (extracted && Object.keys(extracted).length > 0) {
              ocrData = {
                bridegroom_full_name: extracted.bridegroom_full_name || extracted.groom_name || '',
                bride_full_name: extracted.bride_full_name || extracted.bride_name || '',
                marriage_date: extracted.marriage_date || extracted.wedding_date || '',
                marriage_certificate_number: extracted.marriage_certificate_number || extracted.certificate_number || '',
                marriage_venue: extracted.marriage_venue || extracted.venue || '',
                witness_1_name: extracted.witness_1_name || '',
                witness_2_name: extracted.witness_2_name || ''
              };
              await api.post('/marriage-certificate/ocr-webhook/', { document_approval_id: docApprovalId, ocr_data: ocrData });
            }

          } else if (workflowType === 'id_card_front' || workflowType === 'id_card_back') {
            const n8nResp = await api.sendIdCardToN8N(docApprovalId, file, staffId, cardSide);
            const raw = n8nResp?.data;
            const first = Array.isArray(raw) ? raw[0] : raw;
            const payload = (first?.data) ? first.data : first || {};
            let extracted = payload.ocr_data || payload.extracted_data || payload.id_card_data || null;
            if (!extracted && payload && Object.keys(payload).some(k => k.startsWith('ocr_'))) extracted = payload;
            if (extracted && Object.keys(extracted).length > 0) {
              const f = (ocrKey, alt1, alt2) => extracted[ocrKey] || extracted[alt1] || (alt2 ? extracted[alt2] : '') || '';
              ocrData = {
                id_card_name: f('ocr_id_card_name', 'id_card_name', 'name'),
                id_card_nric_fin_number: f('ocr_id_card_nric_fin_number', 'id_card_nric_fin_number', 'nric_fin_number') || f('ocr_id_card_nric_fin_number', 'nric', ''),
                id_card_type: f('ocr_id_card_type', 'id_card_type', 'type'),
                id_card_issue_date: f('ocr_id_card_issue_date', 'id_card_issue_date', 'issue_date'),
                id_card_nationality: f('ocr_id_card_nationality', 'id_card_nationality', 'nationality'),
                id_card_race: f('ocr_id_card_race', 'id_card_race', 'race'),
                id_card_dob: f('ocr_id_card_dob', 'id_card_dob', 'dob'),
                id_card_sex: f('ocr_id_card_sex', 'id_card_sex', 'sex'),
                id_card_address: f('ocr_id_card_address', 'id_card_address', 'address'),
                id_card_postal_code: f('ocr_id_card_postal_code', 'id_card_postal_code', 'postal_code'),
                confidence_score: extracted.confidence_score || extracted.ocr_confidence_score || 95.0
              };
              await api.post('/id-card/ocr-webhook/', { document_approval_id: docApprovalId, card_side: cardSide, ocr_data: ocrData });
            }

          } else if (workflowType === 'education_certificate' || workflowType === 'professional_certificate') {
            const qualFormData = new FormData();
            qualFormData.append('file', file);
            qualFormData.append('document_approval_id', docApprovalId);
            qualFormData.append('document_type', documentType);
            const n8nResp = await api.post('/qualification/trigger-ocr/', qualFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              // Multi-page certificates run one LLM OCR pass per page in n8n —
              // must outlast the backend's 180s n8n timeout (default 30s axios
              // timeout silently dropped the OCR result)
              timeout: 200000
            });
            const proxyResp = n8nResp?.data;
            const n8nData = proxyResp?.data || proxyResp || {};
            const payload = firstMeaningfulQualificationN8nPage(n8nData);
            let extracted = proxyResp?.ocr_data || payload.ocr_data || payload.extracted_data || payload.qualification_data || null;
            if (!extracted && documentType === 'education_certificate' && payload.education) {
              const arr = Array.isArray(payload.education) ? payload.education : [payload.education];
              if (arr[0]) extracted = arr[0];
            }
            if (!extracted && documentType === 'professional_certificate' && payload.professional_certs) {
              const arr = Array.isArray(payload.professional_certs) ? payload.professional_certs : [payload.professional_certs];
              if (arr[0]) extracted = arr[0];
            }
            if (!extracted && documentType === 'professional_certificate' && payload.education) {
              const arr = Array.isArray(payload.education) ? payload.education : [payload.education];
              if (arr[0]) extracted = arr[0];
            }
            if (!extracted && payload && Object.keys(payload).some(k => k.startsWith('ocr_'))) extracted = payload;
            if (extracted && Object.keys(extracted).length > 0) {
              const gf = (ocrKey, alts = []) => {
                if (extracted[ocrKey]) return extracted[ocrKey];
                const base = ocrKey.replace('ocr_', '');
                if (extracted[base]) return extracted[base];
                for (const a of alts) { if (extracted[a]) return extracted[a]; }
                return '';
              };
              if (documentType === 'education_certificate') {
                ocrData = {
                  institution_name: gf('ocr_institution_name', ['institution_name', 'school_name', 'university_name']),
                  qualification_name: gf('ocr_qualification_name', ['qualification_name', 'degree_name', 'diploma_name']),
                  field_of_study: gf('ocr_field_of_study', ['field_of_study', 'major', 'subject']),
                  start_year: extracted.ocr_start_year || extracted.start_year || null,
                  end_year: extracted.ocr_end_year || extracted.end_year || extracted.graduation_year || null,
                  country: gf('ocr_country', ['country']) || 'Singapore',
                  certificate_number: gf('ocr_certificate_number', ['certificate_number', 'cert_number'])
                };
              } else {
                ocrData = {
                  cert_name: gf('ocr_cert_name', ['cert_name', 'certificate_name', 'name', 'ocr_field_of_study', 'field_of_study', 'ocr_qualification_name', 'qualification_name']),
                  cert_short_name: gf('ocr_cert_short_name', ['cert_short_name', 'short_name']),
                  issuing_organization: gf('ocr_issuing_organization', ['issuing_organization', 'issuer', 'organization', 'authority', 'ocr_institution_name', 'institution_name', 'school_name', 'university_name', 'college_name']),
                  cert_category: gf('ocr_cert_category', ['cert_category', 'category']),
                  issue_date: extracted.ocr_issue_date || extracted.issue_date || extracted.issued_date || extracted.date_issued || extracted.ocr_end_date || extracted.end_date || null,
                  expiry_date: extracted.ocr_expiry_date || extracted.expiry_date || null,
                  certificate_number: gf('ocr_certificate_number', ['certificate_number', 'cert_number']),
                  cert_country: gf('ocr_cert_country', ['cert_country', 'country']) || 'Singapore'
                };
              }
              await api.post('/qualification/ocr-webhook/', { document_approval_id: docApprovalId, document_type: documentType, ocr_data: ocrData });
            }

          } else if (workflowType === 'ns_document') {
            const nsFormData = new FormData();
            nsFormData.append('file', file);
            nsFormData.append('document_approval_id', docApprovalId);
            if (staffId) nsFormData.append('staff_id', staffId);
            const n8nResp = await api.post('/ns-call-up/trigger-ocr/', nsFormData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            // Proxy returns: { success, n8n_response: [{ocr_ns_callup_...}] }
            const proxyResp = n8nResp?.data;
            const n8nData = proxyResp?.n8n_response || proxyResp?.data || proxyResp || {};
            const first = Array.isArray(n8nData) ? n8nData[0] : n8nData;
            const payload = (first?.data) ? first.data : first || {};
            let extracted = payload.ocr_data || payload.extracted_data || payload.ns_data || null;
            if (!extracted && payload && Object.keys(payload).some(k => k.startsWith('ocr_'))) extracted = payload;
            if (extracted && Object.keys(extracted).length > 0) {
              ocrData = extracted;
              // Persist OCR data to DocumentOCRData via webhook (matches pattern of other doc types)
              await api.post('/ns-call-up/ocr-webhook/', {
                document_approval_id: docApprovalId,
                ocr_data: ocrData
              });
            }
          } else if (workflowType === 'bank_statement') {
            // Bank statement OCR via n8n
            const bsFormData = new FormData();
            bsFormData.append('file', file);
            bsFormData.append('document_approval_id', docApprovalId);
            if (staffId) bsFormData.append('staff_id', staffId);
            const n8nResp = await api.post('/bank-statement/trigger-ocr/', bsFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 60000,
            });
            const proxyResp = n8nResp?.data;
            const n8nData = proxyResp?.data || proxyResp || {};
            const first = Array.isArray(n8nData) ? n8nData[0] : n8nData;
            const payload = (first?.data) ? first.data : first || {};
            const bankDetails = payload.bank_details || payload.ocr_data || payload.extracted_data || null;
            let extracted = bankDetails || payload;
            // Normalize to ocr_ prefixed keys
            if (extracted && typeof extracted === 'object') {
              const normalize = (obj) => {
                const normalized = {};
                const keyMap = {
                  'bank_name': 'ocr_bank_name',
                  'bank_account_number': 'ocr_bank_account_number',
                  'account_number': 'ocr_bank_account_number',
                  'branch_code': 'ocr_branch_code',
                  'account_holder_name': 'ocr_account_holder_name',
                };
                for (const [k, v] of Object.entries(obj)) {
                  if (k.startsWith('ocr_')) {
                    normalized[k] = v;
                  } else if (keyMap[k]) {
                    normalized[keyMap[k]] = v;
                  }
                }
                return normalized;
              };
              ocrData = normalize(extracted);
            }
            // Persist OCR data via webhook endpoint
            if (Object.keys(ocrData).length > 0) {
              await api.post('/bank-statement/ocr-webhook/', {
                document_approval_id: docApprovalId,
                ocr_data: ocrData
              });
            }

          } else if (workflowType === 'passport') {
            // Passport OCR via n8n
            const passportFormData = new FormData();
            passportFormData.append('file', file);
            passportFormData.append('document_approval_id', docApprovalId);
            if (staffId) passportFormData.append('staff_id', staffId);
            const n8nResp = await api.post('/passport/trigger-ocr/', passportFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 60000,
            });
            const proxyResp = n8nResp?.data;
            const n8nData = proxyResp?.data || proxyResp || {};
            const first = Array.isArray(n8nData) ? n8nData[0] : n8nData;
            const payload = (first?.data) ? first.data : first || {};
            let extracted = payload.ocr_data || payload.extracted_data || payload.passport_details || payload.passport_data || null;
            if (!extracted && payload && Object.keys(payload).some(k => k.startsWith('ocr_passport') || k.includes('passport') || k.includes('mrz'))) {
              extracted = payload;
            }
            if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
              // Normalize to ocr_passport_ prefixed keys
              const gf = (ocrKey, alts = []) => {
                if (extracted[ocrKey]) return extracted[ocrKey];
                const base = ocrKey.replace('ocr_passport_', '');
                if (extracted[base]) return extracted[base];
                for (const a of alts) { if (extracted[a]) return extracted[a]; }
                return '';
              };
              ocrData = {
                ocr_passport_first_name: gf('ocr_passport_first_name', ['first_name', 'given_names', 'given_name']),
                ocr_passport_last_name: gf('ocr_passport_last_name', ['last_name', 'surname', 'family_name']),
                ocr_passport_number: gf('ocr_passport_number', ['passport_number', 'document_number', 'number']),
                ocr_passport_nationality: gf('ocr_passport_nationality', ['nationality', 'country_code', 'issuing_country']),
                ocr_passport_date_of_birth: gf('ocr_passport_date_of_birth', ['date_of_birth', 'dob', 'birth_date']),
                ocr_passport_gender: gf('ocr_passport_gender', ['gender', 'sex']),
                ocr_passport_expiry_date: gf('ocr_passport_expiry_date', ['expiry_date', 'expiration_date', 'date_of_expiry']),
                ocr_passport_mrz_line1: gf('ocr_passport_mrz_line1', ['mrz_line1', 'mrz_line_1', 'mrz1']),
                ocr_passport_mrz_line2: gf('ocr_passport_mrz_line2', ['mrz_line2', 'mrz_line_2', 'mrz2'])
              };
              // Persist OCR data via webhook endpoint
              await api.post('/passport/ocr-webhook/', {
                document_approval_id: docApprovalId,
                ocr_data: ocrData
              });
            }

          } else if (workflowType === 'death_certificate') {
            // Death certificate (Compassionate Leave). The backend proxy forwards
            // to the n8n "compassionate" webhook and persists the extracted fields
            // (deceased name, NRIC last-4, date of death, certificate number, etc.)
            // directly from the synchronous response — no client-side mapping needed.
            const n8nResp = await api.sendDeathCertToN8N(docApprovalId, file, staffId);
            const raw = n8nResp?.data;
            const first = Array.isArray(raw) ? raw[0] : raw;
            const payload = (first?.data) ? first.data : first || {};
            const extracted = payload.ocr_data || payload.extracted_data || payload || {};
            if (extracted && typeof extracted === 'object' && Object.keys(extracted).length > 0) {
              ocrData = extracted;
            }
          } else if (workflowType === 'driving_license') {
            // Driving licence (multi-class). The backend proxy forwards to the
            // n8n "drivinglicence" webhook and persists each licence class onto
            // the working OCR record. The verification modal hydrates the class
            // rows from the verification endpoint, so no client-side mapping is
            // needed here.
            await api.sendDrivingLicenceToN8N(docApprovalId, file, staffId);
          }

          toast.dismiss(loadingToast);
          if (workflowType === 'driving_license') {
            toast.success('Licence classes extracted. Please review below.');
          } else if (Object.keys(ocrData).length > 0) {
            toast.success('Details extracted. Please review below.');
          } else {
            toast.warning('Unable to extract details automatically. Please enter manually in review.', { icon: '\u26a0\ufe0f', duration: 5000 });
          }
        } catch (n8nError) {
          toast.dismiss(loadingToast);
          toast.warning('OCR extraction failed. You can enter details manually in review.', { icon: '\u26a0\ufe0f', duration: 5000 });
        }

        // Death certificate: inform HR which compassionate leave application the
        // upload auto-linked to, then fall through to open the verification modal.
        if (workflowType === 'death_certificate') {
          const linkedAppId = response.data.leave_application_id;
          if (linkedAppId) {
            toast.success(`Linked to compassionate leave application #${linkedAppId}.`);
          } else {
            toast.warning(
              'Uploaded, but no pending compassionate leave application was found to link to. The employee should apply for Compassionate Leave first.',
              { icon: '\u26a0\ufe0f', duration: 7000 }
            );
          }
        }

        // Open the appropriate verification modal for admin to review and approve
        if (documentType === 'ns_call_up') {
          setNsVerificationModal({
            open: true,
            documentApprovalId: docApprovalId,
            leaveApplicationId: null
          });
        } else {
          setVerificationModal({
            open: true,
            documentApprovalId: docApprovalId,
            documentType: documentType,
            initialOcrData: ocrData,
            viewMode: 'edit'
          });
        }

        // Refresh document list
        fetchDocuments();
      }
    } catch (error) {
      const errData = error.response?.data;
      const errMsg = errData?.error || 'Upload failed. Please try again.';
      
      // Handle 409 duplicate responses with specific UX
      if (error.response?.status === 409 && errData?.duplicate_type === 'approved_exists' && errData?.requires_confirmation) {
        // Backend detected approved doc but frontend check missed it (e.g., stale document list)
        const confirmed = await showConfirm({
          title: 'Duplicate Document Detected',
          message: `${errMsg} Click OK if this is for a different child, or Cancel to abort.`,
          confirmLabel: 'Yes, Proceed',
          cancelLabel: 'Cancel',
          variant: 'primary',
        });
        if (confirmed) {
          // Re-upload with confirmation flag
          formData.append('confirm_additional_child', 'true');
          try {
            const retryResp = await api.post(endpoint, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (retryResp.data.success) {
              toast.success(`Document uploaded for ${employee.first_name || targetStaffId}`);
              fetchDocuments();
            }
          } catch (retryErr) {
            toast.error(retryErr.response?.data?.error || 'Upload failed on retry. Please try again.');
          }
        }
      } else {
        toast.error(errMsg);
      }
    } finally {
      setUploadingFiles(prev => { const s = new Set(prev); s.delete(workflowType); return s; });
    }
  };

  const handleAdminMcUpload = async (files, workflowType) => {
    const file = files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: PDF, JPG, PNG.');
      return;
    }

    const leaveTypeCode = workflowType === 'mc_outpatient' ? 'osl' : 'hl';

    setUploadingFiles(prev => { const s = new Set(prev); s.add(workflowType); return s; });

    const loadingToastId = toast.loading('Uploading medical certificate...');

    try {
      // Step 1: Create draft leave application for this employee + leave type
      const initiateResp = await staffAPI.hrInitiateMcLeave(employee.staff_id, leaveTypeCode);
      const applicationId = initiateResp.data.application_id;
      const leaveTypeName = initiateResp.data.leave_type_name;

      // Step 2: Send to n8n for OCR — awaits n8n's synchronous response.
      // The backend proxy now persists OCR data directly from the n8n
      // response, but as a belt-and-suspenders fallback the frontend also
      // forwards the OCR payload via sendMcOcrData if present.
      toast.loading('Analysing medical certificate...', { id: loadingToastId });
      let ocrSavedByFrontend = false;
      try {
        const ocrResp = await staffAPI.sendMedicalCertToN8N(applicationId, file);
        const ocrPayload = Array.isArray(ocrResp?.data) ? ocrResp.data[0] : ocrResp?.data;
        if (ocrPayload?.resumed && ocrPayload.application_id) {
          const resumedId = ocrPayload.application_id;
          if (String(resumedId) !== String(applicationId)) {
            try {
              await staffAPI.discardMcDraft(applicationId);
            } catch (discardErr) {
              console.warn('Failed to discard replaced HR MC draft:', discardErr);
            }
          }
          toast.dismiss(loadingToastId);
          toast.info('This certificate is already on an unfinished application. Opening it for review.');
          setMcAdminVerificationModal({ open: true, applicationId: resumedId, leaveTypeName });
          return;
        }
        const ocrData = ocrPayload?.ocr_data || ocrPayload?.extracted_data;
        if (ocrData && typeof ocrData === 'object') {
          try {
            await staffAPI.sendMcOcrData({ application_id: applicationId, ocr_data: ocrData });
            ocrSavedByFrontend = true;
          } catch (webhookErr) {
            const webhookCode = webhookErr.response?.data?.error;
            if (
              webhookErr.response?.status === 409 &&
              (webhookCode === 'duplicate_mc' || webhookCode === 'duplicate_mc_dates' || webhookCode === 'duplicate_mc_number')
            ) {
              throw webhookErr;
            }
          }
        }
      } catch (ocrErr) {
        toast.dismiss(loadingToastId);
        const errorCode = ocrErr.response?.data?.error;
        if (
          ocrErr.response?.status === 409 &&
          (errorCode === 'duplicate_mc' || errorCode === 'duplicate_mc_dates' || errorCode === 'duplicate_mc_number')
        ) {
          try {
            await staffAPI.discardMcDraft(applicationId);
          } catch (discardErr) {
            console.warn('Failed to discard duplicate HR MC draft:', discardErr);
          }
          const serverMessage = ocrErr.response?.data?.message;
          toast.error(
            serverMessage || 'This medical certificate has already been uploaded for this employee and leave type.',
            { duration: 6000 }
          );
        } else {
          toast.error('Could not analyse the certificate. Please try again.');
        }
        return;
      }

      // Step 3: Poll getMcVerificationData until OCR data is confirmed in
      // the DB.  The proxy saves it synchronously now, so this typically
      // resolves on the first attempt; polling is a safety net.
      toast.loading('Preparing verification data...', { id: loadingToastId });

      const maxAttempts = ocrSavedByFrontend ? 3 : 10;
      let attempts = 0;
      let ocrReady = false;

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, ocrSavedByFrontend ? 1000 : 3000));
        try {
          const checkResp = await staffAPI.getMcVerificationData(applicationId);
          if (
            checkResp?.data?.success &&
            checkResp?.data?.mode !== 'file_only' &&
            checkResp?.data?.extracted_data
          ) {
            ocrReady = true;
            break;
          }
        } catch { /* keep polling */ }
        attempts++;
      }

      toast.dismiss(loadingToastId);

      if (ocrReady) {
        toast.success('MC details extracted. Please verify and confirm.');
      } else {
        toast.warning('OCR took longer than expected. Please review and enter any missing details manually.', {
          icon: '⚠️',
          duration: 5000,
        });
      }

      // Step 4: Open the MC verification modal — OCR data is in DB (or timed out gracefully)
      setMcAdminVerificationModal({ open: true, applicationId, leaveTypeName });

    } catch (error) {
      toast.dismiss(loadingToastId);
      const errMsg = error.response?.data?.error || 'Failed to initiate MC upload. Please try again.';
      toast.error(errMsg);
    } finally {
      setUploadingFiles(prev => { const s = new Set(prev); s.delete(workflowType); return s; });
    }
  };

  const statusSortPriority = {
    pending_verification: 0,
    awaiting_employee: 1,
    rejected: 2,
    approved: 3,
    on_file: 3,
    superseded: 4,
  };

  const filteredDocuments = (selectedCategory === 'all'
    ? documents
    : documents.filter(doc => doc.category === selectedCategory)
  ).slice().sort((a, b) => {
    const pa = statusSortPriority[a.status] ?? 3;
    const pb = statusSortPriority[b.status] ?? 3;
    if (pa !== pb) return pa - pb;
    return new Date(b.uploadedAt) - new Date(a.uploadedAt);
  });

  const pendingCount = documents.filter(doc => doc.status === 'pending_verification').length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4">
        <button
          onClick={() => setSubTab('verification')}
          className={`tab-btn-admin tab-btn-admin-sm ${subTab === 'verification' ? 'active' : ''}`}
        >
          <FileCheck className="w-4 h-4" />
          Document Verification
        </button>
        <button
          onClick={() => setSubTab('upload')}
          className={`tab-btn-admin tab-btn-admin-sm ${subTab === 'upload' ? 'active' : ''}`}
        >
          <Upload className="w-4 h-4" />
          Upload Documents
        </button>
        <button
          onClick={() => setSubTab('letters')}
          className={`tab-btn-admin tab-btn-admin-sm ${subTab === 'letters' ? 'active' : ''}`}
        >
          <FileText className="w-4 h-4" />
          Official HR Letters
        </button>
      </div>

      {subTab === 'verification' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileCheck className="w-6 h-6 text-red-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Document Verification</h2>
                <p className="text-gray-600">Review employee documents and inspect files on record</p>
              </div>
            </div>
            {pendingCount > 0 && (
              <span className="badge badge-warning">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {pendingCount} Pending Review
              </span>
            )}
          </div>

      {/* Document Categories Filter */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`tab-btn-admin tab-btn-admin-sm ${selectedCategory === 'all' ? 'active' : ''}`}
        >
          All Documents
          <CountBadge count={documents.length} theme="admin" active={selectedCategory === 'all'} />
        </button>
        {Object.entries(documentCategories).map(([key, category]) => {
          const IconComponent = category.icon;
          return (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`tab-btn-admin tab-btn-admin-sm ${selectedCategory === key ? 'active' : ''}`}
            >
              <IconComponent className="w-4 h-4" />
              {category.name}
              <CountBadge count={documents.filter(doc => doc.category === key).length} theme="admin" active={selectedCategory === key} />
            </button>
          );
        })}
      </div>

      {/* Documents List */}
      <div className="space-y-3">
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-xl">
            <FileText className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">No documents found</h3>
            <p className="text-sm text-gray-600">
              {selectedCategory === 'all' 
                ? 'This employee has not uploaded any documents yet'
                : `No ${documentCategories[selectedCategory]?.name.toLowerCase()} found`
              }
            </p>
          </div>
        ) : (
          filteredDocuments.map((document) => {
            const isLeaveTriggering = document.triggeredLeaveTypes && document.triggeredLeaveTypes.length > 0;
            const { icon: DocIcon, color, bg } = getDocumentTypeIcon(document.documentType);

            // Derive a short leave-type label for MC cards (OSL / HL / etc.)
            const mcLeaveLabel = (() => {
              if (document.documentType !== 'medical_certificate') return null;
              const ltn = document.leaveTypeName || '';
              if (/outpatient/i.test(ltn)) return { short: 'OSL', full: 'Outpatient Sick Leave', colorClass: 'bg-blue-100 text-blue-700' };
              if (/hospitalisation|hospitalization/i.test(ltn)) return { short: 'HL', full: 'Hospitalisation Leave', colorClass: 'bg-purple-100 text-purple-700' };
              if (ltn) return { short: ltn, full: ltn, colorClass: 'bg-gray-100 text-gray-600' };
              return null;
            })();

            const ocrLine = (() => {
              const s = document.ocrSummary;
              if (!s || !Object.values(s).some(v => v)) return null;
              switch (document.documentType) {
                case 'medical_certificate': {
                  const range = [s.ocr_mc_start_date && formatDate(s.ocr_mc_start_date), s.ocr_mc_end_date && formatDate(s.ocr_mc_end_date)].filter(Boolean);
                  const isSameDay = range.length === 2 && range[0] === range[1];
                  const parts = [
                    range.length === 2 && !isSameDay ? `${range[0]} → ${range[1]}` : range[0],
                    s.ocr_mc_total_days && `${s.ocr_mc_total_days} ${pluralizeDays(s.ocr_mc_total_days)}`,
                    s.ocr_clinic_hospital_name,
                  ].filter(Boolean);
                  return parts.join(' · ') || null;
                }
                case 'education_certificate':
                  return getDocumentSubtitle('education_certificate', s, document.name, { omitQualification: true });
                case 'professional_certificate':
                  return [s.cert_name, s.issuing_organization].filter(Boolean).join(' · ') || null;
                case 'birth_certificate':
                  return [s.child_full_name && `Child: ${s.child_full_name}`, s.child_date_of_birth && `Born ${formatDate(s.child_date_of_birth)}`].filter(Boolean).join(' · ') || null;
                case 'marriage_certificate': {
                  const names = [s.bridegroom_full_name, s.bride_full_name].filter(Boolean);
                  return [names.length === 2 ? `${names[0]} & ${names[1]}` : names[0], s.marriage_date && formatDate(s.marriage_date)].filter(Boolean).join(' · ') || null;
                }
                case 'death_certificate':
                  return [s.death_cert_deceased_name && `Deceased: ${s.death_cert_deceased_name}`, s.death_cert_date_of_death && formatDate(s.death_cert_date_of_death), s.death_cert_certificate_number && `Cert: ${s.death_cert_certificate_number}`].filter(Boolean).join(' · ') || null;
                case 'id_card_front':
                case 'id_card_back':
                  return [s.id_card_name, s.id_card_nric_fin_number].filter(Boolean).join(' · ') || null;
                case 'bank_statement':
                  return [
                    s.ocr_bank_name,
                    s.ocr_account_holder_name && `Holder: ${s.ocr_account_holder_name}`,
                    s.ocr_bank_account_number,
                    s.ocr_branch_code && `Branch: ${s.ocr_branch_code}`,
                  ].filter(Boolean).join(' · ') || null;
                case 'ns_call_up':
                  return [s.call_up_serial_number && `Serial: ${s.call_up_serial_number}`, s.call_up_issue_date && `Issued ${formatDate(s.call_up_issue_date)}`].filter(Boolean).join(' · ') || null;
                case 'passport':
                  return [s.passport_name, s.passport_number, s.passport_expiry_date && `Exp ${formatDate(s.passport_expiry_date)}`].filter(Boolean).join(' · ') || null;
                default:
                  return null;
              }
            })();

            const metaLine = (() => {
              if (document.isProfileSourced) {
                const parts = [];
                if (document.uploadedAt) {
                  parts.push(`Recorded ${formatDate(document.uploadedAt)}`);
                }
                if (document.verifiedAt) {
                  parts.push(`Verified ${formatDate(document.verifiedAt)}`);
                } else {
                  parts.push('Verified credential on profile');
                }
                if (!document.hasDocumentFile) {
                  parts.push('No scan attached');
                }
                return parts.join(' · ');
              }
              const parts = [
                `${isDocumentSubmittedToHr(document.rawStatus, document.documentType) ? 'Submitted' : 'Uploaded'} ${formatDate(document.uploadedAt)}`,
              ];
              if (document.status === 'rejected' && document.verifiedAt) {
                const rejectorName = document.verifiedByName || document.verifiedBy;
                parts.push(`Rejected ${formatDate(document.verifiedAt)}${rejectorName ? ` by ${rejectorName}` : ''}`);
              } else if (document.status !== 'pending_verification' && document.verifiedAt) {
                const verifierName = document.verifiedByName || document.verifiedBy;
                parts.push(`Verified ${formatDate(document.verifiedAt)}${verifierName ? ` by ${verifierName}` : ''}`);
              }
              return parts.join(' · ');
            })();

            const isHistoricalRejection = document.status === 'rejected'
              && hasLaterReplacementUpload(document, documents);

            return (
              <div key={document.id} className={`bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col gap-3 ${
                isLeaveTriggering
                  ? 'border-orange-300 bg-orange-50/30'
                  : document.status === 'rejected' && !isHistoricalRejection
                    ? 'border-red-200'
                    : 'border-gray-200'
              }`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isLeaveTriggering ? 'bg-orange-100' : bg
                    }`}>
                      <DocIcon className={`w-5 h-5 ${isLeaveTriggering ? 'text-orange-600' : color}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 truncate">
                        {document.displayName || document.name}
                      </h4>
                      {(document.certShortName || mcLeaveLabel || isLeaveTriggering || document.isProfileSourced || isHistoricalRejection) && (
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          {document.certShortName && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700"
                              title={document.ocrSummary?.cert_name || document.certShortName}
                            >
                              {document.certShortName}
                            </span>
                          )}
                          {mcLeaveLabel && (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${mcLeaveLabel.colorClass}`}
                              title={mcLeaveLabel.full}
                            >
                              {mcLeaveLabel.short}
                            </span>
                          )}
                          {isLeaveTriggering && (
                            <InfoBadge color="orange" label="Leave Trigger" />
                          )}
                          {isHistoricalRejection && (
                            <InfoBadge
                              color="gray"
                              label="Earlier upload"
                              title="A later upload of this document type is already on file"
                            />
                          )}
                          {document.isProfileSourced && (
                            <span
                              title={
                                document.hasDocumentFile
                                  ? 'Verified profile credential with an attached scan'
                                  : 'Verified profile credential — no scan attached (imported or entered without a file)'
                              }
                            >
                              <InfoBadge
                                color={document.hasDocumentFile ? 'gray' : 'amber'}
                                label={document.hasDocumentFile ? 'On file' : 'Record only'}
                              />
                            </span>
                          )}
                        </div>
                      )}

                      {ocrLine && (
                        <p className="text-sm text-gray-600 mt-0.5 truncate" title={ocrLine}>{ocrLine}</p>
                      )}

                      <p className="text-xs text-gray-400 mt-1 line-clamp-2" title={metaLine}>{metaLine}</p>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <StatusBadge
                      status={document.status}
                      label={document.status === 'approved' ? 'Verified' : undefined}
                    />
                  </div>
                </div>

                {document.rejectionReason && (
                  <RejectionFeedback
                    reason={document.rejectionReason}
                    collapsed
                    label={isHistoricalRejection ? 'Previous feedback' : 'Rejection reason'}
                  />
                )}

                <div className="card-action-footer flex-nowrap">
                    {document.status === 'pending_verification' && document.documentApprovalId && (
                      <button
                        onClick={() => handleOpenVerificationModal(document, null, 'edit')}
                        className="icon-button-admin"
                        title="Review document"
                        aria-label="Review document"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {document.status === 'awaiting_employee' && document.documentApprovalId && (
                      <button
                        onClick={() => handleOpenVerificationModal(document, null, 'view')}
                        className="icon-button-admin"
                        title="View document"
                        aria-label="View document"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {document.status === 'pending_verification' && !document.documentApprovalId && (
                      <button
                        onClick={() => handleDocumentAction(document.id, 'approve')}
                        disabled={actionLoading[document.id]}
                        className="icon-button-admin"
                        title="Review document"
                        aria-label="Review document"
                      >
                        {actionLoading[document.id] ? (
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    {document.status !== 'pending_verification'
                      && document.status !== 'awaiting_employee'
                      && document.documentApprovalId && (
                      <button
                        onClick={() => handleOpenVerificationModal(document, null, 'view')}
                        className="icon-button-admin"
                        title="View document"
                        aria-label="View document"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {document.isProfileSourced && document.hasDocumentFile && (
                      <button
                        type="button"
                        onClick={() => handleViewProfileDocument(document)}
                        className="icon-button-admin"
                        title="View document"
                        aria-label="View document"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {document.isProfileSourced && !document.hasDocumentFile && (
                      <button
                        type="button"
                        onClick={() => handleViewProfileDocument(document)}
                        className="icon-button-admin opacity-50"
                        title="No scan attached — upload under Documents → Upload"
                        aria-label="No scan attached"
                      >
                        <EyeOff className="w-4 h-4" />
                      </button>
                    )}
                    {(document.documentApprovalId || (document.isProfileSourced && document.hasDocumentFile)) && (
                      <button
                        onClick={() => handleDownloadDocument(document)}
                        disabled={actionLoading[`dl_${document.id}`]}
                        className="icon-button-admin"
                        title="Download document"
                        aria-label="Download document"
                      >
                        {actionLoading[`dl_${document.id}`] ? (
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    {(document.documentApprovalId || (document.isProfileSourced && document.profileRecordId)) && (
                      <button
                        type="button"
                        onClick={() => handleDeleteDocument(document)}
                        disabled={actionLoading[`del_${document.id}`]}
                        className="icon-button-danger"
                        title="Delete document"
                        aria-label="Delete document"
                      >
                        {actionLoading[`del_${document.id}`] ? (
                          <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                </div>
              </div>
            );
          })
        )}
      </div>


        </div>
      )}

      {subTab === 'upload' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Upload className="w-6 h-6 text-red-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Upload Documents</h2>
              </div>
            </div>
          </div>

          {/* Upload Categories Grid — dropzone pattern matching Profile.js */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {adminUploadCategories.map(category => {
              const IconComponent = category.icon;
              const isUploading = uploadingFiles.has(category.workflowType);
              const existingDocs = documents.filter(d => d.approvalType === category.workflowType);

              return (
                <div key={category.id} className={`border-2 border-dashed rounded-xl p-4 flex flex-col ${getColorClasses(category.color)} ${category.comingSoon ? 'opacity-75' : ''}`}>
                  <div className="flex flex-col flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                        <IconComponent className={`w-6 h-6 ${getIconColorClass(category.color)}`} />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold">{category.name}</h3>
                        {category.interimBadge && (
                          <span className="inline-flex items-center px-2 py-0.5 mt-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                            Provisional
                          </span>
                        )}
                        {category.comingSoon && (
                          <span className="inline-flex items-center px-2 py-0.5 mt-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                            Coming soon
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm opacity-75 mb-4">{category.description}</p>

                    <div className="flex-1" />

                    {/* Upload Dropzone */}
                    {category.comingSoon ? (
                      <div className="bg-white/60 rounded-lg p-4 border-2 border-dashed border-gray-300 text-center cursor-not-allowed">
                        <Upload className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm text-gray-500 font-medium">Coming soon</p>
                        <p className="text-xs text-gray-400 mt-1">Upload will be enabled here shortly</p>
                      </div>
                    ) : (
                    <div className="relative">
                      <input
                        type="file"
                        accept={category.accept}
                        disabled={isUploading}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            const isMcType = category.workflowType === 'mc_outpatient' || category.workflowType === 'mc_hospitalisation';
                            if (isMcType) {
                              handleAdminMcUpload(e.target.files, category.workflowType);
                            } else {
                              handleAdminFileUpload(e.target.files, category.workflowType);
                            }
                            e.target.value = '';
                          }
                        }}
                      />
                      <div className={`bg-white rounded-lg p-4 border-2 border-dashed transition-colors text-center ${
                        isUploading
                          ? 'border-gray-300 bg-gray-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        {isUploading ? (
                          <>
                            <div className="w-8 h-8 mx-auto border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-2" />
                            <p className="text-sm text-gray-600 font-medium">Analysing document...</p>
                            <p className="text-xs text-gray-500 mt-1">Please don't navigate away</p>
                          </>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600">
                              Drop files here or <span className="text-red-600 font-medium">browse</span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Max 5MB per file</p>
                          </>
                        )}
                      </div>
                    </div>
                    )}
                  </div>

                  <div className="min-h-[112px]">
                    {category.leaveTypes && category.leaveTypes.length > 0 && (
                      <div className="mt-4 p-3 bg-white/80 rounded-lg border border-white/40">
                        <div className="flex items-start gap-2">
                          <Heart className={`w-4 h-4 ${getIconColorClass(category.color)} mt-0.5 flex-shrink-0`} />
                          <div className="text-left">
                            <p className={`text-xs font-medium ${getIconColorClass(category.color)} mb-1`}>
                              {['mc_outpatient', 'mc_hospitalisation', 'ns_document', 'death_certificate'].includes(category.id) ? 'Leave Type' : 'Leave Benefits'}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {category.leaveTypes.map(lt => (
                                <span
                                  key={lt}
                                  className={`inline-block px-2 py-0.5 bg-white text-xs rounded-full border ${getIconColorClass(category.color)}`}
                                >
                                  {lt}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {existingDocs.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-white/20">
                        <p className="text-sm font-medium">
                          {existingDocs.length} document{existingDocs.length !== 1 ? 's' : ''} uploaded
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {subTab === 'letters' && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <File className="w-6 h-6 text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">Official HR Letters</h2>
          </div>

          <div className="bg-white rounded-xl border">
            {lettersLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
                <span className="ml-3 text-gray-600">Loading letters…</span>
              </div>
            ) : (
              <div className="overflow-x-auto p-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Generated</th>
                      <th className="table-header-cell">Template</th>
                      <th className="table-header-cell">Version</th>
                      <th className="table-header-cell">Includes Salary</th>
                      <th className="table-header-cell">Retention</th>
                      <th className="table-header-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(letters || []).map((l) => (
                      <tr key={l.id} className="text-sm">
                        <td className="px-4 py-2">{l.generated_at ? formatDate(l.generated_at) : '-'}</td>
                        <td className="px-4 py-2 font-mono">{l.template_code}</td>
                        <td className="px-4 py-2">{l.template_version}</td>
                        <td className="px-4 py-2">
                          <InfoBadge color={l.includes_salary ? 'green' : 'gray'} label={l.includes_salary ? 'Yes' : 'No'} />
                        </td>
                        <td className="px-4 py-2">{l.retention_until ? formatDate(l.retention_until) : '-'}</td>
                        <td className="px-4 py-2 space-x-2">
                          <button onClick={() => doPreview(l.id)} className="btn btn-primary btn-sm">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => doDownload(l.id)} className="btn btn-success btn-sm">
                            <Download className="w-4 h-4" />
                          </button>
                          <button disabled={!!actionLoading[l.id]} onClick={() => doRegenerate(l.id)} className="btn btn-admin-primary btn-sm">
                            {actionLoading[l.id] ? (
                              <span className="inline-flex items-center"><div className="w-3 h-3 mr-1 border-2 border-white border-t-transparent rounded-full animate-spin" />Regenerating</span>
                            ) : (
                              <span className="inline-flex items-center"><RefreshCw className="w-4 h-4 mr-1" />Regenerate</span>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!letters || letters.length === 0) && (
                  <div className="text-center py-10 text-gray-600">No letters found for this employee.</div>
                )}
              </div>
            )}
          </div>

          {preview.open && (
            <Modal
              isOpen
              onClose={() => setPreview({ open: false, html: '' })}
              size="xl"
              theme="admin"
              className="max-w-4xl"
              customHeader={(
                <div className="modal-header">
                  <h3 className="modal-title">Letter Preview</h3>
                  <button onClick={() => setPreview({ open: false, html: '' })} className="icon-button-admin">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
            >
              <div dangerouslySetInnerHTML={{ __html: preview.html || '<p class="text-gray-500">No preview available</p>' }} />
            </Modal>
          )}
        </div>
      )}
      {/* Document Verification Modal - always rendered regardless of sub-tab */}
      <DocumentVerificationModal
        isOpen={verificationModal.open}
        onClose={() => setVerificationModal({ open: false, documentApprovalId: null, documentType: null, initialOcrData: {}, viewMode: 'edit', applicationId: null })}
        documentApprovalId={verificationModal.documentApprovalId}
        documentType={verificationModal.documentType || 'birth_certificate'}
        mode="hr"
        viewMode={verificationModal.viewMode || 'edit'}
        ocrData={verificationModal.initialOcrData || {}}
        applicationId={verificationModal.applicationId || null}
        onApprovalComplete={handleVerificationComplete}
        employeeName={employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() : null}
        employeeStaffCode={employee?.staff_id || null}
        employeeProfile={{
          staff_id: employee?.staff_id || '',
          full_name: employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() : '',
          first_name: employee?.first_name || '',
          last_name: employee?.last_name || '',
          name_as_per_nric_fin: hrProfile?.hrprofile_name_as_per_nric_fin || '',
          bank_account_holder_name: hrProfile?.hrprofile_bank_account_holder_name || '',
        }}
      />
      <SimpleDocumentViewerModal
        isOpen={profileDocViewer.open}
        onClose={() => setProfileDocViewer({ open: false, document: null })}
        filePath={profileDocViewer.document?.filePath}
        fileName={profileDocViewer.document?.name}
        title={
          profileDocViewer.document?.documentType === 'other_document'
            ? profileDocViewer.document?.name
            : profileDocViewer.document?.ocrSummary?.cert_name
          || profileDocViewer.document?.displayName
          || profileDocViewer.document?.name
          || 'Document'
        }
        theme="admin"
        statusBadge={profileDocViewer.document ? (
          <StatusBadge
            status={profileDocViewer.document.status}
            label={profileDocViewer.document.status === 'approved' ? 'Verified' : undefined}
          />
        ) : null}
        onDownload={() => profileDocViewer.document && handleDownloadDocument(profileDocViewer.document)}
        downloading={Boolean(actionLoading[`dl_${profileDocViewer.document?.id}`])}
      />
      {/* NS Call-up Order Verification Modal */}
      <NSDocumentVerificationModal
        key={nsVerificationModal.documentApprovalId}
        isOpen={nsVerificationModal.open}
        onClose={() => setNsVerificationModal({ open: false, documentApprovalId: null, leaveApplicationId: null })}
        documentApprovalId={nsVerificationModal.documentApprovalId}
        leaveApplicationId={nsVerificationModal.leaveApplicationId}
        onVerificationComplete={() => {
          setNsVerificationModal({ open: false, documentApprovalId: null, leaveApplicationId: null });
          fetchDocuments();
        }}
        employeeName={employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() : null}
        employeeStaffCode={employee?.staff_id || null}
      />
      {/* MC Document Verification Modal — HR admin upload on behalf flow */}
      <MCDocumentVerificationModal
        key={mcAdminVerificationModal.applicationId}
        isOpen={mcAdminVerificationModal.open}
        onClose={() => setMcAdminVerificationModal({ open: false, applicationId: null, leaveTypeName: '' })}
        applicationId={mcAdminVerificationModal.applicationId}
        isHRInitiated={true}
        application={employee ? {
          employee_name: `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || undefined,
          staff_code: employee.staff_id || undefined,
          leave_type_name: mcAdminVerificationModal.leaveTypeName || undefined,
        } : null}
        onVerificationComplete={() => {
          setMcAdminVerificationModal({ open: false, applicationId: null, leaveTypeName: '' });
          fetchDocuments();
        }}
      />
      <ConfirmModal
        isOpen={!!confirmDialog}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || 'Confirm'}
        cancelLabel={confirmDialog?.cancelLabel || 'Cancel'}
        variant={confirmDialog?.variant || 'danger'}
        onConfirm={() => {
          confirmCallbackRef.current?.(true);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        onCancel={() => {
          confirmCallbackRef.current?.(false);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        theme="admin"
      />
    </div>
  );
};

// Professional Tab — Education / Experience / Certificates (CV)
const EmployeeProfessionalTab = ({ employee, activeTab }) => {
  // Memoize config object to prevent unnecessary re-renders in useNestedTabPersistence
  const subTabConfig = useMemo(() => ({
    subTab: {
      default: 'education',
      valid: ['education', 'experience', 'certs'],
      param: 'subTab'
    }
  }), []);
  
  // Use nested tab persistence for sub-tabs (education, experience, certs)
  const { subTab: subTabState } = useNestedTabPersistence(
    'admin-employees-professional', 
    activeTab === 'professional' ? activeTab : null, 
    subTabConfig
  );
  const [subTab, setSubTab] = subTabState;

  const staffId = employee?.staff_id;

  const { data: certificates = [] } = useEmployeeCertificates(staffId);
  const { data: educationRecords = [] } = useEmployeeEducation(staffId);
  const { data: workExperience = [] } = useEmployeeWorkExperience(staffId);
  const createWorkExperience = useCreateWorkExperience(staffId);
  const updateWorkExperience = useUpdateWorkExperience(staffId);
  const deleteWorkExperience = useDeleteWorkExperience(staffId);
  const [experienceModal, setExperienceModal] = useState({ open: false, record: null });
  const [experienceToDelete, setExperienceToDelete] = useState(null);

  const workExperienceSummary = useMemo(() => {
    if (!workExperience?.length) {
      return { employerCount: 0, totalMonths: 0, totalDays: 0 };
    }
    // Sum calendar days first, then convert — never sum truncated months
    // (that dropped leftover days and disagreed with Skills Upgrading).
    const totalDays = workExperience.reduce(
      (sum, exp) => sum + (exp.duration_days || 0),
      0,
    );
    const totalMonths = workExperience.reduce(
      (sum, exp) => sum + (exp.duration_months || 0),
      0,
    );
    return { employerCount: workExperience.length, totalMonths, totalDays };
  }, [workExperience]);

  const isExpired = (cert) => {
    if (cert.cert_is_lifetime || !cert.cert_expiry_date) return false;
    return new Date(cert.cert_expiry_date) < new Date();
  };
  const isExpiringSoon = (cert) => {
    if (cert.cert_is_lifetime || !cert.cert_expiry_date) return false;
    const days = Math.ceil((new Date(cert.cert_expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
    return days <= 90 && days > 0;
  };
  const getEducationStatusKey = (status) => {
    if (!status) return 'pending';
    const s = status.toLowerCase();
    return s === 'verified' ? 'verified' : s === 'invalid' ? 'rejected' : 'pending';
  };
  const getEducationStatusLabel = (status) => {
    if (!status) return 'Pending HR';
    const s = status.toLowerCase();
    return s === 'verified' ? 'Verified' : s === 'invalid' ? 'Rejected' : 'Pending HR';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        <button onClick={() => setSubTab('education')} className={`tab-btn-admin tab-btn-admin-sm ${subTab === 'education' ? 'active' : ''}`} title="Education">
          <GraduationCap className="w-4 h-4" />
          Education
        </button>
        <button onClick={() => setSubTab('experience')} className={`tab-btn-admin tab-btn-admin-sm ${subTab === 'experience' ? 'active' : ''}`} title="Work Experience">
          <Briefcase className="w-4 h-4" />
          Experience
        </button>
        <button onClick={() => setSubTab('certs')} className={`tab-btn-admin tab-btn-admin-sm ${subTab === 'certs' ? 'active' : ''}`} title="Professional Certificates">
          <Award className="w-4 h-4" />
          Certificates
        </button>
      </div>

      {subTab === 'education' && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-red-600" />
              Education
            </h3>
          </div>
          <div className="space-y-3">
            {!educationRecords || educationRecords.length === 0 ? (
              <div className="text-center py-10 bg-white rounded-xl border">
                <GraduationCap className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                <h4 className="text-base font-semibold text-gray-900 mb-1">No education records</h4>
                <p className="text-sm text-gray-600">This employee has not added any education records yet</p>
              </div>
            ) : (
              educationRecords.map((edu) => (
                <div key={edu.id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-gray-900">{edu.education_qualification_name}</h4>
                    {edu.qualification_level_display && (
                      <InfoBadge color="blue" label={edu.qualification_level_display} />
                    )}
                    <StatusBadge
                      status={getEducationStatusKey(edu.education_verification_status)}
                      label={getEducationStatusLabel(edu.education_verification_status)}
                    />
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building2 className="w-4 h-4" />
                      <span>{edu.institution_display_name || edu.education_institution_name || 'N/A'}</span>
                    </div>
                    {edu.education_field_of_study && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <GraduationCap className="w-4 h-4" />
                        <span>{edu.education_field_of_study}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{formatEducationYearRange(edu.education_start_year, edu.education_end_year, edu.education_is_ongoing)}</span>
                    </div>
                    {edu.education_certificate_number && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Shield className="w-4 h-4" />
                        <span>Certificate: {edu.education_certificate_number}</span>
                      </div>
                    )}
                    {edu.education_country && edu.education_country !== 'Singapore' && (
                      <div className="flex items-center gap-2">
                        <InfoBadge color="gray" label={edu.education_country} />
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {subTab === 'experience' && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-red-600" />
              Work Experience
            </h3>
            <button
              type="button"
              onClick={() => setExperienceModal({ open: true, record: null })}
              className="btn btn-admin-primary btn-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Experience
            </button>
          </div>
          {workExperience?.length > 0 && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-white rounded-2xl shadow-sm border p-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="ml-3 min-w-0">
                    <p className="text-xl font-bold text-gray-900 truncate">
                      {formatExperienceFromDays(workExperienceSummary.totalDays)}
                    </p>
                    <p className="text-sm text-gray-600">Total Work Experience</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border p-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                    <Briefcase className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="ml-3 min-w-0">
                    <p className="text-xl font-bold text-gray-900">{workExperienceSummary.employerCount}</p>
                    <p className="text-sm text-gray-600">
                      {workExperienceSummary.employerCount === 1 ? 'Employer Listed' : 'Employers Listed'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-3">
            {!workExperience || workExperience.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border">
                <Briefcase className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">No work experience yet</h4>
                <p className="text-sm text-gray-500 max-w-sm mx-auto">
                  Add prior roles from a CV or LinkedIn so this file, CVs, and IR21 overseas-work evidence stay complete.
                </p>
                <button
                  type="button"
                  onClick={() => setExperienceModal({ open: true, record: null })}
                  className="btn btn-admin-primary mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  <Plus className="w-4 h-4" />
                  Add first work experience
                </button>
              </div>
            ) : (
              workExperience.map((exp) => {
                const bullets = splitWorkExperienceDescription(exp.work_exp_job_description);
                return (
                  <div key={exp.id} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-gray-900">
                            {exp.work_exp_designation || exp.work_exp_company_name}
                          </h4>
                          {exp.work_exp_employment_type && (
                            <InfoBadge
                              color={exp.work_exp_employment_type === 'internship' ? 'blue' : 'gray'}
                              label={getWorkExpEmploymentTypeLabel(exp.work_exp_employment_type)}
                            />
                          )}
                          {exp.work_exp_industry && (
                            <InfoBadge
                              color={exp.work_exp_industry === 'construction' ? 'orange' : 'gray'}
                              label={getWorkExpIndustryLabel(exp.work_exp_industry)}
                            />
                          )}
                          {exp.work_exp_is_current && (
                            <InfoBadge color="green" label="Current" />
                          )}
                          {exp.work_exp_verification_status && (
                            <StatusBadge
                              status={exp.work_exp_verification_status === 'unverified' ? 'unverified' : exp.work_exp_verification_status}
                              label={getWorkExpVerificationLabel(exp.work_exp_verification_status)}
                            />
                          )}
                        </div>
                        <div className="space-y-1.5 text-sm">
                          {exp.work_exp_company_name && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <Building2 className="w-4 h-4 shrink-0" />
                              <span>
                                {exp.work_exp_company_name}
                                {exp.work_exp_department ? ` · ${exp.work_exp_department}` : ''}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-4 h-4 shrink-0" />
                            <span>
                              {formatWorkExperiencePeriod(exp.work_exp_start_date, exp.work_exp_end_date, exp.work_exp_is_current)}
                              {exp.duration_display && (
                                <span className="text-gray-400 ml-2">· {exp.duration_display}</span>
                              )}
                            </span>
                          </div>
                          {(exp.work_exp_location || (exp.work_exp_country && exp.work_exp_country !== 'Singapore')) && (
                            <div className="flex items-center gap-2 text-gray-600">
                              <MapPin className="w-4 h-4 shrink-0" />
                              <span>
                                {[exp.work_exp_location, exp.work_exp_country].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                          {bullets.length > 0 && (
                            <ul className="mt-2 space-y-1 text-gray-600 list-disc pl-5">
                              {bullets.map((line) => (
                                <li key={line}>{line}</li>
                              ))}
                            </ul>
                          )}
                          {exp.work_exp_technologies_used && (
                            <p className="text-gray-500 pt-1">
                              Skills: {exp.work_exp_technologies_used}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => setExperienceModal({ open: true, record: exp })}
                          className="icon-button-admin"
                          title="Edit work experience"
                          aria-label="Edit work experience"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setExperienceToDelete(exp)}
                          className="icon-button-danger"
                          title="Delete work experience"
                          aria-label="Delete work experience"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <WorkExperienceFormModal
        isOpen={experienceModal.open}
        onClose={() => setExperienceModal({ open: false, record: null })}
        staffId={staffId}
        record={experienceModal.record}
        existingRecords={workExperience}
        theme="admin"
        showVerification
        isSubmitting={createWorkExperience.isPending || updateWorkExperience.isPending}
        onSubmit={async (payload) => {
          if (experienceModal.record?.id) {
            await updateWorkExperience.mutateAsync({ id: experienceModal.record.id, data: payload });
          } else {
            await createWorkExperience.mutateAsync(payload);
          }
          setExperienceModal({ open: false, record: null });
        }}
      />
      <ConfirmModal
        isOpen={Boolean(experienceToDelete)}
        title="Delete Work Experience"
        message={
          experienceToDelete
            ? `Delete ${experienceToDelete.work_exp_designation || 'this role'} at ${experienceToDelete.work_exp_company_name}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        theme="admin"
        isLoading={deleteWorkExperience.isPending}
        onConfirm={async () => {
          if (!experienceToDelete) return;
          await deleteWorkExperience.mutateAsync(experienceToDelete.id);
          setExperienceToDelete(null);
        }}
        onCancel={() => setExperienceToDelete(null)}
      />

      {subTab === 'certs' && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Award className="w-5 h-5 text-red-600" />
              Professional Certificates
            </h3>
          </div>
          <div className="space-y-3">
            {!certificates || certificates.length === 0 ? (
              <div className="text-center py-10 bg-white rounded-xl border">
                <Award className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                <h4 className="text-base font-semibold text-gray-900 mb-1">No certificates</h4>
                <p className="text-sm text-gray-600">This employee has not added any professional certificates yet</p>
              </div>
            ) : (
              certificates.map((cert) => (
                <div key={cert.id} className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-gray-900">{cert.cert_name}</h4>
                    {cert.bca_scheme && <BcaSchemeBadge scheme={cert.bca_scheme} />}
                    {cert.cert_verification_status?.toLowerCase() === 'verified' && (
                      <StatusBadge status="verified" />
                    )}
                    {isExpired(cert) && <StatusBadge status="expired" />}
                    {isExpiringSoon(cert) && <StatusBadge status="expiring_soon" />}
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building2 className="w-4 h-4" />
                      <span>{cert.cert_issuing_organization}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Issued: {formatDate(cert.cert_issue_date)}
                        {cert.cert_expiry_date && !cert.cert_is_lifetime &&
                          ` · Expires: ${formatDate(cert.cert_expiry_date)}`
                        }
                        {cert.cert_is_lifetime && ' · Lifetime'}
                      </span>
                    </div>
                    {cert.cert_certificate_number && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Shield className="w-4 h-4" />
                        <span>Certificate: {cert.cert_certificate_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement; 